// Thin wrapper around the cc-os-mcp Edge Function.
// JSON-RPC 2.0 over HTTPS — same protocol the Claude clients use.
//
// Each method matches an Edge Function verb in supabase/functions/cc-os-mcp/verbs/.
// All return values are decoded from the wrapped JSON-RPC envelope.
//
// Wave 5c scope: 21 verbs live (4 Wave 1 + 5 Wave 2 + 2 Wave 3 + 4 Wave 5a + 4 Wave 5b + 2 Wave 5c).

import { useEffect, useRef, useState } from "react";

import { MCP_FUNCTIONS_URL } from "./config";
import { supabase, supabaseSchema } from "./supabase";
import type {
  OpsCompletenessRulesCheckKind,
  ProductionBriefSpecPointsSource,
  ProductionFitSessionsDecision,
  ProductionFitSessionsStage,
} from "./db-enums.generated";
import type { RowBadge } from "./flow-badge";

const MCP_URL =
  import.meta.env.VITE_CC_OS_MCP_URL || `${MCP_FUNCTIONS_URL}/cc-os-mcp`;

interface JsonRpcResult {
  content: Array<{ type: "text"; text: string }>;
  isError?: boolean;
}

interface JsonRpcResponse {
  jsonrpc: "2.0";
  id: string;
  result?: JsonRpcResult;
  error?: { code: number; message: string };
}

async function callTool<TIn, TOut>(name: string, args: TIn): Promise<TOut> {
  // Forward the user's JWT so RLS + audit attribution work end-to-end.
  // `getSession()` reads from localStorage and only network-fetches when
  // refreshing an expired token; in dev that refresh sometimes races with
  // the page mount and surfaces as `TypeError: Failed to fetch`. Catch it
  // and proceed without the bearer header — the Edge Function will return
  // a clean 401 if the user really is unauthenticated, which our caller
  // surfaces as a friendly error.
  const fetchOnce = (token: string | null) => {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(MCP_URL, {
      method: "POST",
      headers,
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: crypto.randomUUID(),
        method: "tools/call",
        params: { name, arguments: args },
      }),
    });
  };

  let accessToken: string | null = null;
  try {
    const { data } = await supabase.auth.getSession();
    accessToken = data.session?.access_token ?? null;
  } catch (err) {
    console.warn(
      `[mcp.${name}] could not read session, sending request without bearer:`,
      err,
    );
  }

  let res: Response;
  try {
    res = await fetchOnce(accessToken);
  } catch (err) {
    // `fetch` rejects with `TypeError: Failed to fetch` on network drops or
    // CORS pre-flight failures. Re-throw with a friendlier message so the
    // page-level catch can render something useful.
    const reason = err instanceof Error ? err.message : String(err);
    throw new Error(`Network error reaching MCP (${name}): ${reason}`, { cause: err });
  }

  // Stale-JWT self-heal: supabase-js `autoRefreshToken` can miss its window
  // when the tab was backgrounded or the laptop slept across the 1h exp,
  // leaving an expired token in localStorage that `getSession()` happily
  // hands back. On a 401, force a refresh and retry once before surfacing
  // the error to the user.
  if (res.status === 401) {
    let refreshed: string | null = null;
    try {
      const { data } = await supabase.auth.refreshSession();
      refreshed = data.session?.access_token ?? null;
    } catch (err) {
      console.warn(`[mcp.${name}] refreshSession failed:`, err);
    }
    if (refreshed && refreshed !== accessToken) {
      try {
        res = await fetchOnce(refreshed);
      } catch (err) {
        const reason = err instanceof Error ? err.message : String(err);
        throw new Error(`Network error reaching MCP (${name}) after refresh: ${reason}`, {
          cause: err,
        });
      }
    }
  }

  if (!res.ok) {
    // HTTP/2 has no statusText, and the Supabase gateway carries the actual
    // reason (e.g. `{"message":"JWT expired"}`) in the body. Read it so the
    // user sees something actionable instead of a bare "MCP HTTP 401:".
    let detail: string;
    try {
      const ct = res.headers.get("content-type") ?? "";
      if (ct.includes("application/json")) {
        const j = (await res.json()) as { message?: unknown; error?: unknown };
        if (typeof j.message === "string") detail = j.message;
        else if (typeof j.error === "string") detail = j.error;
        else detail = JSON.stringify(j);
      } else {
        detail = (await res.text()).trim();
      }
    } catch {
      detail = res.statusText ?? "";
    }
    throw new Error(`MCP HTTP ${res.status}${detail ? `: ${detail}` : ""}`);
  }

  const json = (await res.json()) as JsonRpcResponse;
  if (json.error) throw new Error(`MCP RPC error: ${json.error.message}`);
  if (!json.result) throw new Error("MCP RPC missing result");

  const text = json.result.content?.[0]?.text ?? "{}";
  const parsed = JSON.parse(text) as Record<string, unknown>;

  if (json.result.isError) {
    const errMsg = typeof parsed.error === "string" ? parsed.error : "Unknown MCP error";
    // Surface the underlying detail (e.g. PostgREST error.message) when the
    // EF wrapped a downstream failure with `asError(name + ' failed', detail)`.
    // Without this the user just sees "list_users failed" with no clue.
    const detail = typeof parsed.details === "string" && parsed.details.length > 0
      ? `: ${parsed.details}`
      : "";
    throw new Error(`MCP tool error: ${errMsg}${detail}`);
  }

  return parsed as TOut;
}

// Argument + return types — kept loose where the verbs return arbitrary JSON
// rows from RPCs. Tighten as schemas crystallise.

// Not the full production.orders.status vocabulary — `samples` and
// `ready_to_ship` have been in the CHECK since 5744 and were never added here.
// `committed` IS listed, because #1103 (migration 6191) introduces it and a
// committed order is one the portal now has to be able to describe. The
// authoritative list for anything that renders a picker is
// PRODUCTION_ORDERS_STATUS in lib/db-enums.generated.ts.
export type OrderStatus =
  | "briefed" | "quoted" | "committed" | "confirmed" | "in_production"
  | "shipped" | "delivered" | "closed" | "cancelled";

export type Priority = "low" | "normal" | "high" | "urgent" | "critical";

export type ActionFilter =
  | "mine" | "all_open" | "overdue" | "josh_flagged" | "resolved" | "all";

// Audit columns shipped with every Wave 7+ list_* / get_* response.
// Wave 8 will backfill the few verbs still missing them — see notes in
// each list verb wrapper below if updated_by/agent_id come back null.
export interface AuditFields {
  updated_at?: string | null;
  updated_by?: string | null;
  agent_id?: string | null;
}

// ── Import health (#1229, Epic #1227 phase 1b) ──────────────────────────────
//
// The shape `public.mcp_ingest_run_health()` returns (migration 6213). The
// alert keys, their severities and their titles are DATA
// (`ops.ingest_run_alert_catalogue()`), carried on each entry rather than
// mirrored in a TS literal here — a new alert condition is a migration, and
// this card must render it without a portal change.
export interface IngestRunLogEntry {
  id: string;
  agent_run_id: string;
  mode: "write" | "tripwire";
  trigger: "cron" | "manual" | "unknown";
  started_at: string;
  finished_at: string | null;
  ok: boolean;
  outcome: "ok" | "alert" | "failed" | "skipped";
  alerts: string[];
  /** The Edge Function's IngestRunResult, verbatim. */
  summary: Record<string, unknown>;
}

export interface IngestRunHealthSource {
  ingest_source: string;
  label: string;
  cron_jobname: string | null;
  cron_active: boolean | null;
  stale_after_hours: number;
  last_run: IngestRunLogEntry | null;
  last_ok_at: string | null;
  open_alerts: Array<{
    alert_key: string;
    severity: "info" | "warning" | "critical";
    title: string;
  }>;
}

export interface IngestRunHealth {
  sources: IngestRunHealthSource[];
}

// ── Orders without milestones (#1314, #475, Epic #460) ──────────────────────
// One row per public.mcp_list_orders_missing_milestones() result — the exact
// candidate set ops.orders_missing_milestones reports (migration 6214,
// #1230), enriched with the order's customer (NULL = no customer, flagged by
// the panel rather than guessed) and its order type's label.
export interface OrderMissingMilestonesRow {
  order_id: string;
  display_id: string | null;
  po_number: string | null;
  status: string;
  customer_id: string | null;
  customer_name: string | null;
  order_type_id: string | null;
  order_type_label: string | null;
  order_type_source: string | null;
  template_count: number;
  has_template: boolean;
  created_at: string;
}

// One candidate row inside a backfill dry-run/execute `candidates` array.
// Loosely typed — this is `ops.orders_missing_milestones` projected straight
// to jsonb by the SQL RPC (6214_planning_orders_get_milestones.sql §5), and
// the panel only ever reads it for display, never to decide behaviour.
export interface BackfillOrderMilestonesCandidate {
  order_id: string;
  display_id: string | null;
  po_number: string | null;
  status: string | null;
  created_at: string | null;
  import_source: string | null;
  order_type_id: string | null;
  order_type_source: string | null;
  template_count: number;
  has_template: boolean;
}

// One per-order outcome inside a backfill execute's `results` array.
export interface BackfillOrderMilestonesResultRow {
  order_id: string;
  po_number?: string | null;
  outcome: "seeded" | "already_has_milestones" | "no_template" | "order_not_eligible";
  seeded: number;
  existing?: number | null;
  order_type_id?: string | null;
  order_type_source?: string | null;
  template_count?: number | null;
}

// public.mcp_backfill_order_milestones' return shape — IDENTICAL in both dry
// run and execute mode (only `dry_run` and the counters differ), so a dry run
// can be diffed field-for-field against the execute that follows it.
export interface BackfillOrderMilestonesResult {
  dry_run: boolean;
  candidate_count: number;
  seedable_count: number;
  candidates: BackfillOrderMilestonesCandidate[];
  no_template: unknown[];
  no_template_count: number;
  orders_seeded: number;
  milestones_inserted: number;
  orders_skipped: number;
  results: BackfillOrderMilestonesResultRow[];
  audit_event_id: string | null;
}

// ── MFA requirement (#1107, Epic #1054 phase 3) ─────────────────────────────
//
// One object backs the whole /admin/settings → Security switch: the current
// state, who last changed it, and every holder of the area's capabilities with
// their MFA-enrolment status. Deliberately one read rather than three, so the
// "who will lose access" warning and the state the switch shows come from the
// same snapshot.
export interface MfaRequirementHolder {
  user_id: string;
  email: string | null;
  display_name: string | null;
  /** The subset of the area's capabilities this person actually holds. */
  capabilities: string[];
  verified_factor_count: number;
  total_factor_count: number;
  /** An UNVERIFIED factor is NOT enrolment — only this field decides. */
  has_verified_factor: boolean;
}

export interface MfaRequirementStatus {
  area: string;
  key: string;
  required: boolean;
  has_setting_row: boolean;
  updated_at: string | null;
  updated_by: string | null;
  updated_by_label: string | null;
  capabilities: string[];
  holders: MfaRequirementHolder[];
  holder_count: number;
  holders_without_mfa: number;
}

export interface MfaRequirementWriteResult extends MfaRequirementStatus {
  /** Already in the requested state: nothing written, nobody notified. */
  was_noop: boolean;
  changed_from: boolean;
  changed_to: boolean;
  notified: number;
}

// ── Order-cutover readiness (#1310, Epic #1227 phase 3a) ────────────────────
//
// ONE read backs the whole /admin/settings → Order cutover panel: `ready` plus
// the four gate items, each with the live detail behind it. Deliberately one
// read rather than four, so the green/red beside an item and the `ready` verdict
// can never come from different snapshots.
//
// `passing` is NULL for exactly one item — `sheet_only_rows`, which is
// informational and never blocking: the cutover's own first step is the
// one-shot import that clears that backlog, so requiring it at zero before the
// cutover may run would be circular. Render a null `passing` as neither green
// nor red; see migration 6234's header.
export type OrderCutoverItemKey =
  | "sign_ins"
  | "mfa"
  | "planning_import"
  | "sheet_only_rows";

/** An active staff account that has never signed in. */
export interface OrderCutoverSignInEntry {
  user_id: string;
  email: string | null;
  display_name: string | null;
}

/**
 * One live (un-revoked) exclusion from the sign-in gate. D27's intended use is
 * the cc-os accounts deliberately created without an invite — the reason is
 * admin-authored free text and should name roles, never people.
 */
export interface OrderCutoverSignInExclusion extends OrderCutoverSignInEntry {
  exclusion_id: string;
  reason: string;
  created_at: string;
  created_by: string | null;
  created_by_label: string | null;
}

export interface OrderCutoverSignInDetail {
  never_signed_in: OrderCutoverSignInEntry[];
  never_signed_in_count: number;
  excluded: OrderCutoverSignInExclusion[];
  excluded_count: number;
}

export interface OrderCutoverMfaDetail {
  required: boolean;
  holders_without_mfa: number;
  /** Exactly the holders with no VERIFIED factor — reused from #1107's read. */
  holders_missing: MfaRequirementHolder[];
}

export interface OrderCutoverPlanningImportDetail {
  last_run: IngestRunLogEntry | null;
}

export interface OrderCutoverSheetOnlyRowsDetail {
  count: number;
  last_run_id: string | null;
  /** Plain-English statement of where `count` comes from, from the server. */
  source: string;
}

export interface OrderCutoverItem {
  key: OrderCutoverItemKey;
  label: string;
  /** false for the one informational item; only blocking items decide `ready`. */
  blocking: boolean;
  /** NULL means "this item has no pass/fail reading", not "unknown". */
  passing: boolean | null;
  detail: Record<string, unknown>;
}

export interface OrderCutoverReadiness {
  /** True only when every BLOCKING item passes. */
  ready: boolean;
  items: OrderCutoverItem[];
}

export interface OrderCutoverExclusionResult {
  id: string;
  user_id: string;
  reason: string;
  created_at: string;
  created_by: string | null;
  /** Already excluded: the original row (actor, reason, time) is untouched. */
  was_noop: boolean;
}

export interface OrderCutoverUnexclusionResult {
  id: string;
  user_id: string;
  revoked_at: string;
  revoked_by: string | null;
  revoked_reason: string | null;
}

// ── Order-cutover ACTION (#1325 / migration 6238, Epic #1227 phase 3a pt 2) ─
//
// The gate above says WHETHER the cutover may run; this says WHAT HAPPENED to
// the one cutover that was asked for. It is a three-step state machine rather
// than a single call because a SQL function cannot synchronously run an
// import: the two importers are Edge Functions reached over pg_net, which
// dispatches only after the calling transaction commits, and the Planning
// importer reads `ingest.planning.mode` fresh on every run — so a single
// transaction that posted both imports and then wrote `export_only` would make
// the very final import it just asked for read nothing.
//
//   planning_import — the final one-shot Planning import has been asked for
//   tminus_import   — it landed cleanly; the final T-Minus import is away
//   completed       — the daily schedule is paused, the sheet is an export,
//                     the crossover is dated, audited and announced
export type OrderCutoverRunState =
  | "planning_import"
  | "tminus_import"
  | "completed";

/** One net.http_post attempt. Append-only, so the array proves the order. */
export interface OrderCutoverDispatch {
  target: "planning" | "tminus";
  at: string;
  /** pg_net request id, or null when the POST could not even be queued. */
  request_id: number | null;
  /** Non-null means nothing was sent — the run waits, it does not fail. */
  error: string | null;
}

export interface OrderCutoverRun {
  id: string;
  state: OrderCutoverRunState;
  requested_by: string | null;
  requested_by_label: string | null;
  requested_at: string;
  dispatches: OrderCutoverDispatch[];
  planning_dispatched_at: string | null;
  planning_ingest_run_id: string | null;
  tminus_dispatched_at: string | null;
  tminus_ingest_run_id: string | null;
  /** {jobname, paused, was_active, error} — active=false, never unscheduled. */
  cron_pause: Record<string, unknown> | null;
  cutover_at: string | null;
  completed_at: string | null;
  audit_event_id: string | null;
  notified: number | null;
  /** Why this step is still waiting, including the import that failed. */
  last_observation: Record<string, unknown> | null;
  last_checked_at: string | null;
}

export interface OrderCutoverState {
  /** null until a cutover has been requested. There is at most one, ever. */
  run: OrderCutoverRun | null;
  cutover_at: string | null;
  /** write | tripwire | export_only — export_only means the cutover completed. */
  planning_mode: string;
  planning_cron_job: string;
  /** Three-valued: null is "no answer", not "inactive". */
  planning_cron_active: boolean | null;
}

export interface OrderCutoverRunResult {
  /** true when a cutover already existed: nothing was dispatched or written. */
  was_noop: boolean;
  state: OrderCutoverRunState;
  cutover_run_id: string;
  cutover_at: string | null;
  requested_by: string | null;
  dispatched: OrderCutoverDispatch | null;
  message: string;
}

export interface OrderCutoverAdvanceResult {
  advanced: boolean;
  state: OrderCutoverRunState | null;
  reason: string;
  ingest_run?: string | null;
  cutover_at?: string | null;
  cron_pause?: Record<string, unknown> | null;
  audit_event_id?: string | null;
  notified?: number | null;
  redispatch: Record<string, unknown> | null;
}

// ── T-Minus retirement (#1311, Epic #1227 phase 4 / migration 6239) ─────────
//
// "T-Minus finishes the orders that were already on it, then stops."
//
//   active     no cutover has happened. The import behaves exactly as it always
//              has — this whole phase is inert until one does.
//   read_only  a cutover has happened. The import still updates the orders that
//              were already on the sheet and declines every other row, counting
//              each with its reason on the run summary.
//   retired    an admin ran mcp.retireTminus. The daily schedule is paused and
//              the import writes nothing at all.
export type TminusStatus = "active" | "read_only" | "retired";

/**
 * One pre-cutover order still open on T-Minus — the countdown to retirement.
 *
 * Deliberately carries NO money column: the RPC behind it is SECURITY DEFINER
 * and would bypass the column-level finance masking on `production.orders`.
 */
export interface TminusPreCutoverOrder {
  order_id: string;
  display_id: string | null;
  po_number: string;
  customer_id: string | null;
  customer_name: string | null;
  /** The order's current stage — `production.orders.status`. */
  status: string;
  last_tminus_ingest_at: string | null;
  target_ex_factory: string | null;
  created_at: string;
}

export interface TminusRetirementState {
  status: TminusStatus;
  cutover_at: string | null;
  retired_at: string | null;
  retired_by: string | null;
  retired_by_label: string | null;
  /** The import's own read/write mode (6070) — a DIFFERENT fact from retirement. */
  mode: string;
  cron_job: string;
  /** Three-valued: null is "no answer", not "inactive". */
  cron_active: boolean | null;
  pre_cutover_total: number;
  open_pre_cutover: number;
  /** The same answer `mcp_retire_tminus` refuses on, so the button cannot disagree. */
  may_retire: boolean;
  /** Only the OPEN ones, soonest ex-factory first, capped at 200. */
  orders: TminusPreCutoverOrder[];
  orders_truncated: boolean;
}

export interface TminusRetireResult {
  /** true when T-Minus was already retired: nothing was changed. */
  was_noop: boolean;
  status: TminusStatus;
  retired_at: string | null;
  /** {jobname, paused, was_active, error} — active=false, never unscheduled. */
  cron_pause: Record<string, unknown> | null;
  audit_event_id: string | null;
  notified: number | null;
  open_pre_cutover: number;
  message: string;
}

// ── Flow badges (#984, Epic #956 phase 2b) ──────────────────────────────────
//
// `RowBadge` / `rowBadge()` live in `./flow-badge` — a Supabase-free module, so
// a component unit test can import the parser without mocking this file (see
// that file's header). Re-exported here because every caller that reads a badge
// is already importing list rows from `@/lib/mcp`.
export type { RowBadge } from "./flow-badge";
export { rowBadge } from "./flow-badge";

// ── /pipeline board (#1008, Epic #956 phase 3a) ─────────────────────────────
//
// The raw shape public.get_pipeline_board() returns (one row per card, flat
// badge columns) — see mcp.getPipelineBoard() for the reshape into
// PipelineCard, which every portal consumer should use instead of this.
interface PipelineBoardRow {
  entity_type: "brief" | "order";
  entity_id: string;
  stage: string;
  display_id: string | null;
  title: string | null;
  customer_id: string | null;
  customer_name: string | null;
  season_id: string | null;
  season_code: string | null;
  status: string | null;
  status_since: string | null;
  days_in_status: number | null;
  age_level: string | null;
  missing_required: number;
  missing_recommended: number;
  missing_total: number;
  owner_user_ids: string[] | null;
}

/** One /pipeline board card — a brief or order mapped to a stage column. */
export interface PipelineCard {
  entity_type: "brief" | "order";
  entity_id: string;
  /** ops.stage_owners.stage key (e.g. "s2_brief_received"). */
  stage: string;
  display_id: string | null;
  /** The brief's title, or the order's po_number. */
  title: string | null;
  customer_id: string | null;
  customer_name: string | null;
  season_id: string | null;
  season_code: string | null;
  /** Resolved stage owner(s) — ops.resolve_stage_owner(stage, customer_id). */
  owner_user_ids: string[];
  /** Read with rowBadge() / render with <RowFlowBadges badge={card.badge} />. */
  badge: RowBadge;
}

export interface OrderRow extends AuditFields {
  id: string;
  // Human-readable critical-path order id (CP-YYYY-NNNN). Added by
  // migration 5942 and surfaced by 5946.
  display_id?: string | null;
  // FK to the meeting where this order was placed (5942). Reverse of
  // crm.meetings.order_id which is "meeting ABOUT an order".
  meeting_id?: string | null;
  po_number?: string | null;
  customer_id?: string | null;
  customer_name?: string | null;
  supplier_id?: string | null;
  supplier_name?: string | null;
  season?: string | null;
  season_code?: string | null;
  status?: OrderStatus | null;
  // production.orders column names. Both mcp_get_order (the raw row via
  // to_jsonb) and mcp_list_orders return these.
  target_ex_factory?: string | null;
  actual_ex_factory?: string | null;
  total_qty_pcs?: number | null;
  is_blind_buy?: boolean | null;
  // Aliases that ONLY mcp_list_orders adds (6027). get_order never returns
  // them — reading them on the order-detail page rendered "—" for every order.
  target_ex_factory_date?: string | null;
  actual_ex_factory_date?: string | null;
  blind_buy?: boolean | null;
  total_value_zar?: number | null;
  hks_po_ref?: string | null;
  notes?: string | null;
  // [Epic #1060] Phase 1a (#1061, migration 6160) — D28 receiving party and
  // free-text ticket requirements, out of metadata.
  deliver_to?: string | null;
  ticket_requirements?: string | null;
  // [Epic #1060] Phase 2c (#1103, migration 6191) — design §6.2's S5 → S6
  // "customer PO no. + version" and §6.3's Pepkor confirmation report.
  customer_po_version?: string | null;
  customer_confirmation_report_date?: string | null;
  created_at?: string | null;
  // Provenance (#465): set when this order was converted from a brief/quote
  // via mcp_create_order.
  source_brief_id?: string | null;
  source_quote_id?: string | null;
  /** Flow badge (#984) — missing-item count + days in status. Read with rowBadge(). */
  badge?: RowBadge | null;
  /** Which packing sub-model this order's "packs" lines use (migration 6154, design D23). Null = not chosen. */
  packing_type?: "solid" | "ratio" | null;
  [key: string]: unknown;
}

export interface ListOrdersInput {
  season?: string;
  status?: OrderStatus;
  customer_id?: string;
  supplier_id?: string;
  include_soft_deleted?: boolean;
  limit?: number;
  cursor?: string;
  // Free-text search — matches po_number, display_id, hks_po_ref, customer
  // name, supplier name, and season code server-side (#254).
  query?: string;
}

export interface ListOrdersOutput {
  orders: OrderRow[];
  next_cursor: string | null;
  count: number;
}

/**
 * One row from mcp_get_order_lines — deliberately typed (not
 * Record<string, unknown>) so callers stop treating order lines as an
 * untyped bag; the packs-vs-singles / applied-curve posture and the size
 * split are NOT on this row — fetch them per-line via mcp.getOrderLineSizes
 * (issue #674).
 */
export interface OrderLine {
  id: string;
  line_no: number | null;
  style_code: string;
  style_name: string | null;
  colour: string | null;
  size_breakdown: Record<string, number> | null;
  qty_pcs: number | null;
  unit_price_usd: number | null;
  unit_price_zar: number | null;
  /** samples.briefs.id this line was derived from, if any (since 6121). Provenance only. */
  brief_id?: string | null;
  /** samples.briefs.display_id for brief_id, joined server-side (since 6121). Survives a soft-deleted brief. */
  brief_display_id?: string | null;
  // [Epic #1060] Phase 1a (#1061, migration 6160) — D5: an order line is
  // style x colour x customer PO x drop. Out of order_lines.metadata.
  customer_po_no?: string | null;
  drop_label?: string | null;
  drop_date?: string | null;
  fit_variant?: "regular" | "upsize" | null;
  dc_code?: string | null;
  delivery_date?: string | null;
}

// The get_order fan-out rows below mirror the RETURNS TABLE of each RPC
// (latest definition: migration 6115). Typed for the same reason OrderLine
// is: the order-detail tabs read these by name, and as untyped bags they
// read fields the RPCs never returned (vessel, landed_cost_zar, status on a
// sample, aql_* on a QA row) and rendered "—" for every order.
// tests/pgtap/94_order_detail_rpc_return_shapes.sql pins the SQL side.

/** One row from mcp_get_order_samples. */
export interface OrderSample {
  id: string;
  dev_id: string | null;
  style_code: string;
  colour: string | null;
  kind: string;
  current_status: string;
  notes: string | null;
  /** Latest production.sample_iterations.received_at — samples has no received column. */
  last_received_date: string | null;

  // ── #1068 (Epic #1066 Phase 1b, migration 6170) ──────────────────────────
  // A sample belongs to an ORDER LINE and feeds an ORDER milestone. Every
  // field below is nullable: a sample requested before 6170 has no line, and
  // a sample with no submission yet has no latest_*.
  /** The line this sample was cut for, and that line's line_no. */
  order_line_id: string | null;
  order_line_no: number | null;
  brief_id: string | null;
  library_item_id: string | null;
  /** The order milestone this sample's approval feeds, and its catalog label. */
  milestone_id: string | null;
  milestone_name: string | null;
  /** The supplier PPO the sample was requested on (6165). */
  purchase_order_id: string | null;
  /** The single size, when it matters (a graded fit, one size of a size set). */
  size: string | null;
  requested_at: string | null;
  due_date: string | null;
  /** The LATEST production.sample_iterations row — the current submission. */
  latest_iteration_no: number | null;
  latest_submitted_at: string | null;
  latest_received_at: string | null;
  latest_decision: string | null;
  /** True when the submission was reviewed off photographs, not a garment. */
  latest_is_virtual: boolean | null;
  latest_result_comment: string | null;
  /** point -> {spec, actual, tolerance}. `{}` when no chart was supplied. */
  latest_measurements: Record<string, SampleMeasurementPoint> | null;
}

/** One point on a submission's measurement chart (#1068). */
export interface SampleMeasurementPoint {
  spec?: number | string | null;
  actual?: number | string | null;
  tolerance?: number | string | null;
}

/** production.samples.kind — mirrors samples_kind_check (migration 6170). */
export const SAMPLE_KINDS = [
  "development",
  "fit",
  "size_set",
  "pp",
  "pps",
  "bps",
  "lab_dip",
  "strike_off",
  "trims",
  "top_of_production",
] as const;

export type SampleKind = typeof SAMPLE_KINDS[number];

/** Human labels for the kinds, as the production team says them. */
export const SAMPLE_KIND_LABELS: Record<SampleKind, string> = {
  development: "Development",
  fit: "Fit",
  size_set: "Size set",
  pp: "PP",
  pps: "PPS",
  bps: "BPS",
  lab_dip: "Lab dip",
  strike_off: "Strike-off",
  trims: "Trims",
  top_of_production: "Top of production",
};

/**
 * public.mcp_decide_sample's roll-up verdict (#1068):
 *   none                  — not an approval, or the sample feeds no milestone
 *   awaiting_siblings     — another line's sample of the same milestone is out
 *   actor_may_not_approve — all in, but this user may not approve the milestone
 *   approved              — the order milestone was approved with the decision date
 */
export type SampleMilestoneRollup =
  | "none"
  | "awaiting_siblings"
  | "actor_may_not_approve"
  | "approved";

/** One row from mcp_get_order_qa. */
export interface OrderQaInspection {
  id: string;
  inspector: string | null;
  inspection_date: string;
  aql_level: string | null;
  /** Legacy raw inspector result (pass|fail|hold|retry); the portal renders `status`. */
  result: string | null;
  defect_count: number;
  report_url: string | null;
  inspector_company: string;
  /** Approval lifecycle (4440): pending | pass | fail | rework. */
  status: string;
  aql_critical: number;
  aql_major: number;
  aql_minor: number;
  next_action: string | null;
  report_pdf_url: string | null;
}

/** One row from mcp_get_order_shipments. */
export interface OrderShipment {
  id: string;
  vessel_name: string | null;
  bl_number: string | null;
  container_number: string | null;
  etd: string | null;
  eta: string | null;
  actual_departure: string | null;
  actual_arrival: string | null;
  status: string;
  landed_total_zar: number | null;
}

export interface GetOrderOutput {
  order: OrderRow | null;
  lines: OrderLine[];
  milestones: Array<Record<string, unknown>>;
  samples: OrderSample[];
  qa_inspections: OrderQaInspection[];
  shipments: OrderShipment[];
  linked_emails_count: number;
  linked_files_count: number;
  recent_events: Array<Record<string, unknown>>;
}

// ── Order-line sizes (migration 6057, issue #674) ───────────────────────────
// Replaces order_lines.size_breakdown's raw jsonb with the structured split:
// per-slot quantities against the line's size_sets scale, the applied curve,
// and the explicit packs-vs-singles posture (supply_unit/pack_count/
// pieces_per_pack). size_qty_reconciled = false is a VALID, non-blocking
// state (6057 D3) — render it as a warning, never as a save-blocking error.

export interface OrderLineSizeRow {
  id: string;
  slot_position: number;
  label: string | null;
  label_source: string;
  matrix_code: string | null;
  qty: number;
}

/** Shaped like public.check_factor() — see get_order_line_sizes.ts. */
export interface SizeReconciliation {
  level: string; // none | unknown | ok | warn — never blocks.
  reason: string;
  order_line_id: string;
  order_id?: string;
  status?: string;
  qty_pcs?: number | null;
  size_qty_total?: number | null;
  size_qty_variance?: number | null;
  size_qty_reconciled?: boolean | null;
  unresolved_slots?: number | null;
  unresolved_qty?: number | null;
  override_labels?: number | null;
  tolerance_pcs?: number | null;
  /** Solid size-to-carton diagnostic (migration 6154) — carton_count_exact = false is a warn. */
  pieces_per_carton?: number | null;
  carton_count?: number | null;
  carton_count_exact?: boolean | null;
}

export interface OrderLineSizePosture {
  size_set_id: string | null;
  size_set_code: string | null;
  season_code: string | null;
  size_curve_id: string | null;
  size_curve_code: string | null;
  supply_unit: string | null;
  prepack_id: string | null;
  prepack_code: string | null;
  pack_count: number | null;
  pieces_per_pack: number | null;
  /** The ORDER's packing_type (migration 6154, design D23) — solid | ratio | null. */
  packing_type?: "solid" | "ratio" | null;
  /** Solid size-to-carton fields (migration 6154) — the counterpart of prepack_id/pack_count/pieces_per_pack. */
  pieces_per_carton?: number | null;
  carton_count?: number | null;
  carton_count_exact?: boolean | null;
}

export interface GetOrderLineSizesOutput {
  order_line_id: string;
  sizes: OrderLineSizeRow[];
  posture: OrderLineSizePosture;
  reconciliation: SizeReconciliation;
}

export interface SetOrderLineSizeInput {
  slot_position: number;
  qty: number;
  label?: string;
  label_source?: "resolved" | "override" | "unresolved";
  size_set_id?: string;
  matrix_code?: string;
}

export interface CheckOrderSizesOutput {
  level: string; // ok | warn
  order_id: string;
  line_count: number;
  mismatch_lines: number;
  unlabeled_lines: number;
  /** Solid lines whose total does not divide into whole cartons (migration 6154). */
  carton_mismatch_lines?: number;
  lines: SizeReconciliation[];
}

export interface SizeSetSummary {
  id: string;
  season_code: string;
  code: string;
  name: string | null;
  // production.size_sets.source is NOT NULL (default 'manual') — never null.
  source: string;
  is_active: boolean;
  member_count: number;
  curve_count: number;
  labels: string[];
}

export interface SizeSetMember {
  // Optional: /admin/sizes edits draft members that have not been saved yet
  // and therefore carry no id. Read paths always populate it
  // (production.size_set_members.id is NOT NULL, default gen_random_uuid()).
  id?: string;
  slot_position: number;
  label: string;
}

export interface SizeSetCurve {
  id: string;
  code: string;
  name: string | null;
  is_active: boolean;
  points: Array<Record<string, unknown>>;
}

export interface SizeSetPrepack {
  id: string;
  code: string;
  name: string | null;
  is_active: boolean;
  totals: Record<string, unknown>;
  slots: Array<Record<string, unknown>>;
}

export interface GetSizeSetOutput {
  size_set: Record<string, unknown> | null;
  members: SizeSetMember[];
  curves: SizeSetCurve[];
  prepacks: SizeSetPrepack[];
}

// ── Order create / edit (#465) ──────────────────────────────────────────────

export interface OrderLineInput {
  style_code: string;
  style_name?: string;
  colour?: string;
  size_breakdown?: Record<string, number>;
  qty_pcs: number;
  unit_price_usd?: number;
  unit_price_zar?: number;
  notes?: string;
  /** samples.briefs.id — line-level provenance only (since 6121). */
  brief_id?: string;
  // [Epic #1060] Phase 1a (#1061, migration 6160).
  customer_po_no?: string;
  drop_label?: string;
  drop_date?: string;
  /** Defaults to 'regular' server-side when omitted. */
  fit_variant?: "regular" | "upsize";
  dc_code?: string;
  delivery_date?: string;
}

export interface CreateOrderInput {
  season_id: string;
  lines: OrderLineInput[];
  order_type_id?: string;
  customer_id?: string;
  supplier_id?: string;
  po_number?: string;
  currency?: string;
  target_ex_factory?: string;
  target_in_store?: string;
  is_blind_buy?: boolean;
  notes?: string;
  source_brief_id?: string;
  source_quote_id?: string;
  note?: string;
  brand_id?: string;
  buyer_id?: string;
  label_id?: string;
  channel?: "customer" | "cc_corporate_range" | "cc_stock";
}

export interface CreateOrderOutput {
  order: OrderRow;
  lines_count: number;
  milestones_inserted: number;
}

// ── Order composer (#1101, Epic #1060 Phase 2a, migration 6181) ────────────
// ONE customer PO is ONE order with MANY lines, each sourced from its own
// costed brief (ruling D5). The prices are NOT the portal's to compute — they
// come from each brief's accepted supplier quote and its costing, which live
// behind the finance-masking boundary — so the portal asks the server what a
// line would contain (previewOrderLinesFromBriefs) and sends back only what a
// brief cannot carry: the quantity and the PO/drop/fit/DC fields.

/** One brief-sourced line, as the composer submits it. */
export interface ComposerBriefLineInput {
  brief_id: string;
  qty_pcs: number;
  colour?: string;
  unit_price_usd?: number;
  unit_price_zar?: number;
  size_breakdown?: Record<string, number>;
  customer_po_no?: string;
  drop_label?: string;
  drop_date?: string;
  fit_variant?: "regular" | "upsize";
  dc_code?: string;
  delivery_date?: string;
}

export interface ComposeOrderInput {
  season_id: string;
  /** The brief-sourced lines. Every brief named here is converted to 'won'. */
  brief_lines?: ComposerBriefLineInput[];
  /** Lines that are not sourced from a brief. They convert nothing. */
  lines?: OrderLineInput[];
  order_type_id?: string;
  /** Omit the key to take the brief set's agreed value; null means "none". */
  customer_id?: string | null;
  supplier_id?: string | null;
  po_number?: string;
  currency?: string;
  target_ex_factory?: string | null;
  target_in_store?: string;
  is_blind_buy?: boolean;
  notes?: string;
  note?: string;
  brand_id?: string | null;
  buyer_id?: string | null;
  label_id?: string | null;
  channel?: "customer" | "cc_corporate_range" | "cc_stock";
}

export interface ComposeOrderOutput extends CreateOrderOutput {
  briefs_converted: string[];
  quotes_converted: string[];
}

/** What one brief would contribute to a composed order. */
export interface ComposerLinePreview {
  brief_id: string;
  display_id: string | null;
  title: string;
  status: string;
  /** False for a brief that is not 'briefed'/'costed' — it cannot convert. */
  convertible: boolean;
  style_code: string | null;
  style_name: string | null;
  /** Null when `colour_required` — the pre-fill is withheld on purpose. */
  colour: string | null;
  /** The Briefed-sheet quantity, when the brief came from one (#916). */
  qty_pcs: number | null;
  unit_price_usd: number | null;
  unit_price_zar: number | null;
  notes: string | null;
  quote_id: string | null;
  costing_id: string | null;
  customer_id: string | null;
  /** The brief requested a colour change — state the colour before composing. */
  colour_required: boolean;
  colour_change_details: string[];
  snapshot_colour: string | null;
  provenance: string[];
  /** Non-fatal: e.g. no accepted quote, no costing — the price gate's inputs. */
  warnings: string[];
}

export interface ComposerPreview {
  lines: ComposerLinePreview[];
  /** Order-header values the whole brief set agrees on; null where it does not. */
  defaults: {
    customer_id: string | null;
    /** `customer_id`'s resolved name — the pre-filled picker's label, so an
     *  inherited customer reads as a name and not a UUID prefix (#916). */
    customer_name: string | null;
    supplier_id: string | null;
    /** `supplier_id`'s resolved name — same role as `customer_name`. */
    supplier_name: string | null;
    /** The sheet's raw SUPPLIER text, present only when no live supplier
     *  resolved: pre-types the picker's search box and is shown as
     *  "no matching live supplier found" (#916's dialog behaviour, kept). */
    supplier_search_hint: string | null;
    brand_id: string | null;
    buyer_id: string | null;
    label_id: string | null;
    target_ex_factory: string | null;
    notes: string | null;
    /** The sheet's raw season text, matched against the season list portal-side. */
    season_hint: string | null;
    drops: string[];
    conflicts: string[];
  };
  /** Things that would refuse the write. Show these; do not submit over them. */
  blockers: string[];
}

// ── Planning sheet export (#1104, Epic #1060 Phase 2d, migration 6200) ──────
// The AW27 `Planning` layout generated FROM orders, lines, sizes and costings
// — the inverse of cc-os-planning-ingest, so a new cc-os order is never
// re-typed into the workbook (design §10, crossover Phase B).

export interface PlanningSheetExportInput {
  season_id?: string | null;
  customer_id?: string | null;
  order_ids?: string[] | null;
  /** Cancelled orders are left out unless this says otherwise. */
  include_cancelled?: boolean | null;
  /** Maximum sheet ROWS (order lines). `truncated` reports whether it was hit. */
  limit?: number | null;
}

/**
 * One sheet column: what it is, and where cc-os reads it from.
 *
 * Named `…ExportColumn`, not `PlanningSheetColumn`: that name is already taken
 * by migration 6031/#466's own self-describing planning sheet further down this
 * file (`key`/`label`/`kind`/`formula`). The two share no field, so a duplicate
 * name would NOT be a compile error — TypeScript's declaration merging would
 * silently fuse them into one interface demanding fields from both unrelated
 * features. Keep the names distinct.
 */
export interface PlanningSheetExportColumn {
  ordinal: number;
  /** The workbook's own column letter: A … CO. */
  column_letter: string;
  /** The header string VERBATIM — `TOTAL QTY:` keeps its colon. */
  header: string;
  /** The cc-os read behind it; null exactly when the column is blank. */
  source: string | null;
  is_blank: boolean;
  /** Why cc-os does not hold it. Set exactly when `is_blank` (#1104 AC3). */
  why_blank: string | null;
}

export interface PlanningSheetExport {
  layout: string;
  sheet_name: string;
  /** Always true. The export is a projection of cc-os, never an input to it. */
  read_only: boolean;
  generated_at: string;
  filters: Record<string, unknown>;
  columns: PlanningSheetExportColumn[];
  /** The 93 header strings in sheet order; index-aligned with every row. */
  header_row: string[];
  /** The columns cc-os does not hold, each with its reason. */
  blank_columns: Array<
    Pick<PlanningSheetExportColumn, "ordinal" | "column_letter" | "header" | "why_blank">
  >;
  /**
   * One POSITIONAL array per order line, `header_row.length` wide. Positional
   * rather than keyed because the sheet is a grid and a jsonb object would not
   * preserve column order — see lib/planningSheetExport.ts.
   */
  rows: unknown[][];
  row_count: number;
  order_count: number;
  column_count: number;
  /** True when `limit` cut the sheet short. */
  truncated: boolean;
  /** e.g. pieces parked past size slot 16, which a 16-slot layout cannot hold. */
  warnings: Array<Record<string, unknown>>;
}

// ── Customer PO versions (#1102, Epic #1060 Phase 2b, migration 6193) ──────
// A version is a SNAPSHOT of the order as it stood, never a push-forward: the
// amendment flow is edit the order, then record a version. So the portal never
// sends header or line content here — createCustomerPoVersion takes the order
// id and a reason, and the server reads the live rows. The snapshot carries no
// financial column (no unit prices, no order totals, no GP), which is why
// these types have none.

/** One version of a customer PO, as the history list reports it. */
export interface CustomerPoVersionSummary {
  id: string;
  /** 1 for the first (bootstrap) snapshot, then 2, 3, … per amendment. */
  version_no: number;
  /** True for the one version that matches the order's live lines. */
  is_current: boolean;
  change_notes: string | null;
  po_number: string | null;
  /** The CUSTOMER's own version label (6191, #1103) — "Rev 2", not version_no. */
  customer_po_version: string | null;
  status: string | null;
  total_qty_pcs: number | null;
  target_ex_factory: string | null;
  target_in_store: string | null;
  /** Lines in the snapshot, and their summed qty — not the lines themselves. */
  line_count: number;
  lines_qty_pcs: number;
  created_at: string | null;
  created_by: string | null;
}

export interface CustomerPoVersionList {
  order_id: string;
  /** Newest version first. Empty for an order with no snapshot yet. */
  versions: CustomerPoVersionSummary[];
}

export interface CustomerPoVersionCreated {
  id: string;
  order_id: string;
  version_no: number;
  is_current: boolean;
  /** version_no > 1 — what raised the S5/S6 alert and flagged covering PPOs. */
  is_amendment: boolean;
  /** production.orders.customer_po_version as it stands after the call. */
  customer_po_version: string | null;
}

/**
 * What `createCustomerPoVersionFromExtraction` returns (#1204, Epic #1060
 * Phase 3a, migration 6204): the same version row plus which extraction
 * recorded it. `was_new` is false when that extraction had already been
 * recorded — one confirmed PO document is one amendment, however many times
 * the action is pressed.
 */
export interface CustomerPoVersionFromExtraction extends CustomerPoVersionCreated {
  extraction_id: string;
  was_new: boolean;
}

/**
 * What `applyPepkorConfirmationDatesToOrder` returns (#1276, migration 6231):
 * which order the confirmed Pepkor confirmation-report extraction targeted,
 * the two dates as applied, and how many live lines were patched. `applied`
 * is false when the document stated NEITHER date — a success that wrote
 * nothing, not a refusal. There is no `was_new`: re-applying the same
 * confirmed values is a plain column SET, so pressing it twice is harmless
 * and indistinguishable from pressing it once.
 */
export interface PepkorConfirmationDatesApplied {
  extraction_id: string;
  order_id: string;
  lines_updated: number;
  book_by_date: string | null;
  latest_date_in_dc: string | null;
  applied: boolean;
}

/**
 * What `applyQimaReportExtractionToInspection` returns (#1226, Epic #1069
 * Phase 3b, migration 6217): the booked inspection's id, its (unchanged)
 * `status`, the report's own stated `result`, the AQL counts and
 * `defect_count` now on the row. `was_new` is false when that extraction had
 * already been applied — one confirmed report is one fill, however many
 * times the action is pressed.
 */
export interface QimaReportExtractionApplied {
  id: string;
  status: string;
  result: string | null;
  aql_critical: number;
  aql_major: number;
  aql_minor: number;
  defect_count: number;
  extraction_id: string;
  was_new: boolean;
}

/** One field that differs between two snapshots. */
export interface CustomerPoFieldChange {
  field: string;
  old: unknown;
  new: unknown;
}

export interface CustomerPoVersionDiff {
  order_id: string;
  /** The version supplying the `old` side, as asked for — not chronological. */
  version_a: number;
  version_b: number;
  header_changes: CustomerPoFieldChange[];
  line_changes: {
    /** Lines in version_b only. */
    added: Array<Record<string, unknown>>;
    /** Lines in version_a only. */
    removed: Array<Record<string, unknown>>;
    /** Lines in both, with every differing field listed. */
    changed: Array<{ line_no: number; fields: CustomerPoFieldChange[] | null }>;
  };
}

export interface EditOrderPatch {
  customer_id?: string | null;
  supplier_id?: string | null;
  season_id?: string;
  po_number?: string;
  currency?: string;
  status?: string;
  target_ex_factory?: string | null;
  actual_ex_factory?: string | null;
  target_in_store?: string | null;
  actual_in_store?: string | null;
  is_blind_buy?: boolean;
  target_gp_pct?: number | null;
  actual_gp_pct?: number | null;
  hks_po_ref?: string | null;
  hks_invoice_ref?: string | null;
  notes?: string | null;
  brand_id?: string | null;
  buyer_id?: string | null;
  label_id?: string | null;
  channel?: "customer" | "cc_corporate_range" | "cc_stock";
  /** D28 receiving party. Defaults from the customer's receiver on create (since 6160/#1061). */
  deliver_to?: string | null;
  ticket_requirements?: string | null;
  /** Which packing sub-model this order's "packs" lines use (migration 6154, design D23). */
  packing_type?: "solid" | "ratio" | null;
  /** Design §6.2's S5 → S6 "customer PO no. + version" — the version half,
   *  free text as the customer states it (6191/#1103). */
  customer_po_version?: string | null;
  /** The date CC's order-confirmation report went to the customer — design
   *  §6.3's Pepkor / CODE template addition (6191/#1103). YYYY-MM-DD. */
  customer_confirmation_report_date?: string | null;
}

/** Patch shape for mcp.editOrderLine — the phase-1a per-line fields only (6160/#1061). */
export interface EditOrderLinePatch {
  customer_po_no?: string | null;
  drop_label?: string | null;
  drop_date?: string | null;
  fit_variant?: "regular" | "upsize";
  dc_code?: string | null;
  delivery_date?: string | null;
}

// ── Planning sheet + margin sign-off gate (migration 6031, issue #466) ──────
// The generated sheet is self-describing: every column carries the formula the
// generator applied, so the printed output can be checked against the sheet
// management signs on paper without reading SQL.

export interface PlanningSheetColumn {
  key: string;
  label: string;
  kind: "text" | "date" | "int" | "usd" | "zar" | "ratio" | "pct" | string;
  formula: string;
}

export interface PlanningSheet {
  sheet_id?: string;
  id?: string;
  order_id: string;
  po_number?: string | null;
  is_blind_buy?: boolean;
  version: number;
  columns: PlanningSheetColumn[];
  rows: Array<Record<string, unknown>>;
  totals: Record<string, unknown>;
  margin?: OrderMargin | null;
  margin_pct?: number | string | null;
  content_hash: string;
  generated_at: string;
  generated_by: string | null;
}

/** Result of production.compute_order_margin — the REAL margin, not a blob. */
export interface OrderMargin {
  order_id: string;
  margin_pct: number | string | null;
  revenue_zar: number | string | null;
  cost_zar: number | string | null;
  margin_zar: number | string | null;
  /** order_lines | order_total_value_zar | none */
  source: string;
  lines_total: number;
  lines_costed: number;
  lines_priced: number;
  missing_cost_styles: string[];
  missing_price_styles: string[];
  computed_at: string;
}

export interface OrderSignoff {
  id: string;
  order_id: string;
  sheet_id: string | null;
  sheet_version: number | null;
  signed_by: string | null;
  signed_at: string;
  margin_pct_at_signing: number | string | null;
  threshold_pct_at_signing: number | string | null;
  is_override: boolean;
  override_reason: string | null;
  is_blind_buy: boolean;
  note: string | null;
}

/** production.check_order_confirmation_gate — shaped like check_factor(). */
export interface ConfirmationGate {
  level: "ok" | "block";
  reason: string;
  order_id: string;
  po_number: string | null;
  order_status: string;
  is_blind_buy: boolean;
  margin_pct: number | string | null;
  threshold_pct: number | string;
  margin: OrderMargin | null;
  require_signoff: boolean;
  block_on_unknown_margin: boolean;
  sheet_id: string | null;
  sheet_version: number | null;
  latest_signoff: OrderSignoff | null;
  override: OrderSignoff | null;
  checked_at: string;
}

/**
 * One production.style_costings row as mcp_list_style_costings returns it
 * (#1056, migration 6163). Numerics come back from PostgREST as strings or
 * numbers depending on the column, so every money/ratio field is widened —
 * use `num()` from @/components/briefs/BriefCostingPanel to read one.
 *
 * `margin_pct` and `royalty_zar` are GENERATED columns: the database computes
 * them with production.order_planning_rows' own formula, so a costing and the
 * order it becomes report the same margin. Never compute a margin client-side.
 */
export interface StyleCostingRow {
  id: string;
  style_code: string | null;
  brief_id: string | null;
  quote_id: string | null;
  supplier_id: string | null;
  fob_usd: number | string | null;
  /** The LANDED factor (rand landed cost per supplier USD), not the selling multiple. */
  factor: number | string | null;
  landed_zar: number | string | null;
  fx_rate: number | string | null;
  royalty_pct: number | string | null;
  royalty_zar: number | string | null;
  marketing_zar: number | string | null;
  rsp_zar: number | string | null;
  /** The SELLING multiple (customer rand price per supplier USD, about x40). */
  selling_multiple: number | string | null;
  selling_zar: number | string | null;
  /** Generated: this costing's own gross margin %. */
  margin_pct: number | string | null;
  target_margin_pct: number | string | null;
  /** Margin of the ORDERS this style actually landed on (6031) — a different number. */
  actual_margin_pct: number | string | null;
  /** The brief customer's OWN minimum GP %; null when they have no override. */
  customer_min_gp_pct: number | string | null;
  is_current: boolean | null;
  notes: string | null;
  superseded_at: string | null;
  superseded_by: string | null;
  supersede_note: string | null;
  created_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
  agent_id: string | null;
}

/** Computed actual margin for one style costing, across its linked orders. */
export interface StyleCostingActualMargin {
  margin_pct: number | string | null;
  revenue_zar: number | string | null;
  cost_zar: number | string | null;
  qty_pcs: number | string | null;
  /** order_lines | none */
  source: string;
}

export interface GetPlanningSheetOutput {
  sheet: PlanningSheet | null;
  versions: Array<{
    id: string;
    version: number;
    generated_at: string;
    generated_by: string | null;
    margin_pct: number | string | null;
    content_hash: string;
  }>;
  signoffs: OrderSignoff[];
  gate: ConfirmationGate | null;
}

// ── Email ────────────────────────────────────────────────────────────────
// The entity kinds email.links can point at. Mirrors the check constraint on
// email.links.entity_type (0400_email.sql, widened by migration 6032 with
// style_costing + brief) and the Zod enums on the link/unlink/list verbs —
// all three must move together.
export type EmailEntityType =
  | "customer" | "supplier" | "order" | "action" | "sample"
  | "sample_iteration" | "qa_inspection" | "shipment"
  | "meeting" | "development" | "quote" | "stock_offer"
  | "style_costing" | "brief";

/** One email.links row as returned by list_email_links / get_email_thread. */
export interface EmailLink {
  id?: string;
  message_id?: string;
  entity_type: EmailEntityType;
  entity_id: string;
  /** Resolved server-side by mcp_email_entity_label(); null if the target is gone. */
  entity_label?: string | null;
  link_type?: string | null;
  confidence?: number | null;
  summary?: string | null;
  created_at?: string | null;
  [key: string]: unknown;
}

/** A row from search_emails / list_emails_for_entity (metadata + snippet only). */
export interface EmailListRow {
  id: string;
  thread_id?: string | null;
  message_id?: string | null;
  folder?: string | null;
  from_addr?: string | null;
  from_name?: string | null;
  to_addrs?: string[] | null;
  subject?: string | null;
  snippet?: string | null;
  sent_at?: string | null;
  received_at?: string | null;
  has_attachments?: boolean | null;
  is_outbound?: boolean | null;
  link_count?: number | null;
  /** Present on list_emails_for_entity rows only. */
  link_type?: string | null;
  [key: string]: unknown;
}

/** A message inside get_email_thread, carrying its resolved links. */
export interface EmailThreadMessage extends EmailListRow {
  links?: EmailLink[];
}

export interface ResolveActionInput {
  action_id?: string;
  code?: string;
  resolution_note: string;
  evidence_url?: string;
}

export interface AddActionInput {
  title: string;
  description?: string;
  owner_label?: string;
  owner_user_id?: string;
  priority?: Priority;
  due_date?: string;
  follow_up_date?: string;
  customer_id?: string;
  supplier_id?: string;
  order_id?: string;
  is_josh_flagged?: boolean;
  source?: string;
  source_ref?: string;
  evidence_url?: string;
  idempotency_key?: string;
}

export type ActionStatus =
  | "open" | "in_progress" | "blocked" | "resolved" | "cancelled" | "superseded";

export type ActionUpdateType =
  | "comment" | "status_change" | "priority_change" | "reassignment"
  | "evidence_added" | "due_date_change" | "escalation" | "resolution"
  // SP3 shadow-mode resolver — added by migration 5950. The DB enum
  // also lists these three; once db-enums.generated.ts is regenerated
  // the union will pick them up automatically.
  | "proposed_resolution" | "resolution_confirmed" | "resolution_rejected";

export interface Action {
  id: string;
  code: string;
  action_no: number;
  title: string;
  description?: string | null;
  priority: Priority;
  status: ActionStatus;
  due_date?: string | null;
  follow_up_date?: string | null;
  owner_user_id?: string | null;
  owner_label?: string | null;
  category?: string | null;
  customer_id?: string | null;
  supplier_id?: string | null;
  order_id?: string | null;
  source?: string | null;
  source_ref?: string | null;
  evidence_url?: string | null;
  is_josh_flagged: boolean;
  resolved_at?: string | null;
  resolution_note?: string | null;
  key_entities?: Array<Record<string, unknown>> | null;
  context_payload?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  updated_at?: string | null;
  [key: string]: unknown;
}

export interface ActionUpdate {
  id: string;
  action_id: string;
  update_type: ActionUpdateType;
  // `body` is jsonb in the DB. Legacy update types (`comment`, etc.) write a
  // plain string; SP3 reassignment + proposed_resolution branches write a
  // structured object. Consumers narrow defensively at the call site.
  body?: string | Record<string, unknown> | null;
  from_value?: string | null;
  to_value?: string | null;
  evidence_url?: string | null;
  created_at: string;
  created_by?: string | null;
  agent_id?: string | null;
  model_version?: string | null;
  agent_run_id?: string | null;
  [key: string]: unknown;
}

export interface ReassignActionArgs {
  code: string;
  to_owner_user_id: string;
  note?: string;
}

export interface ReassignActionResult {
  action: Action;
  update: ActionUpdate;
}

export interface ConfirmProposedResolutionArgs {
  update_id: string;
  resolution_note?: string;
}
export interface ConfirmProposedResolutionResult {
  confirmed_at: string;
  action_id: string;
  proposal_id: string;
}
export interface RejectProposedResolutionArgs {
  update_id: string;
  reason: string;
}
export interface RejectProposedResolutionResult {
  rejected_at: string;
  reason: string;
  action_id: string;
}

// ── Wave 5b types ──────────────────────────────────────────────────────────

export type CustomerStatus = "active" | "dormant" | "archived";
export type SupplierStatus = "active" | "paused" | "archived";

export interface Customer extends AuditFields {
  id: string;
  slug: string;
  name: string;
  short_name?: string | null;
  tier: 1 | 2 | 3 | null;
  status: CustomerStatus;
  legal_entity?: string | null;
  vat_number?: string | null;
  payment_terms?: string | null;
  notes?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  deleted_at?: string | null;
  superseded_by?: string | null;
  /** Ruling D33 (migration 6077). FK to staff.users(id) — the CC staff member who owns this relationship. */
  account_manager_id?: string | null;
  [key: string]: unknown;
}

// ── Epic #740 (customer / brand / buyer model) — brand + label types ───────
// Mirror cc-os-mcp/lib/brand-model.ts's BRAND_COLUMNS / LABEL_COLUMNS
// projections (#759). A brand always belongs to exactly one customer_id
// (ruling D1/D2) and nests via parent_brand_id (ruling D25).

export interface BrandRow {
  id: string;
  customer_id: string;
  parent_brand_id: string | null;
  name: string;
  slug: string;
  kind: string;
  active: boolean;
  notes: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

/** A BrandRow nested by parent_brand_id — the shape list_brand/get_customer return as `tree`. */
export interface BrandTreeNode extends BrandRow {
  children: BrandTreeNode[];
}

// ── External parties (issue #1074, epic #1072 phase 1b, migration 6186) ───
// Receivers, inspectors, importers of record, clearing agents, forwarders —
// beside the free-text fields they replace (ops.customer_flow_settings.
// receiver_name, production.qa_inspections.inspector_company).

export type PartyKind = "inspector" | "receiver" | "importer_of_record" | "clearing_agent" | "forwarder";

export interface PartyRow {
  id: string;
  name: string;
  kinds: PartyKind[];
  country: string | null;
  website: string | null;
  notes: string | null;
  metadata: Record<string, unknown>;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PartyContactRow {
  id: string;
  party_id: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: string | null;
  is_primary: boolean;
  notes: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

/** crm.resolve_party_or_text()'s shape — the party when linked, else the free text, else neither. */
export interface ResolvedParty {
  source: "party" | "text" | "none";
  party_id: string | null;
  name: string | null;
  kinds?: PartyKind[];
  country?: string | null;
  archived?: boolean;
}

export interface UnlinkedPartyValue {
  source: "receiver" | "inspector";
  value: string;
  usage_count: number;
}

/** production.labels (migration 6076) — reference data, not scoped to a customer (ruling D24). */
export interface LabelRow {
  id: string;
  name: string;
  slug: string;
  owner_kind: "cc" | "hks" | "third_party";
  owner_customer_id: string | null;
  owner_name: string | null;
  notes: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface Supplier extends AuditFields {
  id: string;
  slug: string;
  name: string;
  short_name?: string | null;
  country?: string | null;
  region?: string | null;
  capabilities?: string[] | null;
  capacity_pcs_month?: number | null;
  lead_time_days?: number | null;
  status: SupplierStatus;
  factory_holidays?: Array<Record<string, unknown>> | null;
  notes?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  deleted_at?: string | null;
  [key: string]: unknown;
}

export interface OrderSearchHit {
  id: string;
  po_number: string | null;
  customer_name: string | null;
  supplier_name: string | null;
  season_code: string | null;
  status: OrderStatus | null;
  target_ex_factory: string | null;
}

// ── Phase 6 polish types ──────────────────────────────────────────────────

export type FileMimeKind = "image" | "pdf" | "excel" | "word" | "powerpoint" | "other";

export interface FileRow {
  id: string;
  storage_provider: string;
  storage_key: string;
  web_url: string | null;
  original_path: string | null;
  original_filename: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  season: string | null;
  style_code: string | null;
  last_modified_at: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
  agent_id?: string | null;
}

// ── Production Dashboard types (Wave 6) ──────────────────────────────────
// Returned by mcp.getProductionDashboard(). All sections come from one RPC.

export type DashboardBucket = "on_track" | "at_risk" | "critical";

export interface DashboardKpis {
  total_active: number;
  on_track: number;
  at_risk: number;
  critical: number;
  shipped_this_week: number;
  pending_qa: number;
  as_of: string;
}

export interface DashboardCriticalOrder {
  id: string;
  po_number: string | null;
  customer_id: string | null;
  customer_name: string | null;
  supplier_id: string | null;
  supplier_name: string | null;
  status: OrderStatus | null;
  target_ex_factory: string | null;
  days_overdue: number;
  overdue_ms_count: number;
  open_ms_count: number;
  total_value_zar: number | null;
  total_value_usd: number | null;
  total_qty_pcs: number | null;
  next_milestone: { name: string; target_date: string | null; days_overdue: number; sequence: number | null } | null;
  last_completed: { name: string; actual_date: string | null; sequence: number | null } | null;
  updated_at: string | null;
  bucket: DashboardBucket;
}

export interface DashboardMilestoneStage {
  stage: string;
  count: number;
}

export interface DashboardCustomerRow {
  customer_id: string | null;
  customer_name: string | null;
  order_count: number;
  critical_count: number;
  on_track_count: number;
  at_risk_count: number;
  on_time_pct: number | null;
  avg_days_late: number;
}

export interface DashboardSupplierRow {
  supplier_id: string | null;
  supplier_name: string | null;
  order_count: number;
  critical_count: number;
  on_track_count: number;
  at_risk_count: number;
  on_time_pct: number | null;
  avg_days_late: number;
}

export interface DashboardTimelineDay {
  date: string;
  order_count: number;
  orders: Array<{
    id: string;
    po_number: string | null;
    customer_name: string | null;
    supplier_name: string | null;
    bucket: DashboardBucket;
  }>;
}

export interface DashboardOverdueMilestone {
  milestone_id: string;
  order_id: string;
  po_number: string | null;
  name: string;
  owner: string;
  supplier_name: string | null;
  customer_name: string | null;
  target_date: string;
  days_overdue: number;
  status: "pending" | "at_risk" | "late";
}

export interface ProductionDashboard {
  kpis: DashboardKpis;
  critical_orders: DashboardCriticalOrder[];
  milestone_radar: DashboardMilestoneStage[];
  customer_view: DashboardCustomerRow[];
  supplier_view: DashboardSupplierRow[];
  timeline: DashboardTimelineDay[];
  overdue_milestones: DashboardOverdueMilestone[];
  generated_at?: string | null;
}

// Returned by mcp.getSeasonKpis() — one row per season. total_value_zar is
// NULL for non-finance roles (the RPC gates the column via staff.has_role).
export interface SeasonKpiRow {
  season_id: string;
  season_code: string;
  order_count: number;
  total_value_zar: number | null;
  shipped_pct: number;
  status_breakdown: Record<string, number>;
}

// Lightweight season row for dropdowns. Hydrated by `mcp.listSeasons`.
// We don't have a server-side `list_seasons` MCP verb yet — until we do,
// the wrapper goes through `supabaseSchema("production").from("seasons")`
// directly. Centralising it here means every dropdown still reads the
// same row shape from a single source of truth.
export interface SeasonOption {
  id: string;
  code: string;
  name: string;
  status: string | null;
  archived_at: string | null;
}

// ── User management types (admin-only) ─────────────────────────────────────
// One row per auth.users row, joined with staff.users + role list. Returned
// by mcp.listUsers(); mcp.getUser() returns the same shape plus phone +
// role_label. `roles` are staff.roles names — data, not a fixed enum, since
// #612; call mcp.listRoles() (admin) or useRoles() (any signed-in user) for
// the current set rather than assuming a list here.
export interface UserRow {
  id: string;
  email: string;
  full_name: string | null;
  is_active: boolean;
  roles: string[];
  created_at: string;
  last_sign_in_at: string | null;
  email_confirmed_at: string | null;
  // Count of ISO weeks (Monday-start, UTC) with 3+ distinct active days,
  // sourced from staff.weekly_active_users (migration 6014, issue #461) —
  // the Finish Line DoD #1 adoption metric. 0 for a user with no
  // ops.audit_events activity.
  active_weeks_3plus_days: number;
}

export interface UserDetail extends UserRow {
  phone?: string | null;
  role_label?: string | null;
}

// ── Notification settings (6038 / issue #577) ───────────────────────────────
// One row per samples.notification_settings.notification_type. resolved_emails
// is the union of get_emails_for_roles(recipient_roles) and additional_emails
// (deduped, server-side) — the actual delivery list, not just the role names.
// zero_member_roles is the subset of recipient_roles with no current
// staff.user_roles holder. Neither field filters undeliverable-looking
// addresses (@cc-os-test.local / .invalid / .test) — that flagging is a UI
// concern the calling page applies to resolved_emails.
export interface NotificationSettingRow {
  id: string;
  notification_type: string;
  enabled: boolean;
  frequency: string;
  recipient_roles: string[];
  additional_emails: string[];
  resolved_emails: string[];
  zero_member_roles: string[];
  updated_at: string;
}

// One row per (role, capability) pair from staff.role_capabilities cross
// staff.capabilities x staff.app_role (6042 / issue #606). `granted` is true
// when a staff.role_capabilities row exists OR role="admin" (mirrors
// staff.has_capability()'s hardcoded admin exemption, migration 6041).
// `locked` is true only for role="admin" — the portal should render those
// checkboxes disabled; mcp_set_role_capability refuses any admin-row
// mutation regardless.
// ── Role registry types (#612) ─────────────────────────────────────────────
// staff.roles replaced the staff.app_role enum: which roles exist is data an
// admin edits from /admin/settings, so nothing in the portal may carry its own
// copy of the list. user_count is what the retire guard checks — a role with
// holders cannot be retired until they are reassigned.
export interface RoleRow {
  id: string;
  name: string;
  label: string;
  description: string | null;
  color: string;
  surfaces: string[];
  is_system: boolean;
  sort_order: number;
  retired_at: string | null;
  user_count: number;
  capability_count: number;
}

export interface CapabilityGridRow {
  role: string;
  capability_id: string;
  capability_name: string;
  description: string | null;
  sensitivity: string;
  granted: boolean;
  locked: boolean;
}

export const mcp = {
  /**
   * Rename a buyer within one meeting. Buyer names are free text repeated on
   * every samples.meeting_buyer_samples row, so this rewrites all of them.
   * Returns ok:false with error:'name_taken' when the name already belongs to
   * another buyer in that meeting — merging is a separate, deliberate action.
   */
  renameMeetingBuyer: (args: { meeting_id: string; old_name: string; new_name: string }) =>
    callTool<typeof args, {
      ok: boolean; renamed?: number; name?: string; unchanged?: boolean; error?: string;
    }>("rename_meeting_buyer", args),

  /**
   * Merge one buyer into another within a meeting. The target survives and its
   * decision wins on any sample both buyers marked; `discarded` counts the
   * source rows dropped that way. Destructive and not undoable.
   */
  mergeMeetingBuyers: (args: { meeting_id: string; source_name: string; target_name: string }) =>
    callTool<typeof args, {
      ok: boolean; moved?: number; discarded?: number; target?: string;
      /** #1268: which shape the roster fold took. */
      roster?: "none" | "target_kept" | "source_renamed";
      /** #1268: the brand the merged buyer keeps. */
      brand_id?: string | null;
      error?: string;
    }>("merge_meeting_buyers", args),

  // ── The meeting roster: who attended, under which brand (#1268) ──────────
  //
  // WHY THESE THREE ARE DIRECT supabase.rpc CALLS AND NOT cc-os-mcp VERBS.
  // Each verb DOES exist (remove_meeting_buyer.ts, link_buyer_to_brand.ts,
  // add_brand_buyer.ts), but cc-os-dev's Edge Functions only move on merge
  // (bot-deploy/cc-os/CLAUDE.md, "Dev's edge functions lag your branch")
  // while a PR's migrations DO apply to dev — so routing Meeting Mode
  // through them would leave the whole brand picker erroring on every
  // preview build of its own PR, and the gating e2e run's verb preflight
  // would fail the PR outright. Same reasoning, and the same shape, as
  // getIngestRunHealth below (#1229). The capability gate is in each SQL
  // function's own BODY (`samples.coordinate` via staff.has_capability), so
  // it stays server-side either way, and pgTAP 150 §G proves it
  // independently of the verb layer.
  //
  // Living here rather than in the page is the #596 front-door rule: a page
  // never calls supabase.rpc() itself, and eslint's no-restricted-syntax
  // enforces it for src/pages/**.

  /**
   * Remove one buyer from a meeting entirely: every
   * samples.meeting_buyer_samples decision row for (meeting_id, buyer_name)
   * AND their samples.meeting_buyers roster row, in one transaction. Matched
   * case/whitespace-insensitively. Two writes is exactly why this is one
   * call — doing them in sequence from the page leaves the orphaned roster
   * row that puts the buyer back in the panel on the next reload.
   * Idempotent. SECURITY INVOKER: both tables' own RLS decides who may.
   */
  removeMeetingBuyer: async (args: { meeting_id: string; buyer_name: string }): Promise<{
    ok: boolean;
    name?: string;
    decisions_removed?: number;
    roster_removed?: number;
    error?: string;
  }> => {
    const { data, error } = await supabase.rpc("mcp_remove_meeting_buyer", {
      p_meeting_id: args.meeting_id,
      p_buyer_name: args.buyer_name,
    });
    if (error) throw new Error(error.message);
    return (data ?? { ok: false, error: "no_result" }) as {
      ok: boolean;
      name?: string;
      decisions_removed?: number;
      roster_removed?: number;
      error?: string;
    };
  },

  /**
   * ADD-ONLY: link an existing crm.customer_buyers row to one crm.brands row
   * of that buyer's own company. Idempotent (`was_new` false on a link that
   * already exists), never a delete, never an is_primary change — which is
   * how it differs from setBuyerBrands, which REPLACES the buyer's whole
   * brand set and stays admin-only. Needs `samples.coordinate`.
   */
  linkBuyerToBrand: async (args: { buyer_id: string; brand_id: string }): Promise<{
    ok: boolean;
    buyer_id?: string;
    brand_id?: string;
    customer_id?: string | null;
    was_new?: boolean;
  }> => {
    const { data, error } = await supabase.rpc("mcp_link_buyer_to_brand", {
      p_buyer_id: args.buyer_id,
      p_brand_id: args.brand_id,
    });
    if (error) throw new Error(error.message);
    return (data ?? { ok: false }) as {
      ok: boolean;
      buyer_id?: string;
      brand_id?: string;
      customer_id?: string | null;
      was_new?: boolean;
    };
  },

  /**
   * ADD-ONLY: put a named person on a brand. The name is matched
   * case/whitespace-insensitively against the BRAND'S OWN company's live
   * buyers first and that match reused (`matched_existing`), so typing an
   * existing buyer's name in different casing never creates a duplicate;
   * only a genuinely new name inserts a crm.customer_buyers row. Then one
   * additive crm.buyer_brands link. Needs `samples.coordinate`.
   */
  addBrandBuyer: async (args: { brand_id: string; name: string; role?: string | null }): Promise<{
    ok: boolean;
    buyer?: { id?: string; name?: string } | null;
    brand_id?: string;
    customer_id?: string | null;
    was_new_buyer?: boolean;
    link_was_new?: boolean;
    matched_existing?: boolean;
  }> => {
    const { data, error } = await supabase.rpc("mcp_add_brand_buyer", {
      p_brand_id: args.brand_id,
      p_name: args.name,
      p_role: args.role ?? null,
    });
    if (error) throw new Error(error.message);
    return (data ?? { ok: false }) as {
      ok: boolean;
      buyer?: { id?: string; name?: string } | null;
      brand_id?: string;
      customer_id?: string | null;
      was_new_buyer?: boolean;
      link_was_new?: boolean;
      matched_existing?: boolean;
    };
  },

  listOrders: (args: ListOrdersInput = {}) =>
    callTool<ListOrdersInput, ListOrdersOutput>("list_orders", args),

  getOrder: (args: { id: string }) =>
    callTool<{ id: string }, GetOrderOutput>("get_order", args),

  /**
   * The completeness checklist for an order (#1103) — every item still
   * missing for its next transition(s) out of its current status, plus the
   * most recent sign-off override (if any) that landed it there. Drives
   * <OrderMissingItemsPanel>. Same evaluator as the order's row badge and its
   * /todo alerts, so the three cannot disagree.
   */
  getOrderMissingItems: (
    args: { id: string },
  ): Promise<OrderMissingItemsResult> =>
    callTool<typeof args, OrderMissingItemsResult>("get_order_missing_items", args),

  getCustomer: (args: { id: string; include_archived_brands?: boolean }) =>
    callTool<typeof args, {
      customer: Record<string, unknown> | null;
      contacts: Array<Record<string, unknown>>;
      recent_orders: Array<Record<string, unknown>>;
      /** Epic #740 Phase 3a (#759): the customer's brands, flat + nested by parent_brand_id (ruling D25). */
      brands: BrandRow[];
      brand_tree: BrandTreeNode[];
    }>("get_customer", args),

  // ── Brand / label pickers (Epic #740 Phase 3, #759/#760) ────────────────
  // Read verbs over crm.brands / production.labels — see
  // cc-os-mcp/verbs/list_brand.ts / list_label.ts.

  listBrand: (
    args: {
      customer_id?: string;
      query?: string;
      include_archived?: boolean;
      active_only?: boolean;
      limit?: number;
    } = {},
  ): Promise<{ brands: BrandRow[]; tree: BrandTreeNode[] }> =>
    callTool<typeof args, { brands: BrandRow[]; tree: BrandTreeNode[] }>("list_brand", args),

  // Brand admin CRUD (#794) — wraps cc-os-mcp/verbs/add_brand.ts /
  // edit_brand.ts / archive_brand.ts / unarchive_brand.ts. Admin-only
  // (admin.manage) server-side. Nesting cycles are rejected by the database
  // trigger crm.tg_brands_validate_parent(), not re-validated here — its
  // error surfaces through the rejected promise.
  addBrand: (
    args: {
      customer_id: string;
      name: string;
      slug?: string;
      parent_brand_id?: string | null;
      kind?: "brand" | "chain";
      active?: boolean;
      notes?: string;
    },
  ): Promise<{ brand: BrandRow; was_new: boolean }> =>
    callTool<typeof args, { brand: BrandRow; was_new: boolean }>("add_brand", args),

  editBrand: (
    args: {
      brand_id: string;
      patch: {
        name?: string;
        slug?: string;
        parent_brand_id?: string | null;
        kind?: "brand" | "chain";
        active?: boolean;
        notes?: string | null;
      };
    },
  ): Promise<{ brand: BrandRow }> =>
    callTool<typeof args, { brand: BrandRow }>("edit_brand", args),

  archiveBrand: (
    args: { brand_id: string },
  ): Promise<{ brand: BrandRow; was_already_archived: boolean; child_brand_ids: string[] }> =>
    callTool<typeof args, { brand: BrandRow; was_already_archived: boolean; child_brand_ids: string[] }>(
      "archive_brand", args),

  unarchiveBrand: (
    args: { brand_id: string },
  ): Promise<{ brand: BrandRow; was_already_active: boolean }> =>
    callTool<typeof args, { brand: BrandRow; was_already_active: boolean }>("unarchive_brand", args),

  // ── External parties (issue #1074, epic #1072 phase 1b, migration 6186) ──
  // Receivers, inspectors, importers of record, clearing agents, forwarders.
  // Reads (listParties/getParty) carry no capability gate — staff reads.
  // Writes (upsertParty/archiveParty/upsertPartyContact/
  // listUnlinkedPartyValues) are admin.manage-gated server-side.
  listParties: (
    args: {
      kind?: PartyKind;
      include_archived?: boolean;
      query?: string;
      limit?: number;
    } = {},
  ): Promise<{ parties: PartyRow[] }> =>
    callTool<typeof args, { parties: PartyRow[] }>("list_parties", args),

  getParty: (
    args: { id: string },
  ): Promise<{ party: PartyRow | null; contacts: PartyContactRow[] }> =>
    callTool<typeof args, { party: PartyRow | null; contacts: PartyContactRow[] }>("get_party", args),

  upsertParty: (
    args: {
      id?: string;
      name: string;
      kinds: PartyKind[];
      country?: string | null;
      website?: string | null;
      notes?: string | null;
      metadata?: Record<string, unknown>;
    },
  ): Promise<{ party: PartyRow; was_new: boolean }> =>
    callTool<typeof args, { party: PartyRow; was_new: boolean }>("upsert_party", args),

  archiveParty: (
    args: { party_id: string },
  ): Promise<{ party: PartyRow; was_already_archived: boolean }> =>
    callTool<typeof args, { party: PartyRow; was_already_archived: boolean }>("archive_party", args),

  upsertPartyContact: (
    args: {
      id?: string;
      party_id: string;
      name: string;
      email?: string | null;
      phone?: string | null;
      role?: string | null;
      is_primary?: boolean;
      notes?: string | null;
      archived?: boolean;
    },
  ): Promise<{ contact: PartyContactRow; was_new: boolean }> =>
    callTool<typeof args, { contact: PartyContactRow; was_new: boolean }>("upsert_party_contact", args),

  listUnlinkedPartyValues: (): Promise<{ values: UnlinkedPartyValue[] }> =>
    callTool<Record<string, never>, { values: UnlinkedPartyValue[] }>("list_unlinked_party_values", {}),

  // production.shipments.forwarder_party_id — orders.manage-gated (merch
  // posture, not admin-only; matches edit_order's gate). NOT a general
  // shipment editor — full shipment CRUD is epic #1072 phase 1a (#1073).
  setShipmentForwarder: (
    args: { shipment_id: string; party_id: string | null },
  ): Promise<{ shipment: Record<string, unknown> }> =>
    callTool<typeof args, { shipment: Record<string, unknown> }>("set_shipment_forwarder", args),

  listLabel: (
    args: {
      query?: string;
      owner_kind?: "cc" | "hks" | "third_party";
      include_archived?: boolean;
      limit?: number;
    } = {},
  ): Promise<{ labels: LabelRow[] }> =>
    callTool<typeof args, { labels: LabelRow[] }>("list_label", args),

  // Label admin CRUD (#795) — wraps cc-os-mcp/verbs/add_label.ts /
  // edit_label.ts / archive_label.ts / unarchive_label.ts. Admin-only
  // (admin.manage) server-side. Labels have no re-parenting (unlike brands) —
  // structurally simpler than the brand CRUD above.
  addLabel: (
    args: {
      name: string;
      slug?: string;
      owner_kind: "cc" | "hks" | "third_party";
      owner_customer_id?: string | null;
      owner_name?: string | null;
      notes?: string | null;
    },
  ): Promise<{ label: LabelRow; was_new: boolean }> =>
    callTool<typeof args, { label: LabelRow; was_new: boolean }>("add_label", args),

  editLabel: (
    args: {
      label_id: string;
      patch: {
        name?: string;
        slug?: string;
        owner_kind?: "cc" | "hks" | "third_party";
        owner_customer_id?: string | null;
        owner_name?: string | null;
        notes?: string | null;
      };
    },
  ): Promise<{ label: LabelRow }> =>
    callTool<typeof args, { label: LabelRow }>("edit_label", args),

  archiveLabel: (
    args: { label_id: string },
  ): Promise<{ label: LabelRow; was_already_archived: boolean }> =>
    callTool<typeof args, { label: LabelRow; was_already_archived: boolean }>(
      "archive_label", args),

  unarchiveLabel: (
    args: { label_id: string },
  ): Promise<{ label: LabelRow; was_already_active: boolean }> =>
    callTool<typeof args, { label: LabelRow; was_already_active: boolean }>(
      "unarchive_label", args),

  // Curated named buyers attached to a customer (crm.customer_buyers) —
  // wraps the same list_buyers_for_customer verb the /customers/:id Buyers
  // tab already called ad hoc via callMcpVerb().
  listBuyersForCustomer: (
    args: { customer_id: string; include_archived?: boolean },
  ): Promise<{ buyers: Array<Record<string, unknown>> }> =>
    callTool<typeof args, { buyers: Array<Record<string, unknown>> }>(
      "list_buyers_for_customer", args,
    ),

  getSupplier: (args: { id: string }) =>
    callTool<{ id: string }, { supplier: Record<string, unknown> | null; contacts: Array<Record<string, unknown>>; recent_quotes: Array<Record<string, unknown>>; recent_orders: Array<Record<string, unknown>> }>(
      "get_supplier", args),

  getProductionStatus: (args: { season?: string; customer_id?: string; supplier_id?: string; order_id?: string; status_filter?: Array<"on_track" | "at_risk" | "late" | "urgent"> } = {}) =>
    callTool<typeof args, { rollup: Record<string, unknown>; orders: Array<Record<string, unknown>> }>(
      "get_production_status", args),

  // Real-time T-Minus order dashboard. Single call returns 7 sections of
  // pre-aggregated data; UI just renders. Server-side RPC is
  // public.mcp_get_production_dashboard().
  getProductionDashboard: () =>
    callTool<Record<string, never>, ProductionDashboard>(
      "get_production_dashboard", {}),

  // Per-season KPI rows for the /seasons list page. SECURITY DEFINER —
  // gates total_value_zar behind staff.has_role(admin|sales) so the page
  // does not have to SELECT the masked column directly. See migration
  // 5440_mcp_get_season_kpis.sql. Called via PostgREST RPC, not the
  // JSON-RPC verb layer.
  getSeasonKpis: async (
    args: { season_id?: string } = {},
  ): Promise<{ rows: SeasonKpiRow[] }> => {
    const { data, error } = await supabase.rpc("mcp_get_season_kpis", {
      p_season_id: args.season_id ?? null,
    });
    if (error) throw new Error(error.message);
    return { rows: (data ?? []) as SeasonKpiRow[] };
  },

  // List active+archived seasons for dropdowns. There's no server-side
  // `list_seasons` MCP verb yet — interim implementation reads
  // production.seasons via PostgREST through the schema-scoped client.
  // Centralised here so every dropdown shares the same row shape; swap
  // the body for `callTool("list_seasons", ...)` if/when the verb lands.
  listSeasons: async (
    args: { include_archived?: boolean } = {},
  ): Promise<{ seasons: SeasonOption[] }> => {
    const prod = supabaseSchema("production");
    let q = prod
      .from("seasons")
      .select("id, code, name, status, archived_at")
      .is("deleted_at", null)
      .order("code", { ascending: false });
    if (!args.include_archived) {
      q = q.is("archived_at", null);
    }
    const { data, error } = await q;
    if (error) throw new Error(error.message);
    return { seasons: (data ?? []) as SeasonOption[] };
  },

  // NOTE for Wave 8: search_customers / search_suppliers do NOT yet include
  // updated_at / updated_by / agent_id. The card grids fall back to "—" via
  // LastEditedBy until those columns are added to the search verb response.
  searchCustomers: (args: { query: string; tier?: 1 | 2 | 3; limit?: number }) =>
    callTool<typeof args, { customers: Array<{ id: string; slug: string; name: string; tier: number | null; status: string; recent_orders_count: number; updated_at?: string | null; updated_by?: string | null; agent_id?: string | null }> }>(
      "search_customers", args),

  searchSuppliers: (args: { query: string; capability?: string; limit?: number }) =>
    callTool<typeof args, { suppliers: Array<{ id: string; slug: string; name: string; country: string | null; capabilities: string[] | null; status: string; recent_orders_count: number; recent_quotes_count: number; updated_at?: string | null; updated_by?: string | null; agent_id?: string | null }> }>(
      "search_suppliers", args),

  addActionItem: (args: AddActionInput) =>
    callTool<AddActionInput, { action: Record<string, unknown>; was_new: boolean }>(
      "add_action_item", args),

  resolveActionItem: (args: ResolveActionInput) =>
    callTool<ResolveActionInput, { action: Record<string, unknown>; action_update: Record<string, unknown> }>(
      "resolve_action_item", args),

  linkEmailToEntity: (args: { p_message_id: string; p_entity_type: EmailEntityType; p_entity_id: string; p_link_type?: string; p_confidence?: number; p_summary?: string; p_hydrate_body?: boolean }) =>
    callTool<typeof args, { link: Record<string, unknown>; body_hydrated: boolean }>(
      "link_email_to_entity", args),

  logMeeting: (args: Record<string, unknown>) =>
    callTool<typeof args, { meeting: Record<string, unknown>; action_items_created: Array<Record<string, unknown>> }>(
      "log_meeting", args),

  // ── Wave 5a action verbs ───────────────────────────────────────────────
  listActions: (args: {
    filter: ActionFilter;
    owner_user_id?: string;
    limit?: number;
    cursor?: string;
  }) =>
    callTool<typeof args, {
      actions: Action[];
      next_cursor: string | null;
      count: number;
      // Exact total matching the filter, uncapped by `limit` (#960). The
      // edge function (list_actions.ts) has always returned this; only the
      // portal's TS type omitted it.
      total_count: number;
      filter: string;
    }>("list_actions", args),

  getAction: (args: { id?: string; code?: string }) =>
    callTool<typeof args, { action: Action | null; updates: ActionUpdate[] }>(
      "get_action", args,
    ),

  addActionUpdate: (args: {
    action_id?: string;
    code?: string;
    update_type: ActionUpdateType;
    body?: string;
    from_value?: string;
    to_value?: string;
    evidence_url?: string;
  }) =>
    callTool<typeof args, { update: ActionUpdate; action: Action }>(
      "add_action_update", args,
    ),

  toggleJoshFlag: (args: { action_id?: string; code?: string; is_josh_flagged: boolean }) =>
    callTool<typeof args, { action: Action }>("toggle_josh_flag", args),

  // Admin-only soft-delete. Goes via the public.mcp_delete_action RPC which
  // raises 'forbidden' (42501) if the caller isn't admin. UI must gate the
  // button on useIsAdmin() so non-admins never see it.
  deleteAction: async (args: { code: string }): Promise<{ id: string; code: string; title: string }> => {
    const { data, error } = await supabase.rpc("mcp_delete_action", { p_code: args.code });
    if (error) throw error;
    return data as { id: string; code: string; title: string };
  },

  // Reassign an action to a new owner. Calls public.mcp_reassign_action,
  // which atomically flips owner_user_id and inserts a `reassignment`
  // action_updates row stamped with from/to owner ids + optional note.
  // Returns both the updated Action and the auto-inserted ActionUpdate so
  // the caller can refresh the detail page + the timeline in one shot.
  reassignAction: async (args: ReassignActionArgs): Promise<ReassignActionResult> => {
    const { data, error } = await supabase.rpc("mcp_reassign_action", {
      p_code: args.code,
      p_to_owner_user_id: args.to_owner_user_id,
      p_note: args.note ?? null,
    });
    if (error) throw error;
    return data as ReassignActionResult;
  },

  confirmProposedResolution: async (args: ConfirmProposedResolutionArgs): Promise<ConfirmProposedResolutionResult> => {
    const { data, error } = await supabase.rpc("mcp_confirm_proposed_resolution", {
      p_update_id: args.update_id,
      p_resolution_note: args.resolution_note ?? null,
    });
    if (error) throw error;
    return data as ConfirmProposedResolutionResult;
  },

  rejectProposedResolution: async (args: RejectProposedResolutionArgs): Promise<RejectProposedResolutionResult> => {
    const { data, error } = await supabase.rpc("mcp_reject_proposed_resolution", {
      p_update_id: args.update_id,
      p_reason: args.reason,
    });
    if (error) throw error;
    return data as RejectProposedResolutionResult;
  },

  // ── Wave 5b CRM/order verbs ────────────────────────────────────────────
  addCustomer: (args: {
    slug: string;
    name: string;
    tier?: 1 | 2 | 3;
    status?: CustomerStatus;
    legal_entity?: string;
    vat_number?: string;
    payment_terms?: string;
    notes?: string;
    idempotency_key?: string;
  }) =>
    callTool<typeof args, { customer: Customer; was_new: boolean }>(
      "add_customer", args,
    ),

  editCustomer: (args: { id: string; patch: Record<string, unknown> }) =>
    callTool<typeof args, { customer: Customer }>("edit_customer", args),

  editSupplier: (args: { id: string; patch: Record<string, unknown> }) =>
    callTool<typeof args, { supplier: Supplier }>("edit_supplier", args),

  searchOrders: (args: {
    query?: string;
    season?: string;
    customer_id?: string;
    supplier_id?: string;
    status?: OrderStatus;
    limit?: number;
  }) =>
    callTool<typeof args, { orders: OrderSearchHit[]; count: number }>(
      "search_orders", args,
    ),

  // ── Wave 5c QA + quotes verbs ──────────────────────────────────────────
  recordQaInspection: (args: {
    order_id: string;
    inspector: string;
    inspection_date: string;
    inspection_type: "inline" | "final" | "pre-shipment";
    result: "pass" | "fail" | "hold" | "retry";
    aql_level?: string;
    defect_count?: number;
    notes?: string;
    evidence_link?: string;
    idempotency_key?: string;
  }) =>
    callTool<typeof args, { id: string; was_new: boolean }>(
      "record_qa_inspection", args,
    ),

  // ── QA approval flow (W4-A1) ────────────────────────────────────────────
  // The v2 record fields (qima_ref .. photos) are #1070 / migration 6158 and
  // are all optional: a caller sending only the W4-A1 set keeps working.
  // Requires the qa.inspect capability — the verb refuses without it, and the
  // RPC re-checks it in SQL.
  submitQaInspection: (args: {
    order_id: string;
    inspector: string;
    inspector_user_id?: string;
    inspector_company?: string;
    inspection_date: string;
    inspection_type: "inline" | "final" | "pre-shipment";
    aql_level?: string;
    aql_critical?: number;
    aql_major?: number;
    aql_minor?: number;
    result?: "pass" | "fail" | "hold" | "retry";
    status?: "pending" | "pass" | "fail" | "rework";
    report_pdf_url?: string;
    report_pdf_storage_key?: string;
    notes?: string;
    idempotency_key?: string;
    /** The inspector's own job/report reference (a QIMA job number). */
    qima_ref?: string;
    /** YYYY-MM-DD the inspection was BOOKED, as opposed to carried out. */
    booking_date?: string;
    /** Pieces presented for inspection. */
    lot_size?: number;
    /** Pieces actually drawn and inspected. */
    sample_size?: number;
    /** Point-of-measure findings, e.g. { chest: { spec: 52, found: 52.5 } }. */
    measurements?: Record<string, unknown>;
    /** production.purchase_orders.id when the inspection covers one PPO. */
    purchase_order_id?: string;
    /** Order-line scope. Every line must belong to order_id. */
    lines?: Array<{ order_line_id: string; qty_inspected?: number; notes?: string }>;
    /** Photos already uploaded to the private `qa-reports` bucket. */
    photos?: Array<{ storage_key: string; caption?: string; sort_order?: number }>;
  }) =>
    callTool<typeof args, {
      id: string;
      was_new: boolean;
      /** How many qa_inspection_lines rows landed. */
      lines: number;
      /** How many qa_inspection_photos rows landed. */
      photos: number;
    }>(
      "submit_qa_inspection", args,
    ),

  approveQaInspection: (args: {
    id: string;
    decision: "approve" | "reject" | "rework";
    next_action: "ship" | "hold" | "rework" | "reject";
    notes?: string;
  }) =>
    callTool<typeof args, {
      id: string;
      status: string;
      next_action: string;
      approved_at: string;
      approved_by_user_id: string | null;
      /** Whether qa-approval-notify confirmed a send (#1071). */
      notified: boolean;
      notify_reason: string | null;
    }>("approve_qa_inspection", args),

  // ── The QA fail decision (#1071, epic #1069 phase 1b, migration 6168) ─────
  // decide_qa_inspection replaces approveQaInspection for everything that is
  // not a plain pass: it can express `discount`, it gates a non-pass on
  // orders.signoff rather than qa.decide (ruling 20), and the RPC refuses a
  // non-pass with no problem log — or, for ship / discount, with no recorded
  // customer permission (D29). It also fires qa-approval-notify server-side,
  // which is why the portal no longer POSTs that function itself.
  // Capability: qa.decide for a pass, orders.signoff for a non-pass.
  decideQaInspection: (args: {
    id: string;
    decision: "approve" | "reject" | "rework";
    next_action: "ship" | "hold" | "rework" | "reject" | "discount";
    notes?: string;
  }) =>
    callTool<typeof args, {
      id: string;
      status: string;
      next_action: string;
      approved_at: string;
      approved_by_user_id: string | null;
      /** Live problem-log rows behind the decision (0 for a pass). */
      problem_log_count: number;
      customer_permission_ref: string | null;
      /** False when qa-approval-notify did not confirm a send. */
      notified: boolean;
      notify_reason: string | null;
    }>("decide_qa_inspection", args),

  /** Capability: qa.inspect. Required before any non-pass decision (D29). */
  logQaProblem: (args: {
    qa_inspection_id: string;
    what_failed: string;
    root_cause?: string | null;
    agreed_fix?: string | null;
    supplier_liable?: boolean;
    discount_pct?: number | null;
  }) =>
    callTool<typeof args, {
      id: string;
      qa_inspection_id: string;
      logged_by: string | null;
      problem_log_count: number;
    }>("log_qa_problem", args),

  /** Capability: orders.signoff. Required to ship or discount a failed lot. */
  recordCustomerPermissionToShip: (args: {
    qa_inspection_id: string;
    reference: string;
    permission_date: string;
    storage_key?: string | null;
  }) =>
    callTool<typeof args, {
      id: string;
      customer_permission_ref: string;
      customer_permission_date: string;
      customer_permission_storage_key: string | null;
      customer_permission_recorded_by: string | null;
    }>("record_customer_permission_to_ship", args),

  // #1175 (Epic #1069 phase 2a, migration 6192): record a QIMA booking
  // (date, company, ref) as a NEW `pending` production.qa_inspections row,
  // BEFORE the inspection itself has happened. Distinct from
  // submitQaInspection — which records a full v1-arrived result — and from
  // recordQaInspection / the legacy verb: this is the "book it" half design
  // §6.2's S8 -> S9 row names first. inspection_date defaults server-side to
  // the order's own goods_ready_date when omitted. Clears the order from
  // ops.qa_booking_reminder_candidates and satisfies the 'inspection_booked'
  // checklist item the moment it lands. Capability: qa.inspect.
  recordQaBooking: (args: {
    order_id: string;
    booking_date: string;
    inspector_company: string;
    qima_ref?: string;
    inspection_date?: string;
    purchase_order_id?: string;
    inspection_type?: "inline" | "final" | "pre-shipment";
    notes?: string;
    idempotency_key?: string;
  }) =>
    callTool<typeof args, {
      id: string;
      was_new: boolean;
      order_id: string;
      booking_date: string;
      inspection_date: string;
    }>("record_qa_booking", args),

  /** Capability: qa.inspect. The previous inspection must already be decided. */
  createReinspection: (args: {
    previous_id: string;
    inspection_date?: string | null;
    inspector?: string | null;
    inspector_company?: string | null;
    inspection_type?: "inline" | "final" | "pre-shipment" | null;
    booking_date?: string | null;
    qima_ref?: string | null;
    notes?: string | null;
  }) =>
    callTool<typeof args, {
      id: string;
      previous_id: string;
      order_id: string | null;
      status: string;
      lines_carried: number;
    }>("create_reinspection", args),

  // [Epic #1069] Phase 3b (#1226, migration 6217) — fill the booked
  // inspection from a fully confirmed QIMA report extraction. An UPDATE of
  // the existing row (never a second one) and it never touches `status` —
  // that stays a separate, explicit decision via <QaDecisionPanel>.
  // Capability: qa.inspect.
  applyQimaReportExtractionToInspection: (args: { extraction_id: string }) =>
    callTool<typeof args, QimaReportExtractionApplied>(
      "apply_qima_report_extraction_to_inspection", args,
    ),

  listQaPending: (
    args: {
      order_id?: string;
      supplier_id?: string;
      status?: "pending" | "pass" | "fail" | "rework";
      result?: "pass" | "fail" | "hold" | "retry";
      since?: string;
      limit?: number;
      cursor?: string;
    } = {},
  ) =>
    callTool<typeof args, {
      qa_inspections: Array<Record<string, unknown>>;
      next_cursor: string | null;
      count: number;
      total_count: number;
      has_more: boolean;
    }>("list_qa_pending", args),

  getQaInspectionDetail: (args: { id: string }) =>
    callTool<typeof args, {
      inspection: Record<string, unknown> | null;
      order: Record<string, unknown> | null;
      supplier: Record<string, unknown> | null;
      approver: Record<string, unknown> | null;
      /** The PPO this inspection is scoped to, when it is (#1070). */
      purchase_order: Record<string, unknown> | null;
      /** production.qa_inspection_lines joined to the order line (#1070). */
      lines: Array<Record<string, unknown>>;
      /** production.qa_inspection_photos — storage keys to sign (#1070). */
      photos: Array<Record<string, unknown>>;
      /** production.qa_problem_logs for this inspection, oldest first (#1071). */
      problem_logs: Array<Record<string, unknown>>;
      /** The re-inspection chain, oldest first; the last row is current (#1071). */
      chain: Array<Record<string, unknown>>;
      /** False once a re-inspection superseded this inspection (#1071). */
      is_current: boolean | null;
      report_url: string | null;
    }>("get_qa_inspection_detail", args),

  getQaDashboard: (
    args: { since?: string; supplier_id?: string } = {},
  ) =>
    callTool<typeof args, {
      summary: Record<string, unknown>;
      by_supplier: Array<Record<string, unknown>>;
      pending: Array<Record<string, unknown>>;
      since: string | null;
      /**
       * #1177 (Epic #1069 phase 2c, migration 6209): the reconciled
       * booking -> result -> decision status (production.order_qa_state,
       * 6207) for every live order currently in S8 (ready_to_ship). Not
       * windowed by `since` — this is current posture, not inspection
       * history.
       */
      s8_checklist?: {
        summary: {
          ready_to_ship?: number;
          awaiting_booking?: number;
          checklist_incomplete?: number;
        };
        orders: Array<Record<string, unknown>>;
      };
    }>("get_qa_dashboard", args),

  // Capability: costings.manage (#1055 — the verb had no auth check before).
  // brief_id links the quote to the brief it was given against (ruling D12);
  // customer_id and style_code then default from that brief server-side.
  addQuote: (args: {
    supplier_id: string;
    customer_id?: string;
    style_code?: string;
    description: string;
    quantity: number;
    fob_unit_price: number;
    currency?: "USD" | "CNY" | "ZAR" | "EUR" | "GBP";
    lead_time_days?: number;
    validity_until?: string;
    notes?: string;
    idempotency_key?: string;
    brief_id?: string;
  }) =>
    callTool<typeof args, { id: string; was_new: boolean }>(
      "add_quote", args,
    ),

  // Accept ONE of a brief's quotes (#1055, ruling D12). Supersedes the brief's
  // other open/accepted quotes and writes the brief's supplier_id +
  // new_supplier_dollar_price. Capability: costings.manage.
  acceptQuote: (args: { quote_id: string; brief_id?: string }) =>
    callTool<typeof args, {
      quote: Record<string, unknown>;
      brief_id: string | null;
      superseded_quote_ids: string[];
      was_noop: boolean;
    }>("accept_quote", args),

  // Reject a quote with a REQUIRED reason; the row is kept, never deleted
  // (#1055). Capability: costings.manage.
  rejectQuote: (args: { quote_id: string; reason: string }) =>
    callTool<typeof args, {
      quote: Record<string, unknown>;
      reason: string;
    }>("reject_quote", args),

  // ── Wave-X list + detail verbs (1300_mcp_list_verbs.sql) ───────────────
  // Capability: costings.view OR costings.manage (#1055). Rows carry brief_id,
  // moq, lead_time_days and superseded_by since migration 6157.
  listQuotes: (
    args: {
      supplier_id?: string;
      customer_id?: string;
      status?: QuoteStatus;
      season?: string;
      /** #1055 — only this brief's quotes. Used by <BriefQuotesPanel>. */
      brief_id?: string;
      limit?: number;
      cursor?: string;
    } = {},
  ) =>
    callTool<typeof args, {
      quotes: BriefQuote[];
      next_cursor: string | null;
      count: number;
      total_count: number;
    }>("list_quotes", args),

  getQuote: (args: { id: string }) =>
    callTool<typeof args, {
      quote: Record<string, unknown> | null;
      supplier: Record<string, unknown> | null;
      customer: Record<string, unknown> | null;
      parent_order: Record<string, unknown> | null;
    }>("get_quote", args),

  listMeetings: (
    args: {
      customer_id?: string;
      supplier_id?: string;
      attendee?: string;
      since?: string;
      limit?: number;
      cursor?: string;
    } = {},
  ) =>
    callTool<typeof args, {
      meetings: Array<Record<string, unknown> & { display_id: string }>;
      next_cursor: string | null;
      count: number;
      total_count: number;
    }>("list_meetings", args),

  getMeeting: (args: { id: string }) =>
    callTool<typeof args, {
      meeting: (Record<string, unknown> & { display_id: string }) | null;
      customer: Record<string, unknown> | null;
      supplier: Record<string, unknown> | null;
      order: Record<string, unknown> | null;
      cascading_actions: Array<Record<string, unknown>>;
    }>("get_meeting", args),

  listQaInspections: (
    args: {
      order_id?: string;
      result?: "pass" | "fail" | "hold" | "retry";
      since?: string;
      limit?: number;
      cursor?: string;
    } = {},
  ) =>
    callTool<typeof args, {
      qa_inspections: Array<Record<string, unknown>>;
      next_cursor: string | null;
      count: number;
      total_count: number;
    }>("list_qa_inspections", args),

  getQaInspection: (args: { id: string }) =>
    callTool<typeof args, {
      inspection: Record<string, unknown> | null;
      order: Record<string, unknown> | null;
      report_url: string | null;
      defects: Array<Record<string, unknown>>;
    }>("get_qa_inspection", args),

  listSamples: (
    args: {
      order_id?: string;
      /** #1068: narrow to the samples of ONE order line. */
      order_line_id?: string;
      supplier_id?: string;
      kind?: SampleKind;
      season?: string;
      /** production.samples.current_status, exact match (#1116). */
      status?: string;
      /** Partial match against the sample's resolved milestone-owner label (#1116). */
      owner?: string;
      limit?: number;
      cursor?: string;
    } = {},
  ) =>
    callTool<typeof args, {
      samples: Array<Record<string, unknown>>;
      next_cursor: string | null;
      count: number;
      total_count: number;
    }>("list_samples", args),

  getSample: (args: { id: string }) =>
    callTool<typeof args, {
      sample: Record<string, unknown> | null;
      iterations: Array<Record<string, unknown>>;
      loans: Array<Record<string, unknown>>;
      order: Record<string, unknown> | null;
    }>("get_sample", args),

  // Log a new production.sample_iterations row against an existing sample.
  // iteration_no is server-assigned (max+1 for the sample). Argument names
  // match add_iteration.ts's inputSchema field-for-field.
  addIteration: (args: {
    sample_id: string;
    requested_at?: string;
    received_at?: string;
    decision?: "approved" | "rejected" | "revise" | "pending";
    notes?: string;
    defects?: Array<Record<string, unknown>>;
    metadata?: Record<string, unknown>;
  }) =>
    callTool<typeof args, { iteration: Record<string, unknown> }>(
      "add_iteration", args,
    ),

  // ── Real production samples (#1068, Epic #1066 Phase 1b; migration 6170) ──
  // Samples are per ORDER LINE and feed an ORDER milestone, so approving every
  // line's sample of a kind is what approves that milestone — the roll-up
  // happens server-side inside decide_sample and is reported back as
  // `milestone_rollup`. All three verbs are production.plan-gated at the Edge
  // Function layer (admin, merch, production, sales).

  /** Request sample kinds for one order line. An already-live kind is skipped, not an error.
   *
   *  Since #1114 (migration 6184) the verb consults the customer's
   *  required-kind matrix: OMIT `kinds` to request exactly what the customer
   *  requires, and when `kinds` is given any required kind not already live on
   *  the line is folded in too (reported as `required_added`, and per created
   *  row as `required`). That union is suppressed when `size` is set. */
  requestSamples: (args: {
    order_line_id: string;
    kinds?: SampleKind[];
    requested_at?: string;
    due_date?: string;
    purchase_order_id?: string;
    size?: string;
    notes?: string;
  }) =>
    callTool<typeof args, {
      order_id: string | null;
      order_line_id: string;
      created: Array<{
        id: string;
        kind: string;
        size: string | null;
        milestone_id: string | null;
        required?: boolean;
      }>;
      created_count: number;
      skipped: string[];
      skipped_count: number;
      required_kinds?: string[];
      required_added?: string[];
    }>("request_samples", args),

  /** Record one submission of a sample: size, measurement chart, comment, virtual flag. */
  recordSampleSubmission: (args: {
    sample_id: string;
    submitted_at?: string;
    received_at?: string;
    size?: string;
    measurements?: Record<string, SampleMeasurementPoint>;
    result_comment?: string;
    is_virtual?: boolean;
    defects?: Array<Record<string, unknown>>;
    notes?: string;
  }) =>
    callTool<typeof args, {
      submission: Record<string, unknown>;
      sample: Record<string, unknown> | null;
    }>("record_sample_submission", args),

  /** Decide a sample's latest submission, on a date that is today or earlier. */
  decideSample: (args: {
    sample_id: string;
    decision: "approved" | "rejected" | "revise";
    date?: string;
    comment?: string;
  }) =>
    callTool<typeof args, {
      sample: Record<string, unknown> | null;
      submission: Record<string, unknown>;
      decision_date: string;
      milestone_id: string | null;
      milestone_rollup: SampleMilestoneRollup;
      milestone: Record<string, unknown> | null;
      siblings_total: number;
      siblings_approved: number;
    }>("decide_sample", args),

  listShipments: (
    args: {
      order_id?: string;
      supplier_id?: string;
      status?: ShipmentStatus;
      season?: string;
      limit?: number;
      cursor?: string;
    } = {},
  ) =>
    callTool<typeof args, {
      shipments: Array<Record<string, unknown>>;
      next_cursor: string | null;
      count: number;
      total_count: number;
    }>("list_shipments", args),

  getShipment: (args: { id: string }) =>
    callTool<typeof args, ShipmentDocument>("get_shipment", args),

  /**
   * The completeness checklist for a shipment (#1179) — every item still
   * missing for its next transition(s) out of its current status. Drives
   * <ShipmentMissingItemsPanel>. Same evaluator as the shipment's row badge
   * and its /todo alerts, so the three cannot disagree. No override echo: no
   * trigger gates a shipment status move, so no shipment override exists.
   */
  getShipmentMissingItems: (
    args: { id: string },
  ): Promise<ShipmentMissingItemsResult> =>
    callTool<typeof args, ShipmentMissingItemsResult>(
      "get_shipment_missing_items",
      args,
    ),

  // ── Multi-order shipments (#1073, Epic #1072 phase 1a; migration 6172) ──
  // A shipment carries MANY customer orders (design §5.12); every write goes
  // through one of these seven capability-gated RPCs — before 6172 the table
  // had read RPCs only and an open `with check (true)` insert policy.

  createShipment: (args: {
    order_ids: string[];
    lines?: ShipmentLineInput[];
    fields?: ShipmentFieldsPatch;
  }) =>
    callTool<typeof args, ShipmentDocument & {
      orders_summary: ShipmentOrdersSummary;
      lines_summary: ShipmentLinesSummary;
    }>("create_shipment", args),

  editShipment: (args: { id: string; fields: ShipmentFieldsPatch }) =>
    callTool<typeof args, ShipmentDocument>("edit_shipment", args),

  addShipmentOrders: (args: {
    id: string;
    order_ids: string[];
    lines?: ShipmentLineInput[];
  }) =>
    callTool<typeof args, ShipmentDocument & {
      orders_summary: ShipmentOrdersSummary;
      lines_summary: ShipmentLinesSummary;
    }>("add_shipment_orders", args),

  removeShipmentOrder: (args: { id: string; order_id: string }) =>
    callTool<typeof args, ShipmentDocument & { removed_order_id: string }>(
      "remove_shipment_order", args,
    ),

  approveShippingMark: (args: { id: string; upload_id?: string }) =>
    callTool<typeof args, ShipmentDocument & { was_noop: boolean }>(
      "approve_shipping_mark", args,
    ),

  cancelShipment: (args: { id: string; reason: string }) =>
    callTool<typeof args, ShipmentDocument & { was_noop: boolean }>(
      "cancel_shipment", args,
    ),

  recordDelivery: (args: {
    id: string;
    status: ShipmentDeliveryStatus;
    delivered_date?: string;
    accepted_date?: string;
    delivered_qty?: Record<string, number>;
    discrepancies?: string;
    delivery_proof_url?: string;
    /** GRN number from the customer DC (#1178). */
    grn_number?: string;
    /** DC booking / appointment number for the delivery slot (#1178). */
    dc_booking_number?: string;
    /** ISO date of the DC booking / appointment (#1178). */
    dc_booking_date?: string;
    /**
     * Per-order-line received quantity + discrepancy reason, upserted into
     * production.shipment_delivery_lines alongside delivered_qty (#1178).
     */
    received_lines?: DeliveryLineInput[];
    /**
     * Fields to RETRACT. An omitted `accepted_date` / `discrepancies` /
     * `delivery_proof_url` / `grn_number` / `dc_booking_number` /
     * `dc_booking_date` leaves that column alone, so blanking one in the UI
     * has to name it here to actually clear it. Naming a field and also
     * passing a value for it is refused.
     */
    clear?: ClearableDeliveryField[];
  }) =>
    callTool<typeof args, ShipmentDocument>("record_delivery", args),

  /** Resolve one order line's delivery discrepancy on a shipment (#1178). */
  resolveDeliveryDiscrepancy: (args: {
    id: string;
    order_line_id: string;
    resolution_reason: string;
  }) =>
    callTool<typeof args, ShipmentDocument>("resolve_delivery_discrepancy", args),

  /**
   * Create or fill a shipment from a FULLY CONFIRMED supplier shipping-document
   * extraction (#1212, Epic #1072 phase 3b, migration 6216).
   *
   * Sends no field content: the server reads the extraction's
   * `confirmed_fields` — never the AI's `ai_draft` — and writes only those
   * whose names are shipping columns. Everything else the document stated is
   * returned in `skipped_fields` and written nowhere. `order_ids` matters only
   * when no shipment exists yet and one is being created.
   */
  applyShipmentFromExtraction: (args: {
    extraction_id: string;
    order_ids?: string[];
    notes?: string;
  }) =>
    callTool<typeof args, ShipmentFromExtraction>(
      "apply_shipment_from_extraction", args,
    ),

  listStockOffers: (
    args: {
      supplier_id?: string;
      season?: string;
      since?: string;
      limit?: number;
      cursor?: string;
    } = {},
  ) =>
    callTool<typeof args, {
      stock_offers: Array<Record<string, unknown>>;
      next_cursor: string | null;
      count: number;
      total_count: number;
    }>("list_stock_offers", args),

  getStockOffer: (args: { id: string }) =>
    callTool<typeof args, {
      offer: Record<string, unknown> | null;
      supplier: Record<string, unknown> | null;
      supplier_capabilities: string[];
    }>("get_stock_offer", args),

  // "One action" stock offer -> quote conversion. An offer already carries
  // supplier/description/quantity/price, so this needs almost no input.
  // Admin/merch-only (mirrors convertQuoteToOrder). Stops at creating the
  // quote — does not touch convertQuoteToOrder (#465).
  convertStockOfferToQuote: (args: {
    stock_offer_id: string;
    customer_id?: string;
    style_code?: string;
    quantity?: number;
    fob_unit_price?: number;
    currency?: "USD" | "CNY" | "ZAR" | "EUR" | "GBP";
    lead_time_days?: number;
    validity_until?: string;
    notes?: string;
    idempotency_key?: string;
  }) =>
    callTool<typeof args, {
      quote: Record<string, unknown>;
      was_new: boolean;
      stock_offer_id: string | null;
    }>("convert_stock_offer_to_quote", args),

  // Record (or change) a buyer decision on a stock offer. Argument names
  // match decide_stock_offer.ts's inputSchema field-for-field.
  decideStockOffer: (args: {
    stock_offer_id: string;
    decision: "interested" | "pass";
  }) =>
    callTool<typeof args, { offer: Record<string, unknown> }>(
      "decide_stock_offer", args,
    ),

  listProductDevelopments: (
    args: {
      season?: string;
      customer_id?: string;
      status?:
        | "brief" | "cad" | "tech_pack" | "fit_sample"
        | "costing" | "quoted" | "po_issued" | "dropped";
      limit?: number;
      cursor?: string;
    } = {},
  ) =>
    callTool<typeof args, {
      product_developments: Array<Record<string, unknown>>;
      next_cursor: string | null;
      count: number;
      total_count: number;
    }>("list_product_developments", args),

  getProductDevelopment: (args: { id: string }) =>
    callTool<typeof args, {
      development: Record<string, unknown> | null;
      season: Record<string, unknown> | null;
      supplier: Record<string, unknown> | null;
      customer: Record<string, unknown> | null;
      iterations: Array<Record<string, unknown>>;
    }>("get_product_development", args),

  listStyleCostings: (
    args: {
      order_id?: string;
      supplier_id?: string;
      status?: "current" | "historical";
      // #507 — filter to costings whose brief_id matches. Used by the brief
      // detail page to resolve whether a costing exists for a given brief.
      brief_id?: string;
      limit?: number;
      cursor?: string;
    } = {},
  ) =>
    callTool<typeof args, {
      style_costings: Array<Record<string, unknown>>;
      next_cursor: string | null;
      count: number;
      total_count: number;
    }>("list_style_costings", args),

  // Create a production.style_costings row. style_code is required; brief_id
  // (#507) optionally links the won brief this costing was prepared from.
  // Caller must hold admin/merch/production.
  createStyleCosting: (
    args: {
      style_code: string;
      dev_id?: string;
      supplier_id?: string;
      season_id?: string;
      fabric?: string;
      fabric_weight_gsm?: number;
      fob_usd?: number;
      factor?: number;
      landed_zar?: number;
      target_margin_pct?: number;
      quote_id?: string;
      notes?: string;
      metadata?: Record<string, unknown>;
      brief_id?: string;
      // #1056 — the rest of the margin inputs. margin_pct and royalty_zar are
      // NOT here: they are generated columns the database computes.
      fx_rate?: number;
      royalty_pct?: number;
      marketing_zar?: number;
      rsp_zar?: number;
      selling_multiple?: number;
      selling_zar?: number;
      /**
       * The costing this one replaces. Retired in the SAME transaction
       * (is_current=false, superseded_by = the new row), which is how the
       * brief's Costing panel saves an edit as a superseding costing instead
       * of mutating history.
       */
      supersedes_id?: string;
      supersede_note?: string;
    },
  ) =>
    callTool<typeof args, { costing: Record<string, unknown> }>(
      "create_style_costing", args,
    ),

  // Build a costing from a brief's ACCEPTED quote (#1056, Epic #1054 phase 1b).
  // Pre-fills the FOB from the quote and both factors from the customer's flow
  // settings, leaving anything the customer has no default for null rather
  // than guessing — `prefill` says which is which. Supersedes whatever current
  // costing the brief already had. Capability: costings.manage.
  createCostingFromQuote: (args: { quote_id: string }) =>
    callTool<typeof args, {
      costing: Record<string, unknown>;
      quote_id: string | null;
      brief_id: string | null;
      superseded_costing_ids: string[];
      customer_min_gp_pct: number | string | null;
      prefill: Record<string, string>;
    }>("create_costing_from_quote", args),

  getStyleCosting: (args: { id: string }) =>
    callTool<typeof args, {
      costing: Record<string, unknown> | null;
      orders: Array<Record<string, unknown>>;
      supplier: Record<string, unknown> | null;
      quote: Record<string, unknown> | null;
      components: Array<Record<string, unknown>>;
      // Since migration 6031: the actual margin COMPUTED from the linked
      // orders' lines for this style (production.order_planning_rows), not the
      // metadata->>'actual_margin_pct' blob the detail page used to render.
      actual_margin: StyleCostingActualMargin | null;
      // #1056 — the brief this costing was prepared from, for the back-link.
      brief: {
        id?: string;
        display_id?: string | null;
        title?: string | null;
        status?: string | null;
        customer_id?: string | null;
        customer_name?: string | null;
      } | null;
      // #1056 — the brief customer's OWN minimum GP % (ruling D25). Null when
      // they have no override; the global 30% fallback is deliberately not
      // surfaced, so a note about being "below the customer's minimum" only
      // ever fires on a number someone actually chose.
      customer_min_gp_pct: number | string | null;
    }>("get_style_costing", args),

  // Mark a style costing as superseded. Flips is_current=false, stamps
  // superseded_at=now() (first time only), and optionally records the
  // replacement costing + note. Caller must hold admin/merch/production.
  supersedeStyleCosting: (
    args: { id: string; superseded_by_id?: string; note?: string },
  ) =>
    callTool<typeof args, { costing: Record<string, unknown> }>(
      "supersede_style_costing", args,
    ),

  listEmailsForEntity: (
    args: {
      entity_type: EmailEntityType;
      entity_id: string;
      since?: string;
      limit?: number;
      cursor?: string;
    },
  ) =>
    callTool<typeof args, {
      emails: Array<Record<string, unknown>>;
      next_cursor: string | null;
      count: number;
      total_count: number;
    }>("list_emails_for_entity", args),

  getEmailBody: (args: { message_id: string }) =>
    callTool<typeof args, {
      message: Record<string, unknown> | null;
      body: Record<string, unknown> | null;
      attachments: Array<Record<string, unknown>>;
      is_hydrated: boolean;
    }>("get_email_body", args),

  // Signed URL for one stored attachment (#1057, Epic #957 Phase 3a). Gated
  // on email.read. signed_url is null with a reason (not_found | not_stored |
  // sign_failed) when there is nothing to sign — not_stored covers
  // oversized/inline/failed-upload attachments.
  getEmailAttachmentUrl: (args: { attachment_id: string }) =>
    callTool<typeof args, {
      signed_url: string | null;
      filename: string | null;
      content_type: string | null;
      reason: string | null;
      expires_in: number | null;
    }>("get_email_attachment_url", args),

  // ── /emails inbox verbs (migration 6032) ────────────────────────────────
  // search_emails is mailbox-wide; list_emails_for_entity above only sees
  // messages already linked to one entity.
  searchEmails: (
    args: {
      query?: string;
      from_addr?: string;
      since?: string;
      until?: string;
      account_id?: string;
      folder?: string;
      limit?: number;
      cursor?: string;
    } = {},
  ) =>
    callTool<typeof args, {
      emails: EmailListRow[];
      next_cursor: string | null;
      count: number;
      total_count: number;
      has_more: boolean;
    }>("search_emails", args),

  getEmailThread: (args: { message_id: string }) =>
    callTool<typeof args, {
      thread: Record<string, unknown> | null;
      messages: EmailThreadMessage[];
      count: number;
      root_message_id: string | null;
    }>("get_email_thread", args),

  listEmailLinks: (args: { message_id: string }) =>
    callTool<typeof args, { links: EmailLink[]; count: number }>(
      "list_email_links", args,
    ),

  // email.links is insert/delete by design — correcting a link is unlink +
  // linkEmailToEntity against the right target.
  unlinkEmailFromEntity: (
    args: {
      p_message_id: string;
      p_entity_type: EmailEntityType;
      p_entity_id: string;
    },
  ) =>
    callTool<typeof args, {
      deleted: boolean;
      link: Record<string, unknown> | null;
    }>("unlink_email_from_entity", args),

  // ── Sample Library verbs ────────────────────────────────────────────────
  // Defensive: if the server doesn't yet expose these, callers should fall
  // back to direct PostgREST via supabaseSchema("library"). The wrappers exist
  // so the UI compiles + ships before the server-side verbs land.

  listLibraryItems: (args: ListLibraryItemsInput = {}) =>
    callTool<ListLibraryItemsInput, ListLibraryItemsOutput>(
      "list_library_items", args,
    ),

  imageGenEligibility: (args: {
    filters?: ImageGenFilters;
    kinds?: Array<"ghost_front" | "ghost_back">;
    include_ids?: boolean;
  } = {}) =>
    callTool<typeof args, { result: ImageGenEligibility }>(
      "image_gen_eligibility", args,
    ),

  createImageGenRun: (args: {
    filters: ImageGenFilters;
    kinds: Array<"ghost_front" | "ghost_back">;
    mode?: "immediate";
    budget_cap_usd?: number;
    force?: boolean;
    /** samples.ai_models.id — which engine to run on. Omit for the registry default. */
    ai_model_id?: string;
  }) =>
    callTool<typeof args, CreateImageGenRunResult>("create_image_gen_run", args),

  cancelImageGenRun: (args: { run_id: string }) =>
    callTool<typeof args, { ok: boolean; error?: string | null; cancelled_jobs?: number | null }>(
      "cancel_image_gen_run", args,
    ),

  resumeImageGenRun: (args: { run_id: string }) =>
    callTool<typeof args, { ok: boolean; error?: string | null }>(
      "resume_image_gen_run", args,
    ),

  reviewImageGenJob: (args: {
    job_id: string;
    action: "approve" | "reject" | "regenerate" | "pass_audit" | "fail_audit";
    regenerate?: boolean;
  }) =>
    callTool<typeof args, ReviewImageGenJobResult>("review_image_gen_job", args),

  // Admin-only: build the exact ghost-mannequin prompt(s) for a sample with
  // no generation and no cost. Uses the same prompt builder as the worker.
  previewImageGenPrompt: (args: {
    sample_id: string;
    kinds?: Array<"ghost_front" | "ghost_back">;
  }) =>
    callTool<typeof args, PreviewImageGenPromptResult>(
      "preview_image_gen_prompt",
      args,
    ),

  getLibraryItem: (args: { id: string }) =>
    callTool<typeof args, {
      item: LibraryItem | null;
      style: Style | null;
      style_tags: Array<{ tag_id: string; name: string; category: string; color: string }>;
      sibling_colorways: Array<{
        id: string; sample_id: string | null; library_code: string | null;
        colour: string | null; pantone_code: string | null; size: string | null;
        current_status: string | null; archived_at: string | null; first_image_url: string | null;
      }>;
      images: Array<{ id: string; url: string; label: string; position: number }>;
      units: LibraryItemUnit[];
      units_total: number;
      units_available: number;
      active_loans: Array<Record<string, unknown>>;
      meetings: Array<Record<string, unknown>>;
      tags: Array<{ tag_id: string; name: string; category: string; color: string }>;
      customer_codes: LibraryItemCustomerCode[];
    }>(
      "get_library_item", args,
    ),

  // 5985/5986: physical copies (units) under one S-number. addLibraryItemUnits
  // backs the "Add copy" action + the quantity field on the add-sample forms;
  // updateLibraryItemUnit edits one copy (condition / location / notes /
  // retired-lost transitions — on_loan is loan-controlled).
  addLibraryItemUnits: (args: {
    library_item_id: string;
    count?: number;
    condition?: LibraryItemUnit["condition"];
    storage_location_id?: string;
    notes?: string;
  }) =>
    callTool<typeof args, {
      library_item_id: string;
      units: LibraryItemUnit[];
      units_total: number;
    }>("add_library_item_units", args),

  updateLibraryItemUnit: (args: {
    unit_id: string;
    patch: {
      condition?: LibraryItemUnit["condition"];
      current_status?: "in_office" | "retired" | "lost";
      storage_location_id?: string | null;
      notes?: string | null;
    };
  }) =>
    callTool<typeof args, { unit: LibraryItemUnit }>(
      "update_library_item_unit", args,
    ),

  // QR scan resolver — match by sample_id (S-YYYY-NNN), library_code, or
  // style_code in a single round-trip. Returns the same envelope as
  // getLibraryItem (item null when no match).
  getLibraryItemByCode: (args: { code: string }) =>
    callTool<typeof args, {
      item: LibraryItem | null;
      images: Array<Record<string, unknown>>;
      active_loans: Array<Record<string, unknown>>;
      meeting_history: Array<Record<string, unknown>>;
    }>("get_library_item_by_code", args),

  addLibraryItem: (args: Partial<LibraryItem> & { sample_id: string }) =>
    callTool<typeof args, { item: LibraryItem; was_new: boolean }>(
      "add_library_item", args,
    ),

  editLibraryItem: (args: { id: string; patch: Partial<LibraryItem> }) =>
    callTool<typeof args, { item: LibraryItem }>(
      "edit_library_item", args,
    ),

  // Admin-only HARD delete of a library item (sample). Permanent: removes the
  // sample + every child (cascade) + detaches loans / presentation_items, and
  // returns the storage object keys so the caller can purge the bytes. The
  // server raises 'forbidden' (42501) for non-admins, 'P0001' on an open loan,
  // '22023' on confirm-code mismatch. UI MUST gate the button on useIsAdmin().
  // Direct supabase.rpc (not the MCP orchestrator), same as deleteAction.
  previewHardDeleteLibraryItem: async (args: { id: string }): Promise<HardDeletePreview> => {
    const { data, error } = await supabase.rpc(
      "mcp_preview_hard_delete_library_item", { p_id: args.id },
    );
    if (error) throw error;
    return data as HardDeletePreview;
  },

  hardDeleteLibraryItem: async (args: { id: string; confirmCode: string }): Promise<HardDeleteResult> => {
    const { data, error } = await supabase.rpc(
      "mcp_hard_delete_library_item", { p_id: args.id, p_confirm_code: args.confirmCode },
    );
    if (error) throw error;
    return data as HardDeleteResult;
  },

  // 5818-5822: style/colorway split.
  listStyles: (args: ListStylesInput = {}) =>
    callTool<ListStylesInput, ListStylesOutput>("list_styles", args),

  getStyle: (args: { id: string }) =>
    callTool<typeof args, GetStyleOutput>("get_style", args),

  addStyle: (args: { payload: Partial<Style> }) =>
    callTool<typeof args, { style: Style }>("add_style", args),

  updateStyle: (args: { id: string; patch: Partial<Style> }) =>
    callTool<typeof args, { style: Style }>("update_style", args),

  addColorway: (args: {
    style_id: string;
    payload: Partial<LibraryItem> & { detail_decoration_tag_ids?: string[] };
    inherit_sibling_tags?: boolean;
  }) =>
    callTool<typeof args, { item: LibraryItem }>("add_colorway", args),

  setStyleTags: (args: { style_id: string; tag_ids: string[] }) =>
    callTool<typeof args, { style_id: string; tag_count: number }>(
      "set_style_tags", args,
    ),

  setLibraryItemCustomerCodes: (args: {
    library_item_id: string;
    codes: Array<{ customer_id: string; code: string }>;
  }) =>
    callTool<typeof args, { library_item_id: string; code_count: number }>(
      "set_library_item_customer_codes", args,
    ),

  listLoans: (args: ListLoansInput = {}) =>
    callTool<ListLoansInput, ListLoansOutput>("list_loans", args),

  getLoan: (args: { id: string }) =>
    callTool<typeof args, { loan: Loan | null }>("get_loan", args),

  // Bulk-create loans for N library samples — one borrower per batch.
  // All-or-nothing: if any sample is not in_office, the verb throws an
  // Error whose `detail` property carries the JSON blocker list
  // (array of { library_item_id, current_status }) so the caller can
  // surface which samples blocked the action.
  createLoansBulk: (args: {
    library_item_ids: string[];
    borrower_kind: "customer" | "supplier" | "staff" | "meeting" | "external";
    customer_id?: string;
    supplier_id?: string;
    staff_user_id?: string;
    borrower_label?: string;
    expected_return?: string;
    condition_on_loan?: "new" | "good" | "worn" | "damaged";
    purpose?: string;
    notes?: string;
    meeting_id?: string;
  }) =>
    callTool<typeof args, { loan_ids: string[]; count: number; meeting_id: string | null }>(
      "create_loans_bulk",
      args,
    ),

  // Open one loan against a single physical copy. Argument names match
  // add_loan.ts's inputSchema field-for-field. `unit_id` names the exact
  // samples.library_item_units row to lend (5985/5986 units model); omit it
  // and the backing RPC's trigger auto-assigns the lowest free copy, raising
  // P0001 when none is free. Backs the "Lend out" flow on the samples detail
  // page (#540); the /library/loans/new page still calls
  // mcp_create_loan_atomic directly because it also writes waybill + meeting
  // links in the same transaction.
  addLoan: (args: {
    library_item_id: string;
    unit_id?: string;
    borrower_kind: "customer" | "supplier" | "staff" | "meeting" | "external";
    customer_id?: string;
    supplier_id?: string;
    staff_user_id?: string;
    borrower_label?: string;
    loaned_at?: string;
    expected_return?: string;
    purpose?: string;
    condition_on_loan?: string;
    notes?: string;
    metadata?: Record<string, unknown>;
  }) =>
    callTool<typeof args, { loan: Loan }>("add_loan", args),

  // Field names match return_loan.ts's inputSchema. Still no call sites in
  // the portal — the live "Return" flow uses <ReturnLoanDrawer>, which calls
  // the atomic mcp_return_loan_atomic RPC directly (loan + unit + rollup in
  // one transaction). Kept as the MCP-path equivalent; the names were wrong
  // (`{id, condition, notes}`) until #540 fixed them, which would have failed
  // zod validation at the edge for the first caller.
  returnLoan: (args: {
    loan_id: string;
    condition_on_return?: string;
    notes?: string;
  }) =>
    callTool<typeof args, { loan: Loan }>("return_loan", args),

  listCategories: (args: { include_archived?: boolean } = {}) =>
    callTool<typeof args, { categories: Category[] }>("list_categories", args),

  listStorageLocations: (args: { include_archived?: boolean } = {}) =>
    callTool<typeof args, { locations: StorageLocation[] }>(
      "list_storage_locations", args,
    ),

  upsertStorageLocation: (args: Partial<StorageLocation> & { code: string }) =>
    callTool<typeof args, { location: StorageLocation }>(
      "upsert_storage_location", args,
    ),

  // Admin-only writes for samples.library_storage_locations. The underlying
  // RPCs (mcp_add/edit/delete_storage_location) are service-role-only and the
  // verbs add a per-request requireRole(['admin']) gate, so the portal page
  // doesn't need to make direct PostgREST writes (which would 403 against
  // the table-level grants anyway).
  addStorageLocation: (args: {
    name: string;
    description?: string | null;
    color?: string | null;
    parent_id?: string | null;
    sequence_order?: number;
  }) =>
    callTool<typeof args, { storage_location: Record<string, unknown> }>(
      "add_storage_location",
      // strip nulls — the verb's Zod schema is `.optional()` not `.nullable()`
      Object.fromEntries(
        Object.entries(args).filter(([, v]) => v !== null && v !== undefined),
      ) as typeof args,
    ),

  editStorageLocation: (args: {
    id: string;
    patch: {
      name?: string;
      description?: string | null;
      color?: string | null;
      parent_id?: string | null;
      sequence_order?: number;
    };
  }) =>
    callTool<
      { id: string; patch: Record<string, unknown> },
      { storage_location: Record<string, unknown> }
    >(
      "edit_storage_location",
      {
        id: args.id,
        patch: Object.fromEntries(
          Object.entries(args.patch).filter(
            ([, v]) => v !== null && v !== undefined,
          ),
        ),
      },
    ),

  deleteStorageLocation: (args: { id: string }) =>
    callTool<typeof args, { deleted: boolean }>("delete_storage_location", args),

  getLibraryDashboard: () =>
    callTool<Record<string, never>, LibraryDashboard>(
      "get_library_dashboard", {},
    ),

  importSamples: (args: { rows: Array<Record<string, unknown>>; idempotency_key?: string }) =>
    callTool<typeof args, { inserted: number; updated: number; skipped: number; errors: Array<Record<string, unknown>> }>(
      "import_samples", args,
    ),

  exportSamples: (args: ListLibraryItemsInput & { format?: "csv" | "xlsx" | "pdf" | "pptx" }) =>
    callTool<typeof args, { url: string; expires_at: string; row_count: number }>(
      "export_samples", args,
    ),

  printLabels: (args: { sample_ids: string[]; format?: "qr" | "barcode"; layout?: "a4" | "letter" | "roll" }) =>
    callTool<typeof args, { url: string; expires_at: string; label_count: number }>(
      "print_labels", args,
    ),

  scanSample: (args: { code: string }) =>
    callTool<typeof args, { item: LibraryItem | null; resolved_via: "qr" | "barcode" | "sample_id" | null }>(
      "scan_sample", args,
    ),

  // ── User management verbs (admin-only) ─────────────────────────────────
  listUsers: () =>
    callTool<Record<string, never>, { users: UserRow[]; count: number }>(
      "list_users", {},
    ).then((r) => r.users),

  // View/Edit role-grants — bypass the EF and call the admin-gated RPCs
  // directly via PostgREST. The RPCs (mcp_list_user_role_grants /
  // mcp_set_user_role_permission) check staff.has_role('admin') in their
  // own bodies, so the gate stays server-side. Skipping the EF saves a
  // round-trip and keeps the role drawer responsive while admins flick
  // toggles back and forth.
  listUserRoleGrants: async (userId: string) => {
    const { data, error } = await supabase.rpc("mcp_list_user_role_grants", {
      p_user_id: userId,
    });
    if (error) throw new Error(error.message);
    return (data ?? []) as Array<{ role: string; permission_level: "view" | "edit" }>;
  },

  setUserRolePermission: async (userId: string, role: string, level: "view" | "edit") => {
    const { data, error } = await supabase.rpc("mcp_set_user_role_permission", {
      p_user_id: userId,
      p_role: role,
      p_level: level,
    });
    if (error) throw new Error(error.message);
    return (data ?? {}) as { user_id: string; role: string; level: string };
  },

  getUser: (id: string) =>
    callTool<{ id: string }, { user: UserDetail | null }>(
      "get_user", { id },
    ).then((r) => r.user),

  updateUserRoles: (userId: string, roles: string[]) =>
    callTool<
      { user_id: string; roles: string[] },
      { user_id: string; roles: string[]; added: number; removed: number }
    >("update_user_roles", { user_id: userId, roles }).then((r) => ({
      added: r.added,
      removed: r.removed,
    })),

  disableUser: (id: string) =>
    callTool<{ id: string }, { disabled: boolean }>(
      "disable_user", { id },
    ).then(() => undefined),

  // Customer access (agent role, per-customer RLS scoping) — bypass the EF and
  // call the admin-gated RPCs directly via PostgREST, same pattern as
  // listUserRoleGrants/setUserRolePermission above. The RPCs check
  // staff.has_role('admin') in their own bodies (6021_).
  listUserCustomers: async (userId: string) => {
    const { data, error } = await supabase.rpc("mcp_list_user_customers", {
      p_user_id: userId,
    });
    if (error) throw new Error(error.message);
    return (data ?? []) as Array<{
      customer_id: string;
      customer_name: string;
      assigned_at: string;
      brand_id: string | null;
      brand_name: string | null;
    }>;
  },

  // brandId omitted/null = whole-company grant (unchanged pre-6086 behavior).
  // A set brandId scopes the grant to that one crm.brands row (migration
  // 6086_agent_customer_scoping_brand_granularity.sql, ruling D12).
  assignUserCustomer: async (userId: string, customerId: string, brandId?: string | null) => {
    const { data, error } = await supabase.rpc("mcp_assign_user_customer", {
      p_user_id: userId,
      p_customer_id: customerId,
      p_brand_id: brandId ?? null,
    });
    if (error) throw new Error(error.message);
    return (data ?? {}) as {
      user_id: string;
      customer_id: string;
      brand_id: string | null;
      added: number;
    };
  },

  // brandId must match the row being removed EXACTLY (omitted/null removes
  // only the whole-company row; a set brandId removes only that brand's row).
  unassignUserCustomer: async (userId: string, customerId: string, brandId?: string | null) => {
    const { data, error } = await supabase.rpc("mcp_unassign_user_customer", {
      p_user_id: userId,
      p_customer_id: customerId,
      p_brand_id: brandId ?? null,
    });
    if (error) throw new Error(error.message);
    return (data ?? {}) as {
      user_id: string;
      customer_id: string;
      brand_id: string | null;
      removed: number;
    };
  },

  // ── Tags verbs (5700/5701) ─────────────────────────────────────────────
  // Many-to-many tags shared across samples.library_items and
  // production.stock_offers. Linear-style 12-colour enum drives chip styling.

  listTags: (): Promise<Tag[]> =>
    callTool<Record<string, never>, { tags: Tag[]; count: number }>(
      "list_tags", {},
    ).then((r) => r.tags),

  createTag: (
    payload: {
      name: string;
      category: TagCategory;
      color?: TagColor;
      description?: string;
    },
  ): Promise<Tag> =>
    callTool<typeof payload, { tag: Tag }>("create_tag", payload).then(
      (r) => r.tag,
    ),

  updateTag: (
    id: string,
    patch: Partial<{
      name: string;
      category: TagCategory;
      color: TagColor;
      description: string;
    }>,
  ): Promise<Tag> =>
    callTool<{ id: string } & typeof patch, { tag: Tag }>(
      "update_tag",
      { id, ...patch },
    ).then((r) => r.tag),

  deleteTag: (id: string): Promise<void> =>
    callTool<{ id: string }, {
      tag: Tag;
      soft_deleted: boolean;
      detached_links: number;
    }>("delete_tag", { id }).then(() => undefined),

  setLibraryItemTags: (sampleId: string, tagIds: string[]): Promise<void> =>
    callTool<{ sample_id: string; tag_ids: string[] }, {
      library_item_id: string;
      tag_ids: string[];
      added: number;
      removed: number;
    }>("set_library_item_tags", { sample_id: sampleId, tag_ids: tagIds })
      .then(() => undefined),

  setStockOfferTags: (stockOfferId: string, tagIds: string[]): Promise<void> =>
    callTool<{ stock_offer_id: string; tag_ids: string[] }, {
      stock_offer_id: string;
      tag_ids: string[];
      added: number;
      removed: number;
    }>("set_stock_offer_tags", {
      stock_offer_id: stockOfferId,
      tag_ids: tagIds,
    }).then(() => undefined),

  // ── Critical Path verbs (W7.3 — 5745) ─────────────────────────────────────
  // Universal critical path replacing the Truworths-only flow. Verbs are
  // SECURITY DEFINER + RLS gated; admin-only mutations check
  // staff.is_admin() in SQL. See migration-plans/2026-05-04-universal-critical-
  // path-and-admin-crud.md §5 for the full contract.

  // Returns { catalog: [...] } from the EF; we unwrap to the array.
  listMilestoneCatalog: (args: { include_archived?: boolean } = {}) =>
    callTool<typeof args, { catalog: MilestoneCatalogRow[] }>(
      "list_milestone_catalog", args,
    ).then((r) => r.catalog),

  addMilestoneToCatalog: (args: {
    name: string;
    label: string;
    description?: string | null;
    default_owner_role: string;
    side_effects?: Record<string, unknown>;
    default_color?: string | null;
    default_sequence: number;
  }) =>
    callTool<typeof args, { catalog: MilestoneCatalogRow }>(
      "add_milestone_to_catalog", args,
    ).then((r) => r.catalog),

  editMilestoneInCatalog: (args: { id: string; patch: Record<string, unknown> }) =>
    callTool<typeof args, { catalog: MilestoneCatalogRow }>(
      "edit_milestone_catalog", args,
    ).then((r) => r.catalog),

  archiveMilestoneInCatalog: (id: string) =>
    callTool<{ id: string }, { catalog: MilestoneCatalogRow }>(
      "archive_milestone_in_catalog", { id },
    ).then((r) => r.catalog),

  unarchiveMilestoneInCatalog: (id: string) =>
    callTool<{ id: string }, { catalog: MilestoneCatalogRow }>(
      "unarchive_milestone_in_catalog", { id },
    ).then((r) => r.catalog),

  // Returns { templates: [...] }. The rows are pre-joined with order_type
  // metadata (order_type_id, order_type_slug, order_type_label,
  // order_type_display_order) and milestone_catalog metadata. Pass
  // order_type_slug to filter to a single type.
  listOrderTypeTemplates: (args: { order_type_slug?: string } = {}) =>
    callTool<typeof args, { templates: OrderTypeTemplateRow[] }>(
      "list_order_type_templates", args,
    ).then((r) => r.templates),

  editOrderTypeTemplate: (args: {
    order_type_id: string;
    milestones: Array<{
      milestone_catalog_id: string;
      sequence: number;
      required: boolean;
      owner_role_override?: string | null;
    }>;
  }) =>
    callTool<typeof args, {
      order_type_id: string;
      inserted_count: number;
      template: OrderTypeTemplateRow[];
    }>("edit_order_type_template", args),

  timelineSettings: () => callTool<Record<string, never>, TimelineSettings>("timeline_settings", {}),
  editTimeline: (args: Record<string, unknown>) => callTool<typeof args, { ok: boolean }>("edit_timeline", args),
  getOrderCriticalPath: (args: { order_id: string }) =>
    callTool<typeof args, OrderCriticalPath>(
      "get_order_critical_path", args,
    ),

  // `date` (#1067): the day the approval really happened, for an owner
  // recording history rather than signing off live. Omit it (or send null) for
  // "today". The server refuses a future date, or one before the order's
  // earliest known date (order.earliest_dateable), and audits every accepted
  // value — do not treat the client-side `max`/`min` as the check.
  approveMilestone: (
    args: { milestone_id: string; note?: string | null; date?: string | null },
  ) =>
    callTool<typeof args, { result: Record<string, unknown> }>(
      "approve_milestone", args,
    ).then((r) => r.result),

  unapproveMilestone: (
    args: { milestone_id: string; note: string; date?: string | null },
  ) =>
    callTool<typeof args, { result: Record<string, unknown> }>(
      "unapprove_milestone", args,
    ).then((r) => r.result),

  resetMilestone: (args: { milestone_id: string; note: string }) =>
    callTool<typeof args, { result: Record<string, unknown> }>(
      "reset_milestone", args,
    ).then((r) => r.result),

  changeOrderType: (args: { order_id: string; new_order_type_id: string; note: string }) =>
    callTool<typeof args, {
      order_id: string;
      order_type_id: string;
      inserted_count: number;
      skipped_count: number;
      no_change: boolean;
    }>("change_order_type", args),

  // ── Order create / edit / convert (#465) ────────────────────────────────
  // create_order / edit_order are admin/merch-gated at the verb layer (see
  // supabase/functions/cc-os-mcp/verbs/{create,edit}_order.ts). UI should
  // still gate the buttons on roles.includes('admin'|'merch') so non-gated
  // users don't hit a 403 after filling in a form.

  createOrder: (args: CreateOrderInput) =>
    callTool<CreateOrderInput, CreateOrderOutput>("create_order", args),

  // [Epic #1060] Phase 2a (#1101, migration 6181) — THE order-creation path
  // for /orders/new and the brief convert dialog: one customer PO is one
  // order with many lines, each sourced from its own costed brief. Lines with
  // no brief ride along and convert nothing, which is what keeps the plain
  // manual order working. Send a key as null for "none, do not infer"; OMIT
  // it to inherit the value the selected briefs agree on.
  composeOrderFromBriefs: (args: ComposeOrderInput) =>
    callTool<ComposeOrderInput, ComposeOrderOutput>(
      "compose_order_from_briefs", args,
    ),

  // The composer's read side. Returns what each brief WOULD contribute,
  // computed by the same server-side derivation the write uses — the portal
  // never works out a line's price itself (it comes from the brief's accepted
  // quote and costing, which the portal cannot read).
  previewOrderLinesFromBriefs: (args: { brief_ids: string[] }) =>
    callTool<typeof args, ComposerPreview>(
      "preview_order_lines_from_briefs", args,
    ),

  // [Epic #1060] Phase 2d (#1104, migration 6200) — the AW27 Planning sheet
  // generated FROM cc-os, the inverse of the Planning ingest. READ-ONLY: it
  // writes nothing back and does not record that an export happened. The
  // portal formats the result (lib/planningSheetExport.ts) and computes no
  // cell of it — the money columns come from production.order_planning_rows,
  // the same figures the order-confirmation margin gate enforces.
  exportPlanningSheet: (args: PlanningSheetExportInput = {}) =>
    callTool<PlanningSheetExportInput, PlanningSheetExport>(
      "export_planning_sheet", args,
    ),

  // [Epic #1060] Phase 2b (#1102, migration 6193) — customer PO versions.
  // Record the order AS IT STANDS: no header or line content is sent, because
  // the server snapshots the live rows. Amend the order first (editOrder /
  // editOrderLine), then record the version — that ordering is what makes the
  // current version and the order's lines agree by construction.
  createCustomerPoVersion: (
    args: {
      order_id: string;
      change_notes?: string | null;
      /** The customer's OWN version label. Omit to leave the order's as-is. */
      customer_po_version?: string | null;
    },
  ) =>
    callTool<typeof args, CustomerPoVersionCreated>(
      "create_customer_po_version", args,
    ),

  // [Epic #1060] Phase 3a (#1204, migration 6204) — the same act as
  // createCustomerPoVersion above, taken on the authority of a fully confirmed
  // customer-PO extraction, and stamped with the document it was taken on. It
  // sends no header or line content either: the server still snapshots the
  // LIVE order, so amend the order first and then record the version.
  createCustomerPoVersionFromExtraction: (
    args: {
      extraction_id: string;
      change_notes?: string | null;
      customer_po_version?: string | null;
    },
  ) =>
    callTool<typeof args, CustomerPoVersionFromExtraction>(
      "create_customer_po_version_from_extraction", args,
    ),

  // #1276 (migration 6231) — the confirmation-report sibling of the bridge
  // above. A Pepkor "Purchase Order Confirmation" is the buyer's
  // ACKNOWLEDGEMENT of a PO, not a PO, so a confirmed draft of one does not
  // produce a version: it writes the two dates it exists to state
  // (`book_by_date`, `latest_date_in_dc`) onto every live line of the order,
  // through edit_order_line. Capability: orders.manage.
  applyPepkorConfirmationDatesToOrder: (args: { extraction_id: string }) =>
    callTool<typeof args, PepkorConfirmationDatesApplied>(
      "apply_pepkor_confirmation_dates_to_order", args,
    ),

  // The history panel's read: summary rows only, newest first.
  listCustomerPoVersions: (args: { order_id: string }) =>
    callTool<typeof args, CustomerPoVersionList>(
      "list_customer_po_versions", args,
    ),

  // Everything that differs between two versions. `version_a` is the `old`
  // side and `version_b` the `new` one, in the order given — pass the older
  // number first for a forward-reading diff.
  diffCustomerPoVersions: (args: { order_id: string; version_a: number; version_b: number }) =>
    callTool<typeof args, CustomerPoVersionDiff>(
      "diff_customer_po_versions", args,
    ),

  editOrder: (args: { id: string; patch: EditOrderPatch; note?: string }) =>
    callTool<typeof args, { order: OrderRow }>("edit_order", args),

  // [Epic #1060] Phase 1a (#1061, migration 6160) — line-level counterpart of
  // editOrder, scoped to customer_po_no/drop_label/drop_date/fit_variant/
  // dc_code/delivery_date. Sizes/packing stay on setOrderLineSizes /
  // applySizeCurveToOrderLine / applyPrepackToOrderLine.
  editOrderLine: (args: { id: string; patch: EditOrderLinePatch; note?: string }) =>
    callTool<typeof args, { order_line: OrderLine }>("edit_order_line", args),

  // "One action" brief -> order conversion. Caller supplies season_id +
  // qty_pcs (a brief carries neither) — everything else (style/price/
  // customer) is derived server-side from the brief. Since #916,
  // supplier_id/target_ex_factory distinguish "omit the key" (infer from the
  // brief's source snapshot / sheet) from "send null" (no supplier/date at
  // all, no inference) — always send both keys from the dialog (see
  // ConvertBriefToOrderDialog.tsx).
  convertBriefToOrder: (args: {
    brief_id: string;
    season_id: string;
    qty_pcs: number;
    order_type_id?: string;
    supplier_id?: string | null;
    po_number?: string;
    currency?: string;
    target_ex_factory?: string | null;
    target_in_store?: string;
    colour?: string;
    unit_price_usd?: number;
    unit_price_zar?: number;
    notes?: string;
    note?: string;
  }) =>
    callTool<typeof args, CreateOrderOutput>("convert_brief_to_order", args),

  // "One action" quote -> order conversion. A quote already carries
  // supplier/customer/season/style/qty/price, so this needs almost no input.
  convertQuoteToOrder: (args: {
    quote_id: string;
    season_id?: string;
    order_type_id?: string;
    po_number?: string;
    currency?: string;
    target_ex_factory?: string;
    target_in_store?: string;
    qty_pcs?: number;
    notes?: string;
    note?: string;
  }) =>
    callTool<typeof args, CreateOrderOutput>("convert_quote_to_order", args),

  // ── Order-line sizes (migration 6057, issue #674) ─────────────────────────
  // orders.manage-gated at the verb layer (see supabase/functions/cc-os-mcp/
  // verbs/{get,set,apply_size_curve_to,apply_prepack_to,check}_order*.ts).
  // UI should still gate the write controls on roles.includes('admin'|'merch')
  // — same posture as editOrder above — so a non-gated user doesn't hit a 403
  // after filling in a form.

  getOrderLineSizes: (args: { order_line_id: string }) =>
    callTool<typeof args, GetOrderLineSizesOutput>("get_order_line_sizes", args),

  setOrderLineSizes: (args: {
    order_line_id: string;
    sizes: SetOrderLineSizeInput[];
    note?: string;
  }) =>
    callTool<typeof args, { reconciliation: SizeReconciliation }>(
      "set_order_line_sizes", args,
    ),

  applySizeCurveToOrderLine: (args: {
    order_line_id: string;
    size_curve_id: string;
    total_pieces?: number;
  }) =>
    callTool<typeof args, { reconciliation: SizeReconciliation }>(
      "apply_size_curve_to_order_line", args,
    ),

  applyPrepackToOrderLine: (args: {
    order_line_id: string;
    prepack_id: string;
    pack_count: number;
  }) =>
    callTool<typeof args, {
      reconciliation: SizeReconciliation;
      prepack: Record<string, unknown>;
      pack_count: number;
      pieces_per_pack: number;
      implied_pieces: number;
    }>("apply_prepack_to_order_line", args),

  checkOrderSizes: (args: { order_id: string }) =>
    callTool<typeof args, CheckOrderSizesOutput>("check_order_sizes", args),

  // Read-only pickers for the curve/pre-pack UI. orders.manage-gated (not
  // admin.manage) — see list_size_sets_for_order_line.ts's coordination note
  // on why this issue wraps its own copy of the underlying reads rather than
  // depending on sibling #673's admin-gated equivalents.
  listSizeSetsForOrderLine: (args: { season_code?: string; include_inactive?: boolean }) =>
    callTool<typeof args, { size_sets: SizeSetSummary[] }>(
      "list_size_sets_for_order_line", args,
    ),

  getSizeSetForOrderLine: (args: { size_set_id: string }) =>
    callTool<typeof args, GetSizeSetOutput>("get_size_set_for_order_line", args),

  // ── Planning sheet + margin sign-off gate (6031 / issue #466) ─────────────
  // generatePlanningSheet appends a NEW sheet version and invalidates any
  // override signed against the previous one — call it deliberately, not on
  // every render. getPlanningSheet is the read path and also returns the live
  // gate evaluation, so a page never has to re-derive "can this confirm?".

  generatePlanningSheet: (args: { order_id: string }) =>
    callTool<typeof args, { sheet: PlanningSheet }>(
      "generate_planning_sheet", args,
    ).then((r) => r.sheet),

  getPlanningSheet: (args: { order_id: string; version?: number | null }) =>
    callTool<typeof args, GetPlanningSheetOutput>("get_planning_sheet", args),

  getOrderMargin: (args: { order_id: string }) =>
    callTool<typeof args, { margin: OrderMargin | null; gate: ConfirmationGate | null }>(
      "get_order_margin", args,
    ),

  recordOrderSignoff: (args: {
    order_id: string;
    sheet_id?: string | null;
    override_reason?: string | null;
    note?: string | null;
  }) =>
    callTool<typeof args, { signoff: Record<string, unknown> }>(
      "record_order_signoff", args,
    ).then((r) => r.signoff),

  listOrderSignoffs: (args: { order_id: string }) =>
    callTool<typeof args, { signoffs: OrderSignoff[] }>(
      "list_order_signoffs", args,
    ).then((r) => r.signoffs),

  // ── Settings verbs (5735) ─────────────────────────────────────────────────
  // Admin-gated key/value access to ops.config_settings. Both verbs flow
  // through the SQL functions mcp_get_setting / mcp_set_setting; the SQL
  // layer enforces staff.has_role('admin') and redacts is_secret rows.
  // UI-side gating: wrap calling pages with useIsAdmin.

  getSetting: (key: string): Promise<unknown | null> =>
    callTool<{ key: string }, { value: unknown | null }>(
      "get_setting",
      { key },
    ).then((r) => r.value),

  setSetting: (
    key: string,
    value: unknown,
    description?: string,
  ): Promise<{
    key: string;
    value: unknown;
    description: string | null;
    updated_at: string;
  }> =>
    callTool<
      { key: string; value: unknown; description?: string },
      {
        key: string;
        value: unknown;
        description: string | null;
        updated_at: string;
      }
    >("set_setting", { key, value, description }),

  // ── MFA requirement (#1107 / migration 6218, Epic #1054 phase 3) ─────────
  // ops.config_settings' `security.mfa_required.<area>` keys are NOT reachable
  // through setSetting above — public.mcp_set_setting refuses every security.*
  // key, so this pair is the only way in.
  //
  // The READ goes through the audited cc-os-mcp verb like every other Settings
  // read: public.mcp_get_mfa_requirement_status has no `authenticated` grant.
  getMfaRequirementStatus: (area = "costings"): Promise<MfaRequirementStatus> =>
    callTool<{ area: string }, MfaRequirementStatus>(
      "get_mfa_requirement_status",
      { area },
    ),

  // The WRITE goes DIRECTLY over PostgREST, NOT through the Edge Function —
  // the one deliberate exception on this surface. public.mcp_set_mfa_requirement
  // refuses to turn a requirement OFF unless the CALLER's own session is
  // MFA-verified, which it checks by reading `auth.jwt() ->> 'aal'`. The
  // service-role JWT the EF holds carries no `aal` claim at all, so routing
  // this through the EF would make the switch impossible to turn off — the
  // whole point being that removing an MFA floor needs a second factor held by
  // a human at that moment. The RPC is granted to `authenticated` for exactly
  // this reason and still enforces admin.manage in its own body (migration
  // 6218's header), so the grant is not the gate. Changes are audited by the
  // SQL function itself (one ops.audit_events row + an admin notification), so
  // nothing is lost by skipping withAudit().
  setMfaRequirement: async (args: {
    area?: string;
    required: boolean;
  }): Promise<MfaRequirementWriteResult> => {
    const { data, error } = await supabase.rpc("mcp_set_mfa_requirement", {
      p_area: args.area ?? "costings",
      p_required: args.required,
    });
    if (error) throw new Error(error.message);
    return data as MfaRequirementWriteResult;
  },

  // ── Order-cutover readiness (#1310 / migration 6234, Epic #1227 phase 3a) ─
  //
  // All three go through the audited cc-os-mcp verbs, NOT direct PostgREST:
  // the three SQL functions have no `authenticated` grant at all, exactly like
  // public.mcp_get_mfa_requirement_status above (#1107), whose status read this
  // one embeds. None of them needs to see the caller's own `aal` claim — the
  // one thing that forced setMfaRequirement onto the direct path — so there is
  // no reason to open a second route to them. admin.manage is checked inside
  // each function's BODY via public.audit_resolve_actor(), so the verb layer is
  // not the gate; tests/pgtap/157_order_cutover_readiness.sql §B proves that
  // independently of these calls.

  /**
   * The whole Settings → Order cutover panel in one read: `ready` (true only
   * once every BLOCKING gate item passes) plus the four items with their live
   * detail. Reads only — nothing is written and no cutover is performed.
   */
  getOrderCutoverReadiness: (): Promise<OrderCutoverReadiness> =>
    callTool<Record<string, never>, OrderCutoverReadiness>(
      "get_order_cutover_readiness",
      {},
    ),

  /**
   * Exclude one staff account from the cutover's sign-in gate, with a required
   * non-blank reason. Idempotent: an already-excluded account comes back
   * `was_noop: true` with its ORIGINAL reason and actor intact — changing a
   * reason is an un-exclude followed by a re-exclude, which keeps the history
   * honest. Every real exclusion writes one ops.audit_events row.
   */
  excludeStaffSignin: (args: {
    user_id: string;
    reason: string;
  }): Promise<OrderCutoverExclusionResult> =>
    callTool<typeof args, OrderCutoverExclusionResult>(
      "exclude_staff_signin",
      args,
    ),

  /**
   * Undo an ACTIVE exclusion. The row is revoked, never deleted, so "this
   * account was excluded, then restored" stays readable. Raises for an id that
   * does not exist or is already revoked.
   */
  unexcludeStaffSignin: (args: {
    exclusion_id: string;
    reason?: string;
  }): Promise<OrderCutoverUnexclusionResult> =>
    callTool<typeof args, OrderCutoverUnexclusionResult>(
      "unexclude_staff_signin",
      args,
    ),

  // ── Order-cutover ACTION (#1325 / migration 6238) ────────────────────────
  //
  // All THREE go DIRECTLY over PostgREST, not through cc-os-mcp — the
  // setMfaRequirement exception above, for its own reason and then for a
  // second one:
  //
  //   1. `runOrderCutover` MUST be direct. It refuses unless the CALLER's own
  //      session is MFA-verified, which it checks by reading
  //      `auth.jwt() ->> 'aal'`. The service-role JWT the Edge Function holds
  //      carries no `aal` claim at all, so routing it through a verb would
  //      make the action impossible to perform — the whole point being that
  //      the crossover needs a second factor held by a human at that moment.
  //      There is deliberately no `run_order_cutover` verb for that reason.
  //   2. The other two are direct to keep the panel WORKING ON ITS OWN PR.
  //      cc-os-dev's Edge Functions only move on merge (#1120), so a panel
  //      that read its state through a brand-new verb would error on every
  //      preview build of the PR that introduced it — the ImportHealthPanel
  //      precedent (6213). `get_order_cutover_state` / `advance_order_cutover`
  //      DO exist as verbs, for MCP agents; the portal just does not need them.
  //
  // All three RPCs are granted to `authenticated` and enforce admin.manage in
  // their own bodies via public.audit_resolve_actor(), so the grant is a route
  // and not the gate (tests/pgtap/159_order_cutover_action.sql §B proves it
  // independently of these calls). They audit themselves — one
  // ops.audit_events row for the decision and one for the completion, plus an
  // admin notification — so nothing is lost by skipping withAudit().

  /**
   * Everything the panel renders about the cutover ACTION: the single recorded
   * run (or null), the live `orders.cutover_at` and `ingest.planning.mode`, and
   * whether the daily Planning schedule is still active. Reads only.
   */
  getOrderCutoverState: async (): Promise<OrderCutoverState> => {
    const { data, error } = await supabase.rpc("mcp_get_order_cutover_state");
    if (error) throw new Error(error.message);
    return data as OrderCutoverState;
  },

  /**
   * THE cutover. Refuses a non-admin, an AAL1 session, a `confirm` that is not
   * the literal string `CUTOVER`, and any failing BLOCKING readiness item —
   * naming the items that failed. On success it records the decision and asks
   * for the final Planning import; the rest follows from the imports landing.
   * A second call is a no-op reporting the existing `cutover_at`.
   */
  runOrderCutover: async (args: {
    confirm: string;
  }): Promise<OrderCutoverRunResult> => {
    const { data, error } = await supabase.rpc("mcp_run_order_cutover", {
      p_confirm: args.confirm,
    });
    if (error) throw new Error(error.message);
    return data as OrderCutoverRunResult;
  },

  /**
   * Advance the live cutover by one step. Normally unnecessary — each step is
   * advanced by the very import it waits for — so this is the retry for an
   * import whose POST could not be queued, or one that landed unobserved.
   * Safe to call repeatedly; a no-op when there is nothing to advance.
   */
  advanceOrderCutover: async (): Promise<OrderCutoverAdvanceResult> => {
    const { data, error } = await supabase.rpc("mcp_advance_order_cutover");
    if (error) throw new Error(error.message);
    return data as OrderCutoverAdvanceResult;
  },

  // ── T-Minus retirement (#1311 / migration 6239) ──────────────────────────
  //
  // Both go DIRECTLY over PostgREST, for the two reasons the cutover action
  // above gives, in the same order:
  //
  //   1. `retireTminus` MUST be direct. It refuses unless the CALLER's own
  //      session is MFA-verified, read from `auth.jwt() ->> 'aal'`, and the
  //      service-role JWT an Edge Function holds carries no `aal` claim at
  //      all — so there is deliberately no `retire_tminus` verb.
  //   2. `getTminusRetirementState` is direct to keep the panel working on
  //      its own PR preview (cc-os-dev's Edge Functions only move on merge,
  //      #1120 — the ImportHealthPanel precedent).
  //
  // Both RPCs enforce their capability in their own bodies via
  // public.audit_resolve_actor(), so the `authenticated` grant is a route and
  // not the gate (tests/pgtap/160 §A/§D/§E prove it independently of these
  // calls), and the action audits itself plus notifies every admin — so
  // nothing is lost by skipping withAudit().

  /**
   * Everything the T-Minus card renders: the three-state status line, the
   * cutover/retirement dates, whether the daily schedule is still active, and
   * the OPEN pre-cutover orders still to be finished. Readable by an admin and
   * by any staff user holding `orders.manage`. Reads only.
   */
  getTminusRetirementState: async (): Promise<TminusRetirementState> => {
    const { data, error } = await supabase.rpc(
      "mcp_get_tminus_retirement_state",
    );
    if (error) throw new Error(error.message);
    return data as TminusRetirementState;
  },

  /**
   * Retire T-Minus. Refuses a non-admin, an AAL1 session, a `confirm` that is
   * not the literal string `RETIRE`, a state where no cutover has run, and any
   * pre-cutover order still open — naming them. On success it pauses the daily
   * schedule (never removes it), marks the import retired, audits the act and
   * notifies every admin. Nothing is deleted, and a second call is a no-op.
   */
  retireTminus: async (args: {
    confirm: string;
  }): Promise<TminusRetireResult> => {
    const { data, error } = await supabase.rpc("mcp_retire_tminus", {
      p_confirm: args.confirm,
    });
    if (error) throw new Error(error.message);
    return data as TminusRetireResult;
  },

  // ── Notification settings (6038 / issue #577) ────────────────────────────
  // Admin-gated read/write of samples.notification_settings — the "who gets
  // which email, and how often" config, previously SQL-only. Both verbs flow
  // through public.mcp_list_notification_settings /
  // mcp_update_notification_setting; writes land an ops.audit_events row via
  // the shared withAudit() EF wrapper. UI-side gating: wrap calling pages
  // with useIsAdmin.

  listNotificationSettings: (): Promise<{
    settings: NotificationSettingRow[];
    count: number;
  }> =>
    callTool<Record<string, never>, { settings: NotificationSettingRow[]; count: number }>(
      "list_notification_settings",
      {},
    ),

  updateNotificationSetting: (args: {
    notification_type: string;
    enabled?: boolean;
    frequency?: string;
    recipient_roles?: string[];
    additional_emails?: string[];
  }): Promise<NotificationSettingRow> =>
    callTool<typeof args, NotificationSettingRow>(
      "update_notification_setting",
      args,
    ),

  // ── Capability admin grid (6042 / issue #606, Phase 4 of #597) ──────────
  // Admin-gated read/write of the role x capability matrix, previously
  // editable only via direct SQL against staff.role_capabilities. Both verbs
  // flow through public.mcp_list_capability_grid / mcp_set_role_capability;
  // writes land an ops.audit_events row via the SQL function itself (actor,
  // role, capability, direction) in addition to the shared withAudit() EF
  // wrapper's mcp_call row. UI-side gating: wrap calling pages with
  // useIsAdmin. mcp_set_role_capability refuses any mutation targeting
  // role="admin" (structurally protected — see staff.has_capability()'s
  // hardcoded admin exemption, migration 6041); the portal should also
  // disable those checkboxes client-side (locked=true on the grid row)
  // rather than let the click round-trip into a rejected call.

  listCapabilityGrid: (): Promise<{
    grid: CapabilityGridRow[];
    count: number;
  }> =>
    callTool<Record<string, never>, { grid: CapabilityGridRow[]; count: number }>(
      "list_capability_grid",
      {},
    ),

  // ── Role registry (#612) ────────────────────────────────────────────────
  listRoles: (args: { include_retired?: boolean } = {}): Promise<{
    roles: RoleRow[];
    count: number;
  }> =>
    callTool<typeof args, { roles: RoleRow[]; count: number }>("list_roles", args),

  createRole: (args: {
    name: string;
    label?: string;
    description?: string;
    color?: string;
    surfaces?: string[];
  }): Promise<{ id: string; name: string; label: string }> =>
    callTool<typeof args, { id: string; name: string; label: string }>(
      "create_role",
      args,
    ),

  updateRole: (args: {
    name: string;
    new_name?: string;
    label?: string;
    description?: string;
    color?: string;
    surfaces?: string[];
    sort_order?: number;
  }): Promise<{ id: string; name: string; label: string; renamed: boolean }> =>
    callTool<
      typeof args,
      { id: string; name: string; label: string; renamed: boolean }
    >("update_role", args),

  setRoleRetired: (args: {
    name: string;
    retired: boolean;
  }): Promise<{ id: string; name: string; retired_at: string | null }> =>
    callTool<typeof args, { id: string; name: string; retired_at: string | null }>(
      "set_role_retired",
      args,
    ),

  setRoleCapability: (args: {
    role: string;
    capability_name: string;
    grant: boolean;
  }): Promise<{
    role: string;
    capability_name: string;
    capability_id: string;
    granted: boolean;
    direction: "grant" | "revoke";
    changed: boolean;
  }> =>
    callTool<
      typeof args,
      {
        role: string;
        capability_name: string;
        capability_id: string;
        granted: boolean;
        direction: "grant" | "revoke";
        changed: boolean;
      }
    >("set_role_capability", args),

  // ── Stage owners (#941, Epic #914 §5.14) ────────────────────────────────
  // Schema/RPC only for now — no settings UI exists yet (that is a separate,
  // later issue). Bypass the EF and call the admin-gated RPCs directly via
  // PostgREST, same pattern as listUserRoleGrants/listUserCustomers above:
  // the RPCs check public.mcp_check_admin_or_raise() (admin.manage) in their
  // own bodies, so the gate stays server-side regardless of which transport
  // reaches them.
  listStageOwners: async (stage?: string, customerId?: string) => {
    const { data, error } = await supabase.rpc("mcp_list_stage_owners", {
      p_stage: stage ?? null,
      p_customer_id: customerId ?? null,
    });
    if (error) throw new Error(error.message);
    return (data ?? []) as Array<{
      id: string;
      stage: string;
      customer_id: string | null;
      customer_name: string | null;
      owner_user_id: string | null;
      owner_user_name: string | null;
      owner_role: string | null;
      created_at: string;
      updated_at: string;
    }>;
  },

  // Exactly one of ownerUserId / ownerRole must be set — adds a co-owner if
  // the (stage, customer tier) already has one; use removeStageOwner + this
  // to change an existing assignment (no update verb, see migration 6128).
  setStageOwner: async (args: {
    stage: string;
    ownerUserId?: string | null;
    ownerRole?: string | null;
    customerId?: string | null;
  }) => {
    const { data, error } = await supabase.rpc("mcp_set_stage_owner", {
      p_stage: args.stage,
      p_owner_user_id: args.ownerUserId ?? null,
      p_owner_role: args.ownerRole ?? null,
      p_customer_id: args.customerId ?? null,
    });
    if (error) throw new Error(error.message);
    return data as {
      id: string;
      stage: string;
      customer_id: string | null;
      owner_user_id: string | null;
      owner_role: string | null;
    };
  },

  removeStageOwner: async (id: string) => {
    const { data, error } = await supabase.rpc("mcp_remove_stage_owner", {
      p_id: id,
    });
    if (error) throw new Error(error.message);
    return data as {
      id: string;
      stage: string;
      customer_id: string | null;
      owner_user_id: string | null;
      owner_role: string | null;
    };
  },

  // ── Stage ageing thresholds (#1010, Epic #956 §3c) ──────────────────────
  // ops.stage_age_thresholds is a per-stage override (PK on stage, no seed
  // rows) of the global ops.config_settings['flow.age_defaults'] default
  // (both shipped read-only by 6135_status_age.sql, #959). Same transport
  // choice as the stage-owner wrappers above — bypass the EF and call the
  // admin-gated RPCs directly via PostgREST; they check
  // public.mcp_check_admin_or_raise() (admin.manage) in their own bodies.
  // The global default is read via the existing generic mcp.getSetting()
  // (5735_'s mcp_get_setting, routed through the cc-os-mcp EF) since
  // authenticated has no direct grant on ops.config_settings' RLS-admin-only
  // row; writing it goes through the new dedicated mcp_set_age_defaults RPC
  // (validates warn_days < critical_days, which the generic mcp_set_setting
  // does not).
  getAgeDefaults: async (): Promise<{ warn_days: number; critical_days: number }> => {
    const value = (await mcp.getSetting("flow.age_defaults")) as
      | { warn_days: number; critical_days: number }
      | null;
    return value ?? { warn_days: 7, critical_days: 14 };
  },

  setAgeDefaults: async (warnDays: number, criticalDays: number) => {
    const { data, error } = await supabase.rpc("mcp_set_age_defaults", {
      p_warn_days: warnDays,
      p_critical_days: criticalDays,
    });
    if (error) throw new Error(error.message);
    return data as {
      key: string;
      value: { warn_days: number; critical_days: number };
      updated_at: string;
    };
  },

  listStageAgeThresholds: async () => {
    const { data, error } = await supabase.rpc("mcp_list_stage_age_thresholds");
    if (error) throw new Error(error.message);
    return (data ?? []) as Array<{
      stage: string;
      warn_days: number;
      critical_days: number;
      created_at: string;
      updated_at: string;
    }>;
  },

  setStageAgeThreshold: async (stage: string, warnDays: number, criticalDays: number) => {
    const { data, error } = await supabase.rpc("mcp_set_stage_age_threshold", {
      p_stage: stage,
      p_warn_days: warnDays,
      p_critical_days: criticalDays,
    });
    if (error) throw new Error(error.message);
    return data as { stage: string; warn_days: number; critical_days: number };
  },

  removeStageAgeThreshold: async (stage: string) => {
    const { data, error } = await supabase.rpc("mcp_remove_stage_age_threshold", {
      p_stage: stage,
    });
    if (error) throw new Error(error.message);
    return data as { stage: string; warn_days: number; critical_days: number };
  },

  // ── Default action owner (#977, Epic #914 §3b) ──────────────────────────
  // ops.actions_default_owner_config is a true singleton (0 or 1 row) — the
  // optional override used by ops.resolve_default_action_owner() when a new
  // ops.actions row has neither owner_user_id nor owner_label
  // (6036_actions_default_owner.sql). `authenticated` has SELECT only on the
  // table, so reads go directly through PostgREST (table + the resolver
  // function, both already execute-granted); the write goes through the
  // admin.manage-gated mcp_set_default_action_owner RPC, the sole write path.
  getDefaultActionOwnerConfig: async () => {
    const { data, error } = await supabase
      .schema("ops")
      .from("actions_default_owner_config")
      .select("id, default_owner_user_id, updated_at, updated_by")
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data as {
      id: string;
      default_owner_user_id: string | null;
      updated_at: string;
      updated_by: string | null;
    } | null;
  },

  // The least-loaded-active-admin (or, failing that, non-system staff)
  // fallback ops.resolve_default_action_owner() computes live — shown by the
  // UI whenever no override is configured, rather than a blank/error state.
  resolveDefaultActionOwner: async () => {
    const { data, error } = await supabase.schema("ops").rpc("resolve_default_action_owner");
    if (error) throw new Error(error.message);
    return (data ?? null) as string | null;
  },

  // p_owner_user_id null clears the override (reverts to the fallback above).
  setDefaultActionOwner: async (ownerUserId: string | null) => {
    const { data, error } = await supabase.rpc("mcp_set_default_action_owner", {
      p_owner_user_id: ownerUserId,
    });
    if (error) throw new Error(error.message);
    return data as {
      id: string;
      default_owner_user_id: string | null;
      updated_at: string;
      updated_by: string | null;
    };
  },

  // ── Completeness rules (#944, Epic #914 §5.15) ──────────────────────────
  // Same transport choice as the stage-owner wrappers above — bypass the EF
  // and call the admin-gated RPCs directly via PostgREST; they check
  // public.mcp_check_admin_or_raise() (admin.manage) in their own bodies.
  // Backs the "Flow & owners" Settings tab (#976).
  listCompletenessRules: async (args?: {
    entity?: string;
    stageTo?: string;
    templateId?: string | null;
  }) => {
    const { data, error } = await supabase.rpc("mcp_list_completeness_rules", {
      p_entity: args?.entity ?? null,
      p_stage_to: args?.stageTo ?? null,
      p_template_id: args?.templateId ?? null,
    });
    if (error) throw new Error(error.message);
    return (data ?? []) as Array<{
      id: string;
      entity: string;
      stage_from: string;
      stage_to: string;
      template_id: string | null;
      template_code: string | null;
      rule_key: string;
      label: string;
      level: "required" | "recommended" | "off";
      // Mirrors ops.completeness_rules' check_kind CHECK, widened by
      // migration 6184 (#1114, sample_approvals) and 6192 (#1175,
      // qa_booking). Hand-written, so unlike db-enums.generated.ts it does
      // NOT self-heal — widen BOTH when a migration adds a kind.
      check_kind: OpsCompletenessRulesCheckKind;
      check_config: Record<string, unknown>;
      owner_role: string | null;
      owner_stage: string | null;
      sort_order: number;
      created_at: string;
      updated_at: string;
    }>;
  },

  // Upserts on (entity, stage_from, stage_to, template tier, rule_key) — a
  // second call with the same key edits level/label/config/owner in place.
  setCompletenessRule: async (args: {
    entity: string;
    stageFrom: string;
    stageTo: string;
    ruleKey: string;
    label: string;
    // Same source as listCompletenessRules' row type above — never a second
    // hand-written copy of the CHECK's member list.
    checkKind: OpsCompletenessRulesCheckKind;
    checkConfig: Record<string, unknown>;
    level?: "required" | "recommended" | "off";
    templateId?: string | null;
    ownerRole?: string | null;
    ownerStage?: string | null;
    sortOrder?: number;
  }) => {
    const { data, error } = await supabase.rpc("mcp_set_completeness_rule", {
      p_entity: args.entity,
      p_stage_from: args.stageFrom,
      p_stage_to: args.stageTo,
      p_rule_key: args.ruleKey,
      p_label: args.label,
      p_check_kind: args.checkKind,
      p_check_config: args.checkConfig,
      p_level: args.level ?? "required",
      p_template_id: args.templateId ?? null,
      p_owner_role: args.ownerRole ?? null,
      p_owner_stage: args.ownerStage ?? null,
      p_sort_order: args.sortOrder ?? 100,
    });
    if (error) throw new Error(error.message);
    return data as Record<string, unknown>;
  },

  removeCompletenessRule: async (id: string) => {
    const { data, error } = await supabase.rpc("mcp_remove_completeness_rule", {
      p_id: id,
    });
    if (error) throw new Error(error.message);
    return data as Record<string, unknown>;
  },

  // ── Completeness templates + customer/brand map (#976, Epic #914 Phase 3a,
  // migration 6147) ─────────────────────────────────────────────────────────
  // Closes the gap #944 left open: ops.completeness_templates /
  // ops.customer_completeness_templates had SELECT-only grants and no write
  // RPC before this. Same direct-PostgREST transport as the stage-owner /
  // completeness-rule wrappers above.
  listCompletenessTemplates: async (includeRetired = false) => {
    const { data, error } = await supabase.rpc("mcp_list_completeness_templates", {
      p_include_retired: includeRetired,
    });
    if (error) throw new Error(error.message);
    return (data ?? []) as Array<{
      id: string;
      code: string;
      label: string;
      description: string | null;
      retired_at: string | null;
      mapped_count: number;
      rule_count: number;
      created_at: string;
      updated_at: string;
    }>;
  },

  // Creates (id omitted) or updates (id set) one named template.
  setCompletenessTemplate: async (args: {
    code: string;
    label: string;
    description?: string | null;
    id?: string | null;
  }) => {
    const { data, error } = await supabase.rpc("mcp_set_completeness_template", {
      p_code: args.code,
      p_label: args.label,
      p_description: args.description ?? null,
      p_id: args.id ?? null,
    });
    if (error) throw new Error(error.message);
    return data as Record<string, unknown>;
  },

  // Retiring is BLOCKED (P0001) while any customer/brand mapping still points
  // at the template — unmap first.
  setCompletenessTemplateRetired: async (id: string, retired: boolean) => {
    const { data, error } = await supabase.rpc("mcp_set_completeness_template_retired", {
      p_id: id,
      p_retired: retired,
    });
    if (error) throw new Error(error.message);
    return data as Record<string, unknown>;
  },

  listCustomerCompletenessTemplates: async (customerId?: string | null) => {
    const { data, error } = await supabase.rpc("mcp_list_customer_completeness_templates", {
      p_customer_id: customerId ?? null,
    });
    if (error) throw new Error(error.message);
    return (data ?? []) as Array<{
      id: string;
      customer_id: string;
      customer_name: string | null;
      brand_id: string | null;
      brand_name: string | null;
      template_id: string;
      template_code: string | null;
      template_label: string | null;
      created_at: string;
      updated_at: string;
    }>;
  },

  // Maps (or re-maps) a customer, or a customer+brand, to a template.
  // brandId omitted/null = every brand of that customer; a brand-specific
  // row wins over the customer-level one. Upserts on (customer_id, brand_id)
  // — re-running with a different templateId edits the mapping in place.
  setCustomerCompletenessTemplate: async (args: {
    customerId: string;
    templateId: string;
    brandId?: string | null;
  }) => {
    const { data, error } = await supabase.rpc("mcp_set_customer_completeness_template", {
      p_customer_id: args.customerId,
      p_template_id: args.templateId,
      p_brand_id: args.brandId ?? null,
    });
    if (error) throw new Error(error.message);
    return data as Record<string, unknown>;
  },

  removeCustomerCompletenessTemplate: async (id: string) => {
    const { data, error } = await supabase.rpc("mcp_remove_customer_completeness_template", {
      p_id: id,
    });
    if (error) throw new Error(error.message);
    return data as Record<string, unknown>;
  },

  // ── Per-customer flow settings (#943 / migration 6131, consumed by #976) ──
  // Reads go straight through the Data API: ops.customer_flow_settings
  // grants `authenticated` SELECT, gated by a permissive admin.manage policy
  // AND-ed with a RESTRICTIVE AAL2 policy — no RPC needed for the read side.
  getCustomerFlowSettings: async (customerId: string) => {
    const { data, error } = await supabaseSchema("ops")
      .from("customer_flow_settings")
      .select(
        "customer_id, min_gp_pct, landed_factor_default, selling_multiple_default, receiver_name, receiver_party_id, committed_state_enabled, required_sample_kinds, updated_at",
      )
      .eq("customer_id", customerId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data as {
      customer_id: string;
      min_gp_pct: number | null;
      landed_factor_default: number | null;
      selling_multiple_default: number | null;
      receiver_name: string | null;
      // #1074 — D28's receiver, resolvable to a crm.parties row. Lives beside
      // receiver_name, which is left exactly as-is.
      receiver_party_id: string | null;
      committed_state_enabled: boolean | null;
      // #1114 — the required-kind matrix, RAW and three-valued: null = not
      // answered yet, [] = "requires none", a non-empty array = these kinds.
      // Resolution (the global fallback) is a server concern; the editor on
      // /admin/settings only ever round-trips the customer's own answer.
      required_sample_kinds: string[] | null;
      updated_at: string;
    } | null;
  },

  // Writes go through the cc-os-mcp Edge Function (NOT direct PostgREST, see
  // its verb file's header): public.mcp_set_customer_flow_settings has no
  // `authenticated` grant at all — the service-role client is the only path
  // a human reaches it through. Patch semantics: an omitted key is left
  // alone, an explicit null clears it back to "no override".
  setCustomerFlowSettings: (args: {
    customerId: string;
    patch: {
      min_gp_pct?: number | null;
      landed_factor_default?: number | null;
      selling_multiple_default?: number | null;
      receiver_name?: string | null;
      receiver_party_id?: string | null;
      committed_state_enabled?: boolean | null;
      /** #1114 — which sample kinds this customer requires before an order of
       *  theirs may leave S7. Three-valued like committed_state_enabled: omit
       *  to leave alone, `null` to clear the answer, `[]` for "requires
       *  none". */
      required_sample_kinds?: SampleKind[] | null;
    };
  }) =>
    callTool<
      { customer_id: string; patch: typeof args.patch },
      {
        settings: {
          customer_id: string;
          min_gp_pct: number | null;
          landed_factor_default: number | null;
          selling_multiple_default: number | null;
          receiver_name: string | null;
          receiver_party_id: string | null;
          committed_state_enabled: boolean | null;
          required_sample_kinds: string[] | null;
          created_at: string;
          updated_at: string;
        };
        resolved: {
          customer_id: string;
          min_gp_pct: number;
          min_gp_pct_source: "customer" | "global";
          landed_factor_default: number | null;
          selling_multiple_default: number | null;
          receiver_name: string | null;
          receiver_party_id: string | null;
          // #1074 — the party when linked, else the text, else neither.
          receiver: ResolvedParty;
          committed_state_enabled: boolean | null;
          required_sample_kinds: string[] | null;
          required_sample_kinds_resolved: string[];
          required_sample_kinds_source: "customer" | "global";
          has_settings_row: boolean;
        };
      }
    >("set_customer_flow_settings", { customer_id: args.customerId, patch: args.patch }),

  // ── Notifications (#958, Epic #956 §5.16) ───────────────────────────────
  // Schema/RPC only for now — no badge or feed UI exists yet (phase 2a).
  // Same transport choice as the stage-owner wrappers above: bypass the EF
  // and call the RPCs directly via PostgREST. These four are the ONLY way an
  // `authenticated` user changes notification state — ops.notifications
  // grants it SELECT and nothing else, and every RPC re-derives the caller
  // from auth.uid() server-side, so a forged user id in a payload is
  // impossible by construction. The emit side (ops.emit_notification*) is
  // service_role-only and deliberately has no wrapper here.
  listMyNotifications: async (args?: {
    includeRead?: boolean;
    limit?: number;
    before?: string | null;
  }) => {
    const { data, error } = await supabase.rpc("mcp_list_my_notifications", {
      p_include_read: args?.includeRead ?? false,
      p_limit: args?.limit ?? 50,
      p_before: args?.before ?? null,
    });
    if (error) throw new Error(error.message);
    return (data ?? []) as Array<{
      id: string;
      kind: string;
      severity: "info" | "warning" | "critical";
      stage: string | null;
      entity_type: string | null;
      entity_id: string | null;
      title: string;
      body: string | null;
      link_path: string | null;
      dedupe_key: string;
      created_at: string;
      read_at: string | null;
      acked_at: string | null;
      snoozed_until: string | null;
    }>;
  },

  // Each of the three mutators returns the number of rows CHANGED, so 0 means
  // "nothing to do" (already read/acked, resolved, or not yours) rather than
  // an error.
  markNotificationsRead: async (ids: string[]) => {
    const { data, error } = await supabase.rpc("mcp_mark_notifications_read", {
      p_ids: ids,
    });
    if (error) throw new Error(error.message);
    return (data ?? 0) as number;
  },

  ackNotification: async (id: string) => {
    const { data, error } = await supabase.rpc("mcp_ack_notification", {
      p_id: id,
    });
    if (error) throw new Error(error.message);
    return (data ?? 0) as number;
  },

  // `until` is an ISO timestamp; pass one in the past to un-snooze.
  snoozeNotification: async (id: string, until: string) => {
    const { data, error } = await supabase.rpc("mcp_snooze_notification", {
      p_id: id,
      p_until: until,
    });
    if (error) throw new Error(error.message);
    return (data ?? 0) as number;
  },

  // Every badge count the caller is allowed to see, in one round trip (#960,
  // Epic #956 §5.16). Same transport as the notification wrappers above —
  // direct supabase.rpc(), bypassing the EF. Keys of `surfaces` are nav url
  // values (AppSidebar.tsx); a surface the caller cannot reach is simply
  // absent from the map (never present with a zero count). `kinds` holds
  // whatever a badge SOURCE reports for that surface — `open`/`overdue` for
  // /actions today, more as 2c/E4 land. No UI consumes this yet (2a).
  getMyBadgeCounts: async (): Promise<{
    total: number;
    surfaces: Record<string, { count: number; kinds: Record<string, number> }>;
    generated_at: string;
  }> => {
    const { data, error } = await supabase.rpc("get_my_badge_counts");
    if (error) throw new Error(error.message);
    return data as {
      total: number;
      surfaces: Record<string, { count: number; kinds: Record<string, number> }>;
      generated_at: string;
    };
  },

  // ── /pipeline board (#1008, Epic #956 phase 3a) ───────────────────────────
  //
  // One round trip: every brief/order row that currently maps to a seeded
  // ops.stage_owners.stage (public.get_pipeline_board, migration 6151), with
  // the SAME missing-item/days-in-status numbers ops.list_row_badges reports
  // elsewhere. The flat RPC columns are reshaped into the `badge` sub-object
  // so the board can reuse @/components/flow/FlowBadges' rowBadge() /
  // <RowFlowBadges> unmodified, exactly like every other flow-badge surface.
  getPipelineBoard: async (args?: {
    ownerUserId?: string | null;
    customerId?: string | null;
    seasonId?: string | null;
  }): Promise<PipelineCard[]> => {
    const { data, error } = await supabase.rpc("get_pipeline_board", {
      p_owner_user_id: args?.ownerUserId ?? null,
      p_customer_id: args?.customerId ?? null,
      p_season_id: args?.seasonId ?? null,
    });
    if (error) throw new Error(error.message);
    return ((data ?? []) as PipelineBoardRow[]).map((r) => ({
      entity_type: r.entity_type,
      entity_id: r.entity_id,
      stage: r.stage,
      display_id: r.display_id,
      title: r.title,
      customer_id: r.customer_id,
      customer_name: r.customer_name,
      season_id: r.season_id,
      season_code: r.season_code,
      owner_user_ids: r.owner_user_ids ?? [],
      badge: {
        missing_required: r.missing_required,
        missing_recommended: r.missing_recommended,
        missing_total: r.missing_total,
        status: r.status,
        status_since: r.status_since,
        days_in_status: r.days_in_status,
        age_level: r.age_level === "warn" || r.age_level === "critical" ? r.age_level : "ok",
      },
    }));
  },

  // The seeded stage keys to render as board columns (ops.stage_owners.stage,
  // distinct). Read directly — the table grants `authenticated` a plain
  // `select true` (6130_stage_owners.sql), no RPC needed. A from-zero
  // database with no real staff accounts yet seeds ZERO rows here (6130's own
  // seed-by-label comment), which renders zero columns — not an error, see
  // the /pipeline page's empty state.
  listPipelineStageKeys: async (): Promise<string[]> => {
    const { data, error } = await supabaseSchema("ops")
      .from("stage_owners")
      .select("stage");
    if (error) throw new Error(error.message);
    const keys = new Set<string>();
    for (const row of (data ?? []) as Array<{ stage: string }>) keys.add(row.stage);
    return Array.from(keys).sort();
  },

  // ── Ingest health (#464) ────────────────────────────────────────────────
  // Per-source (tminus / planning) ingest health: cron active flag + last
  // run timestamp/status, plus last_ingested_at / rows_ingested from the
  // source's raw.legacy_* landing table. Backs the Ingest section's
  // "Last successful ingest" panel on /admin/settings.
  listIngestHealth: (): Promise<{
    sources: Array<{
      source: string;
      ingest_source_tag: string;
      cron_jobname: string;
      cron_active: boolean | null;
      cron_last_run_ts: string | null;
      cron_last_run_status: string | null;
      last_ingested_at: string | null;
      rows_ingested: number;
    }>;
    count: number;
  }> =>
    callTool<Record<string, never>, {
      sources: Array<{
        source: string;
        ingest_source_tag: string;
        cron_jobname: string;
        cron_active: boolean | null;
        cron_last_run_ts: string | null;
        cron_last_run_status: string | null;
        last_ingested_at: string | null;
        rows_ingested: number;
      }>;
      count: number;
    }>("list_ingest_health", {}),

  // ── Import health: the run log and its alerts (#1229) ───────────────────
  // The LAST run per sheet-import source, with its mode, trigger, outcome,
  // the run's whole returned summary and the alert keys currently open FOR
  // THE CALLER. Backs the Ingest section's "Import health" card on
  // /admin/settings.
  //
  // WHY A DIRECT supabase.rpc AND NOT A cc-os-mcp VERB. cc-os-dev's Edge
  // Functions only move on merge (bot-deploy/cc-os/CLAUDE.md, "Dev's edge
  // functions lag your branch"), while a PR's migrations DO apply to dev — so
  // a new verb would leave this card erroring on every preview build of its
  // own PR. public.mcp_ingest_run_health is admin-gated in its own body
  // (mcp_check_admin_or_raise), exactly like listUserRoleGrants above, so the
  // gate stays server-side either way.
  getIngestRunHealth: async (): Promise<IngestRunHealth> => {
    const { data, error } = await supabase.rpc("mcp_ingest_run_health");
    if (error) throw new Error(error.message);
    return (data ?? { sources: [] }) as IngestRunHealth;
  },

  // ── Orders without milestones (#1314, #475, Epic #460) ──────────────────
  //
  // The admin panel behind "Settings → Orders without milestones": an order
  // whose milestone template was never seeded (migration 6214, #1230)
  // renders every critical-path stage as an un-actionable ghost. The verb
  // that fixes it (`backfillOrderMilestones` below) has existed since 6214
  // but had no button — this list is the panel's read side.
  //
  // WHY A DIRECT supabase.rpc AND NOT A cc-os-mcp VERB, same reasoning as
  // getIngestRunHealth above: cc-os-dev's Edge Functions only move on merge,
  // while this PR's new migration DOES apply to dev per-PR, so a brand-new
  // Edge Function verb would leave the panel erroring on its own PR's
  // preview. public.mcp_list_orders_missing_milestones is admin-gated in its
  // own body (mcp_check_admin_or_raise), so the gate stays server-side
  // either way.
  listOrdersMissingMilestones: async (): Promise<OrderMissingMilestonesRow[]> => {
    const { data, error } = await supabase.rpc("mcp_list_orders_missing_milestones");
    if (error) throw new Error(error.message);
    return (data ?? []) as OrderMissingMilestonesRow[];
  },

  // The seed/preview verb itself DOES exist as a cc-os-mcp verb already
  // (shipped with migration 6214) — `backfill_order_milestones`, admin-gated
  // (admin.manage), DRY RUN BY DEFAULT in both the Zod schema and the SQL
  // body, and audited on every execute (one ops.audit_events row naming the
  // actor, the counts, and every order id — a dry run writes nothing at
  // all). This wrapper is the panel's ONLY path to it, for both Preview
  // (dry_run: true) and Seed (dry_run: false).
  backfillOrderMilestones: (args: {
    dry_run: boolean;
    order_ids?: string[] | null;
    limit?: number | null;
  }): Promise<BackfillOrderMilestonesResult> =>
    callTool<typeof args, BackfillOrderMilestonesResult>(
      "backfill_order_milestones",
      args,
    ),

  // ── Presentations (W-Presentations / 5811-5816) ────────────────────────
  // Wave 1 ships scaffolds returning `unknown`. Wave 3 tightens the types
  // alongside the create + history pages.

  lookupPresentation: (args: {
    dedup_hash: string;
    sample_ids: string[];
    supplier_id?: string | null;
    customer_id?: string | null;
    format_xlsx: boolean;
    format_pptx: boolean;
  }): Promise<{
    kind: "exact" | "similar" | "none";
    presentation?: Record<string, unknown> | null;
    candidates?: Array<Record<string, unknown>>;
  }> =>
    callTool<typeof args, {
      kind: "exact" | "similar" | "none";
      presentation?: Record<string, unknown> | null;
      candidates?: Array<Record<string, unknown>>;
    }>("lookup_presentation", args),

  enqueuePresentation: (args: {
    title: string;
    purpose?: string | null;
    supplier_id?: string | null;
    customer_id?: string | null;
    field_selection?: string[];
    format_xlsx: boolean;
    format_pptx: boolean;
    sample_ids: string[];
    /** Per-slide notes keyed by library_items.id. Only applied when
     *  field_selection includes "notes" (rendering is gated on the same key). */
    sample_notes?: Record<string, string>;
    metadata?: Record<string, unknown>;
    /** Optional per-deck ZAR pricing factor (rand per USD). When set it
     *  overrides every sample's own library_items.factor for this deck.
     *  Omit/null = each slide prices at its own sample's factor (issue #307);
     *  there is no global fallback. Also a dedup-hash dimension: the same
     *  sample set at two factors is two presentations. */
    zar_factor?: number | null;
    skip_fuzzy?: boolean;
    /** Optional FK to crm.meetings.id (issue #391). Set only when this
     *  presentation was created via the "Create presentation" button on a
     *  meeting page, so it can be found again from that meeting. Omit/null
     *  for presentations created outside a meeting context. */
    meeting_id?: string | null;
    /** Optional FK to samples.meeting_buyers.id (issue #1270) — the ROSTER
     *  buyer this deck is for. Requires meeting_id, and the buyer must belong
     *  to that meeting. UNLIKE meeting_id it IS part of the dedup hash: the
     *  title, front notes page and reason tags are all that buyer's, so two
     *  buyers with identical picks must get two decks. */
    meeting_buyer_id?: string | null;
    /** Snapshot of the front notes page's text, seeded from the buyer's notes
     *  box and edited before generating. Only applied when field_selection
     *  includes "front_page" (rendering is gated on the same key). */
    front_page_notes?: string | null;
    /** Per-slide reasons keyed by library_items.id: why that sample is in
     *  this buyer's deck. Persisted regardless of the tag switch; printing is
     *  gated on "reason_tags" in field_selection. */
    sample_meeting_reasons?: Record<string, Array<"kept" | "cutting" | "note">>;
  }): Promise<{
    kind: "enqueued" | "exact" | "similar";
    job_id?: string | null;
    presentation_id?: string | null;
    presentation?: Record<string, unknown> | null;
    candidates?: Array<Record<string, unknown>>;
  }> =>
    callTool<typeof args, {
      kind: "enqueued" | "exact" | "similar";
      job_id?: string | null;
      presentation_id?: string | null;
      presentation?: Record<string, unknown> | null;
      candidates?: Array<Record<string, unknown>>;
    }>("enqueue_presentation", args),

  listPresentations: (args: {
    search?: string;
    supplier_id?: string | null;
    customer_id?: string | null;
    scope?: "supplier" | "customer" | "general";
    created_by?: string | null;
    contains_sample_id?: string | null;
    date_from?: string | null;
    date_to?: string | null;
    format?: "xlsx" | "pptx" | "both";
    include_archived?: boolean;
    limit?: number;
    cursor?: string;
  } = {}): Promise<{
    rows: Array<Record<string, unknown>>;
    next_cursor: string | null;
    count: number;
    total_count: number;
    has_more: boolean;
  }> =>
    callTool<typeof args, {
      rows: Array<Record<string, unknown>>;
      next_cursor: string | null;
      count: number;
      total_count: number;
      has_more: boolean;
    }>("list_presentations", args),

  getPresentation: (args: { id: string }): Promise<{
    presentation: Record<string, unknown> | null;
    items: Array<Record<string, unknown>>;
    files: Array<Record<string, unknown>>;
    job: Record<string, unknown> | null;
    jobs: Array<Record<string, unknown>>;
  }> =>
    callTool<typeof args, {
      presentation: Record<string, unknown> | null;
      items: Array<Record<string, unknown>>;
      files: Array<Record<string, unknown>>;
      job: Record<string, unknown> | null;
      jobs: Array<Record<string, unknown>>;
    }>("get_presentation", args),

  cancelPresentationJob: (args: { job_id: string }): Promise<{ ok: true }> =>
    callTool<typeof args, { ok: true }>("cancel_presentation_job", args),

  archivePresentation: (args: { id: string }): Promise<{ ok: true }> =>
    callTool<typeof args, { ok: true }>("archive_presentation", args),

  addPresentationSamples: (args: {
    presentation_id: string;
    sample_ids: string[];
  }): Promise<{
    kind: "enqueued" | "no_change" | "busy" | "conflict" | "duplicate" | "archived";
    presentation_id?: string | null;
    job_ids?: string[];
    added_count?: number;
    collider?: { id: string; title: string; archived_at: string | null } | null;
  }> =>
    callTool<typeof args, {
      kind: "enqueued" | "no_change" | "busy" | "conflict" | "duplicate" | "archived";
      presentation_id?: string | null;
      job_ids?: string[];
      added_count?: number;
      collider?: { id: string; title: string; archived_at: string | null } | null;
    }>("add_presentation_samples", args),

  updatePresentationNotes: (args: {
    presentation_id: string;
    /** Per-slide notes keyed by library_items.id (null/empty clears). */
    notes: Record<string, string | null>;
  }): Promise<{
    kind: "enqueued" | "no_change" | "busy" | "archived";
    presentation_id?: string | null;
    job_ids?: string[];
    touched?: number;
  }> =>
    callTool<typeof args, {
      kind: "enqueued" | "no_change" | "busy" | "archived";
      presentation_id?: string | null;
      job_ids?: string[];
      touched?: number;
    }>("update_presentation_notes", args),

  // ── Briefs verbs (5961-5964) ────────────────────────────────────────────
  // ONE BRIEF = ONE SAMPLE (5964): each brief is a single sample-development
  // brief carrying its own source, requested changes, pricing, origin and
  // status (the status doubles as the per-sample outcome). The former
  // brief_items detail table — and its add/update/delete verbs — are gone.
  // Files attach via the brief scope on the sample-uploads verbs above. Roles:
  // admin, sales, sample_coordinator (gated server-side).

  /**
   * One PAGE of briefs, newest first (#1197). The verb bounds the read with or
   * without `limit` (its own default is 100, max 500), so a caller that wants
   * a wider set asks for one explicitly instead of relying on an unbounded
   * read — and walks `offset`/`next_offset` beyond the cap.
   *
   * `has_more` / `next_offset` are optional on the response because
   * cc-os-dev's deployed cc-os-mcp lags an open PR (edge functions deploy on
   * merge, see bot-deploy/cc-os/CLAUDE.md "Environments"): an older verb
   * answers `{briefs, count}` only, and a caller must read a missing
   * `has_more` as "no further page", never as "unknown, retry".
   */
  listBriefs: (
    args: {
      status?: BriefStatus;
      customer_id?: string;
      meeting_id?: string;
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<{
    briefs: BriefListRow[];
    count: number;
    has_more?: boolean;
    next_offset?: number | null;
  }> =>
    callTool<typeof args, {
      briefs: BriefListRow[];
      count: number;
      has_more?: boolean;
      next_offset?: number | null;
    }>(
      "list_briefs", args,
    ),

  getBrief: (
    args: { id: string },
  ): Promise<{ brief: Brief | null; origin_opportunity: BriefOpportunity | null }> =>
    callTool<typeof args, { brief: Brief | null; origin_opportunity: BriefOpportunity | null }>(
      "get_brief", args,
    ),

  /**
   * The completeness checklist for a brief (#964) — items still missing for
   * its next transition(s), required first, plus the most recent sign-off
   * override (if any) that landed it in its current status. Drives
   * MissingItemsPanel, StageChip and AdvanceButton.
   */
  getBriefMissingItems: (
    args: { id: string },
  ): Promise<BriefMissingItemsResult> =>
    callTool<typeof args, BriefMissingItemsResult>("get_brief_missing_items", args),

  createBrief: (args: {
    customer_id?: string;
    buyer_name?: string;
    title: string;
    meeting_id?: string;
    notes?: string;
    source_library_item_id?: string;
    changes?: BriefChange[];
    new_supplier_dollar_price?: number;
    new_factor?: number;
    final_price_zar?: number;
    origin?: BriefSettableOrigin;
    brand_id?: string;
    buyer_id?: string;
    label_id?: string;
  } & BriefSpecCreateArgs & BriefStyleIdentityCreateArgs): Promise<{ brief: Brief }> =>
    callTool<typeof args, { brief: Brief }>("create_brief", args),

  updateBrief: (args: {
    id: string;
    title?: string;
    status?: BriefStatus;
    customer_id?: string | null;
    buyer_name?: string | null;
    notes?: string | null;
    changes?: BriefChange[];
    new_supplier_dollar_price?: number | null;
    new_factor?: number | null;
    final_price_zar?: number | null;
    origin?: BriefSettableOrigin;
    brand_id?: string | null;
    buyer_id?: string | null;
    label_id?: string | null;
    /**
     * Sign-off override for the `draft → briefed` completeness gate (#944,
     * migration 6132). Only valid alongside `status: "briefed"`, and only for a
     * holder of the `orders.signoff` capability. The reason, the actor and the
     * exact waived items land in ops.completeness_overrides + ops.audit_events;
     * the waived items keep their badge.
     */
    signoff_override_reason?: string;
  } & BriefSpecUpdateArgs & BriefStyleIdentityUpdateArgs): Promise<{ brief: Brief }> =>
    callTool<typeof args, { brief: Brief }>("update_brief", args),

  deleteBrief: (args: { id: string }): Promise<{ brief: Brief }> =>
    callTool<typeof args, { brief: Brief }>("delete_brief", args),

  createBriefsFromMeeting: (args: { meeting_id: string }): Promise<{
    briefs: Brief[];
    count: number;
    warnings: BriefFromMeetingWarning[];
  }> =>
    callTool<typeof args, {
      briefs: Brief[];
      count: number;
      warnings: BriefFromMeetingWarning[];
    }>("create_briefs_from_meeting", args),

  // ── Brief <-> S1 opportunity matcher (#963, migration 6137) ─────────────
  // Suggest-and-confirm (design D10): suggestBriefOpportunities never writes;
  // linkBriefOpportunity/unlinkBriefOpportunity are the only writes, and only
  // ever run in response to a person clicking Link/Unlink.

  suggestBriefOpportunities: (
    args: { brief_id: string; limit?: number },
  ): Promise<{ suggestions: BriefOpportunity[] }> =>
    callTool<typeof args, { suggestions: BriefOpportunity[] }>(
      "suggest_brief_opportunities", args,
    ),

  linkBriefOpportunity: (
    args: { brief_id: string; opportunity_id: string; replace?: boolean },
  ): Promise<{ brief: Brief }> =>
    callTool<typeof args, { brief: Brief }>("link_brief_opportunity", args),

  unlinkBriefOpportunity: (args: { brief_id: string }): Promise<{ brief: Brief }> =>
    callTool<typeof args, { brief: Brief }>("unlink_brief_opportunity", args),

  // ── Brief measurement spec + fit/PP sessions (#1274, Epic #1272 phase 1b,
  // migration 6220) — the "Spec & fits" tab. setBriefSpecPoints always
  // resubmits the COMPLETE POM list; the SQL side decides what changed
  // (supersede / soft-remove / insert). createFitSession is one-shot — a
  // correction is a new session with supersedesSessionId pointing at the one
  // it replaces, never a patch.

  setBriefSpecPoints: (args: {
    brief_id: string;
    points: BriefSpecPointInput[];
    source?: BriefSpecPointSource;
  }): Promise<BriefSpecPointsList> =>
    callTool<typeof args, BriefSpecPointsList>("set_brief_spec_points", args),

  listBriefSpecPoints: (args: { brief_id: string }): Promise<BriefSpecPointsList> =>
    callTool<typeof args, BriefSpecPointsList>("list_brief_spec_points", args),

  // Import a CONFIRMED Truworths briefsheet measurement_grid field into the
  // brief's live spec above (#1293, Epic #1272 phase 2d, migration 6227) —
  // via set_brief_spec_points unchanged, never a direct write. Idempotent:
  // calling it again on an unchanged confirmed grid revises nothing.
  applyBriefSpecFromExtraction: (
    args: { extraction_id: string },
  ): Promise<BriefSpecPointsList> =>
    callTool<typeof args, BriefSpecPointsList>(
      "apply_brief_spec_from_extraction", args,
    ),

  createFitSession: (args: {
    brief_id: string;
    stage: FitSessionStage;
    decision: FitSessionDecision;
    size_measured: string;
    measurements: FitMeasurementInput[];
    session_date?: string;
    sample_id?: string;
    general_comments?: string;
    next_sample_due?: string;
    notes?: string;
    supersedes_session_id?: string;
  }): Promise<FitSessionDetail> =>
    callTool<typeof args, FitSessionDetail>("create_fit_session", args),

  listFitSessions: (
    args: { brief_id: string; stage?: FitSessionStage },
  ): Promise<FitSessionsList> =>
    callTool<typeof args, FitSessionsList>("list_fit_sessions", args),

  getFitSession: (args: { fit_session_id: string }): Promise<FitSessionDetail> =>
    callTool<typeof args, FitSessionDetail>("get_fit_session", args),

  // ── Sample uploads verbs (5900) ────────────────────────────────────────
  // Generic file uploads attached to a colorway (library_item) or its parent
  // style. Each upload carries a doc-type tag, audit trail, supersede chain,
  // and soft-delete. See docs/plans/2026-05-11-sample-uploads-design.md.

  listSampleUploadDocTypes: (): Promise<SampleUploadDocType[]> =>
    callTool<Record<string, never>, { doc_types: SampleUploadDocType[] }>(
      "list_sample_upload_doc_types", {},
    ).then((r) => r.doc_types),

  listSampleUploads: (
    args:
      | { library_item_id: string; include_deleted?: boolean }
      | { meeting_id: string; include_deleted?: boolean }
      | { brief_id: string; include_deleted?: boolean }
      // #1204 (migration 6204): the customer-order scope — the order's own
      // files, e.g. the customer PO document.
      | { order_id: string; include_deleted?: boolean }
      // #1208 (migration 6205): the supplier-PO (PPO) scope — the PPO's own
      // files, e.g. the supplier's PI document.
      | { purchase_order_id: string; include_deleted?: boolean }
      // #1211 (migration 6208): the shipment scope — the shipping and
      // receiving documents.
      | { shipment_id: string; include_deleted?: boolean }
      // #1225 (migration 6211): the QA-inspection scope — the inspector's own
      // report, filed here by email triage or by a person on /qa/:id.
      | { qa_inspection_id: string; include_deleted?: boolean }
      // #1292 (migration 6226): the fit / PP session scope — CC's own fit
      // sample report in both renditions, and the supplier's marked-up reply.
      | { fit_session_id: string; include_deleted?: boolean },
  ): Promise<SampleUpload[]> =>
    callTool<typeof args, { uploads: SampleUpload[] }>(
      "list_sample_uploads", args,
    ).then((r) => r.uploads),

  listSampleUploadEvents: (
    args: { upload_id: string },
  ): Promise<SampleUploadEvent[]> =>
    callTool<typeof args, { events: SampleUploadEvent[] }>(
      "list_sample_upload_events", args,
    ).then((r) => r.events),

  dedupCheckSampleUpload: (
    args: {
      scope:
        | { library_item_id: string }
        | { style_id: string }
        | { meeting_id: string }
        | { brief_id: string }
        | { order_id: string }
        | { purchase_order_id: string }
        | { shipment_id: string }
        | { qa_inspection_id: string }
        | { fit_session_id: string };
      content_hash: string;
    },
  ): Promise<{
    existing?: {
      upload_id: string;
      original_filename: string;
      created_at: string;
      created_by: string | null;
      uploaded_by_email?: string | null;
    };
  }> =>
    callTool<typeof args, {
      existing?: {
        upload_id: string;
        original_filename: string;
        created_at: string;
        created_by: string | null;
        uploaded_by_email?: string | null;
      };
    }>("dedup_check_sample_upload", args),

  // ── Outgoing documents (#1273, migration 6225, Epic #1272 phase 1a) ──────
  // The GENERATED half of a scope's files — what CC sends OUT — plus the
  // record that one actually went out. Incoming documents (a customer PO, a
  // supplier PI, a QIMA report) stay with listSampleUploads above; the two
  // lists are deliberately separate, because "has this gone to the customer
  // yet" is only a question about the outgoing ones.

  getOutgoingDocuments: (
    args: { scope_kind: OutgoingDocumentScopeKind; scope_id: string },
  ): Promise<OutgoingDocumentsResult> =>
    callTool<typeof args, OutgoingDocumentsResult>("get_outgoing_documents", args),

  /**
   * Record that a document went out. Does NOT send anything — there is no
   * automatic email in cc-os (ruling D16); a person sends the file and then
   * records that they did. Calling it twice records two sends, which is
   * correct: a document can go to the buyer and again to the DC.
   */
  markDocumentSent: (
    args: {
      upload_id: string;
      sent_at?: string;
      sent_to?: string[];
      note?: string;
    },
  ): Promise<DocumentSendResult> =>
    callTool<typeof args, DocumentSendResult>("mark_document_sent", args),

  requestSampleUploadDownload: (
    args: { upload_id: string },
  ): Promise<{
    signed_url: string;
    original_filename: string;
    mime_type: string | null;
    expires_at: string;
  }> =>
    callTool<typeof args, {
      signed_url: string;
      original_filename: string;
      mime_type: string | null;
      expires_at: string;
    }>("request_sample_upload_download", args),

  requestBulkSampleUploadDownload: (
    args: { upload_ids: string[] },
  ): Promise<{
    files: Array<{
      upload_id: string;
      signed_url: string;
      original_filename: string;
      mime_type: string | null;
      size_bytes: number | null;
    }>;
  }> =>
    callTool<typeof args, {
      files: Array<{
        upload_id: string;
        signed_url: string;
        original_filename: string;
        mime_type: string | null;
        size_bytes: number | null;
      }>;
    }>("request_bulk_sample_upload_download", args),

  createSampleUpload: (
    args: {
      scope:
        | { library_item_id: string }
        | { style_id: string }
        | { meeting_id: string }
        | { brief_id: string }
        | { order_id: string }
        | { purchase_order_id: string }
        | { shipment_id: string }
        | { qa_inspection_id: string }
        | { fit_session_id: string };
      storage_path: string;
      original_filename: string;
      mime_type?: string;
      size_bytes?: number;
      content_hash?: string;
      label?: string;
      doc_type_id?: string;
      supersedes_id?: string;
      /** Free-text version note. The portal requires it from v2 onward for Tech Pack uploads. */
      change_note?: string;
      /** 'cc' (CC's own pack), 'customer' (the buyer's own pack, e.g. uploaded as v1), 'supplier' (the supplier's own shipping document, #1211) or 'inspector' (a third-party inspector's own report, #1225). Defaults to 'cc' server-side for a brief-scoped Tech Pack. */
      origin?: "cc" | "customer" | "supplier" | "inspector";
    },
  ): Promise<SampleUpload> =>
    callTool<typeof args, { upload: SampleUpload }>(
      "create_sample_upload", args,
    ).then((r) => r.upload),

  // ── Tech pack versions (6146, #962, Epic #957 Phase 1c) ─────────────────

  approveTechPackVersion: (
    args: { upload_id: string; approve: boolean; reason?: string },
  ): Promise<SampleUpload> =>
    callTool<typeof args, { upload: SampleUpload }>(
      "approve_tech_pack_version", args,
    ).then((r) => r.upload),

  listBriefTechPackVersions: (
    args: { brief_id: string },
  ): Promise<TechPackVersion[]> =>
    callTool<typeof args, { versions: TechPackVersion[]; count: number }>(
      "list_brief_tech_pack_versions", args,
    ).then((r) => r.versions),

  updateSampleUploadLabel: (
    args: { upload_id: string; label: string },
  ): Promise<SampleUpload> =>
    callTool<typeof args, { upload: SampleUpload }>(
      "update_sample_upload_label", args,
    ).then((r) => r.upload),

  setSampleUploadDocType: (
    args: { upload_id: string; doc_type_id: string },
  ): Promise<SampleUpload> =>
    callTool<typeof args, { upload: SampleUpload }>(
      "set_sample_upload_doc_type", args,
    ).then((r) => r.upload),

  supersedeSampleUpload: (
    args: { new_upload_id: string; superseded_upload_id: string },
  ): Promise<SampleUpload> =>
    callTool<typeof args, { upload: SampleUpload }>(
      "supersede_sample_upload", args,
    ).then((r) => r.upload),

  softDeleteSampleUpload: (
    args: { upload_id: string; reason?: string },
  ): Promise<SampleUpload> =>
    callTool<typeof args, { upload: SampleUpload }>(
      "soft_delete_sample_upload", args,
    ).then((r) => r.upload),

  restoreSampleUpload: (
    args: { upload_id: string },
  ): Promise<SampleUpload> =>
    callTool<typeof args, { upload: SampleUpload }>(
      "restore_sample_upload", args,
    ).then((r) => r.upload),

  createSampleUploadDocType: (
    args: {
      name: string;
      slug?: string;
      description?: string;
      display_order?: number;
    },
  ): Promise<SampleUploadDocType> =>
    callTool<typeof args, { doc_type: SampleUploadDocType }>(
      "create_sample_upload_doc_type", args,
    ).then((r) => r.doc_type),

  updateSampleUploadDocType: (
    args: {
      id: string;
      name?: string;
      slug?: string;
      description?: string;
      display_order?: number;
    },
  ): Promise<SampleUploadDocType> =>
    callTool<typeof args, { doc_type: SampleUploadDocType }>(
      "update_sample_upload_doc_type", args,
    ).then((r) => r.doc_type),

  deleteSampleUploadDocType: (
    args: { id: string },
  ): Promise<SampleUploadDocType> =>
    callTool<typeof args, { doc_type: SampleUploadDocType; deleted: true }>(
      "delete_sample_upload_doc_type", args,
    ).then((r) => r.doc_type),

  // ── Sample-upload share links (6022/6023, #519) ────────────────────────
  // Scoped, expiring, revocable links that let someone WITHOUT a portal
  // account read specific documents. The plaintext token comes back from
  // `createSampleUploadShareLink` and nowhere else — only its SHA-256 hash is
  // stored, so a link the user fails to copy is gone for good. Redemption
  // goes through the unauthenticated `sample-share` edge function (see
  // lib/shareLink.ts), never through these verbs.

  createSampleUploadShareLink: (
    args: {
      upload_ids: string[];
      expires_in_hours: number;
      label?: string;
    },
  ): Promise<CreatedSampleUploadShareLink> =>
    callTool<typeof args, CreatedSampleUploadShareLink>(
      "create_sample_upload_share_link", args,
    ),

  listSampleUploadShareLinks: (
    args: { upload_id: string; include_inactive?: boolean },
  ): Promise<SampleUploadShareLink[]> =>
    callTool<typeof args, { share_links: SampleUploadShareLink[] }>(
      "list_sample_upload_share_links", args,
    ).then((r) => r.share_links),

  revokeSampleUploadShareLink: (
    args: { share_link_id: string },
  ): Promise<{ share_link: SampleUploadShareLink; already_revoked: boolean }> =>
    callTool<
      typeof args,
      { share_link: SampleUploadShareLink; already_revoked: boolean }
    >("revoke_sample_upload_share_link", args),

  // ── Admin Settings + Notifications verbs (W14 / 5754), wired for #596 ────
  // Admin-only system surface: cron schedules, audit log, ingest health. All
  // SECURITY DEFINER + admin-gated server-side (mcp_check_admin_or_raise()).
  // These verbs already existed and were registered in cc-os-mcp/index.ts;
  // the pages just called supabase.rpc() directly instead of routing through
  // here, so pause/unpause and friends never picked up the withAudit() trail.

  listCronJobs: () =>
    callTool<Record<string, never>, {
      jobs: Array<{
        jobid: number;
        jobname: string;
        schedule: string;
        command_excerpt: string;
        active: boolean;
        last_run_ts: string | null;
        last_run_status: string | null;
      }>;
      count: number;
    }>("list_cron_jobs", {}),

  pauseCronJob: (args: { jobname: string }) =>
    callTool<typeof args, {
      jobid: number;
      jobname: string;
      was_active: boolean;
      now_active: boolean;
    }>("pause_cron_job", args),

  unpauseCronJob: (args: { jobname: string }) =>
    callTool<typeof args, {
      jobid: number;
      jobname: string;
      was_active: boolean;
      now_active: boolean;
    }>("unpause_cron_job", args),

  listFailedCronRuns: (args: { limit?: number } = {}) =>
    callTool<typeof args, {
      runs: Array<{
        runid: number;
        jobid: number;
        jobname: string | null;
        start_time: string | null;
        end_time: string | null;
        status: string;
        return_message: string;
        duration_ms: number | null;
      }>;
      count: number;
    }>("list_failed_cron_runs", args),

  listRecentAuditEvents: (
    args: {
      table_name?: string;
      actor_id?: string;
      since_ts?: string;
      limit?: number;
    } = {},
  ) =>
    callTool<typeof args, {
      events: Array<{
        id: number;
        ts: string;
        op: string;
        table_schema: string;
        table_name: string;
        record_id: string | null;
        actor_user_id: string | null;
        actor_email: string | null;
        agent_id: string | null;
      }>;
      count: number;
    }>("list_recent_audit_events", args),

  listRecentIngestRuns: (args: { limit?: number } = {}) =>
    callTool<typeof args, {
      runs: Array<{
        id: string;
        created_at: string;
        event_type: string;
        event_name: string;
        agent_id: string | null;
        agent_run_id: string | null;
        payload: unknown;
      }>;
      count: number;
    }>("list_recent_ingest_runs", args),

  // ── Library split / loan atomics / fabric lookup (#596) ─────────────────
  // New verbs added specifically for this migration — see
  // supabase/functions/cc-os-mcp/verbs/{split_library_item_to_new_style,
  // create_loan_atomic,extend_loan_atomic,fabric_for_items}.ts. All were
  // previously called directly via supabase.rpc(...), bypassing withAudit().

  splitLibraryItemToNewStyle: (args: { item_id: string }) =>
    callTool<typeof args, { new_style_id: string }>(
      "split_library_item_to_new_style", args,
    ),

  // Distinct from `addLoan` (backs the older mcp_add_loan RPC). This wraps
  // mcp_create_loan_atomic, which also carries waybill_number + meeting_id
  // through the same transaction — used by the /library/loans/new form.
  createLoanAtomic: (args: {
    library_item_id: string;
    borrower_kind: "customer" | "supplier" | "staff" | "meeting" | "external";
    customer_id?: string | null;
    supplier_id?: string | null;
    staff_user_id?: string | null;
    borrower_label?: string | null;
    purpose?: string | null;
    expected_return?: string | null;
    condition_on_loan?: string;
    notes?: string | null;
    meeting_id?: string | null;
    waybill_number?: string | null;
    unit_id?: string | null;
    buyer_id?: string | null;
  }) =>
    callTool<typeof args, {
      id: string;
      status: string;
      unit_id?: string;
      unit_number?: number;
    }>("create_loan_atomic", args),

  // Distinct from `extendLoan` (backs the older mcp_extend_loan RPC).
  extendLoanAtomic: (args: {
    loan_id: string;
    new_expected_return: string;
    reason?: string | null;
  }) =>
    callTool<typeof args, { extension_id: string; expected_return: string }>(
      "extend_loan_atomic", args,
    ),

  fabricForItems: (args: { item_ids: string[] }) =>
    callTool<typeof args, {
      items: Array<{ library_item_id: string; fabric: string | null }>;
      count: number;
    }>("fabric_for_items", args),

  // ── Size sets / curves / pre-packs (6057 / #595, #648, #673, #681) ──────
  // /admin/sizes: size sets are the ordered scales (MA = XXS..5XL, ...), named
  // curves are reusable relative-weight distributions across a set's slots
  // (ratio is ALWAYS derived server-side — migration 6057 D2 — never a
  // stored field, so these types never carry one), and pre-packs (declared
  // vs computed pack total) are now writable too — migration 6084 (#681)
  // added upsert_size_prepack alongside the PACKS/SINGLES
  // packing-instruction catalogue read/write pair. All are
  // admin.manage-gated at the Edge Function layer.

  listSizeSets: (args: { season_code?: string; include_inactive?: boolean } = {}) =>
    callTool<typeof args, { size_sets: SizeSetSummary[] }>(
      "list_size_sets", args,
    ).then((r) => r.size_sets),

  getSizeSet: (args: { size_set_id: string }) =>
    callTool<typeof args, SizeSetDetail>("get_size_set", args),

  upsertSizeSet: (args: {
    season_code: string;
    code: string;
    name?: string | null;
    description?: string | null;
    source?: string;
    is_active?: boolean;
    notes?: string | null;
    members?: Array<{ slot_position: number; label: string }>;
  }) =>
    callTool<typeof args, {
      size_set_id: string;
      season_code: string;
      code: string;
      members: SizeSetMember[];
    }>("upsert_size_set", args),

  upsertSizeCurve: (args: {
    size_set_id: string;
    code: string;
    name?: string | null;
    description?: string | null;
    is_active?: boolean;
    points?: Array<{ slot_position: number; weight: number }>;
  }) =>
    callTool<typeof args, {
      size_curve_id: string;
      size_set_id: string;
      code: string;
      ratios: SizeCurveRatioPoint[];
    }>("upsert_size_curve", args),

  // ── Alias mapping (Epic #740 Phase 3a/#759, over migration 6076) ────────
  // list_aliases is ungated (D30: read-only for everyone); map_alias/
  // unmap_alias require crm.map_aliases. #761's Map Unmatched Names screen
  // (customers only for now) wires these three together.
  listAliases: (
    args: {
      scope?: AliasScope;
      source?: AliasSource;
      customer_id?: string;
      query?: string;
      limit?: number;
    } = {},
  ) =>
    callTool<typeof args, { aliases: Array<Record<string, unknown>> }>(
      "list_aliases", args,
    ),

  mapAlias: (args: {
    alias_raw: string;
    scope: AliasScope;
    customer_id?: string;
    brand_id?: string;
    buyer_id?: string;
    label_id?: string;
    channel?: "cc_corporate_range" | "cc_stock";
    source?: AliasSource;
    note?: string | null;
  }) =>
    callTool<typeof args, { alias: Record<string, unknown>; was_new: boolean }>(
      "map_alias", args,
    ),

  unmapAlias: (
    args:
      | { alias_id: string; source?: AliasSource }
      | { scope: AliasScope; alias_raw: string; source?: AliasSource },
  ) =>
    callTool<typeof args, { deleted: boolean; alias: Record<string, unknown> | null }>(
      "unmap_alias", args,
    ),

  // ── Map Unmatched Names screen (#761, Epic #740 Phase 3, widened by #797) ─
  // All three sheets (briefed | tminus | planning) and customer/brand/buyer
  // scopes. Gated on crm.map_aliases at the Edge Function layer. Label and
  // channel have no sheet-driven unmatched list (see the verb's header) —
  // they are mapped by hand via mapAlias with a typed string.
  listUnmatchedCustomers: (
    args: { sheet?: "briefed" | "tminus" | "planning" } = {},
  ) =>
    callTool<typeof args, {
      sheet: "briefed" | "tminus" | "planning";
      unmatched_customer: Array<{ name: string; rows: number }>;
      unmatched_brand: Array<{ name: string; rows: number }>;
      unmatched_buyer: Array<{ name: string; rows: number }>;
    }>("list_unmatched_customers", args),

  // #681 — write RPC for pre-packs (migration 6084).
  upsertSizePrepack: (args: {
    size_set_id: string;
    code: string;
    name?: string | null;
    description?: string | null;
    declared_pack_total?: number | null;
    is_active?: boolean;
    slots?: Array<{ slot_position: number; qty: number }>;
  }) =>
    callTool<typeof args, {
      prepack_id: string;
      size_set_id: string;
      code: string;
      totals: SizePrepackTotals;
      slots: SizePrepackSlot[];
    }>("upsert_size_prepack", args),

  // #681 — PACKS/SINGLES packing-instruction catalogue (migration 6084).
  // Not scoped to a size set — free-standing catalogue keyed by
  // (season_code, code), same as size sets.
  listPackingInstructions: (args: { season_code?: string; include_inactive?: boolean } = {}) =>
    callTool<typeof args, { packing_instructions: PackingInstructionRow[] }>(
      "list_packing_instructions", args,
    ).then((r) => r.packing_instructions),

  upsertPackingInstruction: (args: {
    season_code: string;
    code: string;
    label?: string | null;
    description?: string | null;
    unit_kind?: string;
    is_active?: boolean;
  }) =>
    callTool<typeof args, { id: string; season_code: string; code: string }>(
      "upsert_packing_instruction", args,
    ),

  // ── Per-customer/category size-curve templates (#1062, Epic #1060 Phase
  // 1b, design D22; migration 6154) — /admin/sizes' templates section.
  // admin.manage-gated at the Edge Function layer, same posture as the
  // other size-model write verbs above.
  listSizeCurveTemplates: (args: { size_set_id?: string } = {}) =>
    callTool<typeof args, { templates: SizeCurveTemplateRow[] }>(
      "list_size_curve_templates", args,
    ).then((r) => r.templates),

  upsertSizeCurveTemplate: (args: {
    size_set_id: string;
    size_curve_id: string;
    customer_id?: string | null;
    category?: string | null;
    priority?: number;
    notes?: string | null;
  }) =>
    callTool<typeof args, {
      id: string;
      size_set_id: string;
      size_curve_id: string;
      customer_id: string | null;
      category: string | null;
      priority: number;
    }>("upsert_size_curve_template", args),

  // ── Solid size-to-carton packing + brief/template pre-fill (#1062, Epic
  // #1060 Phase 1b; migration 6154). orders.manage-gated at the Edge
  // Function layer — same posture as apply_size_curve_to_order_line /
  // apply_prepack_to_order_line above.
  prefillOrderLineSizes: (args: { order_line_id: string; overwrite?: boolean }) =>
    callTool<typeof args, PrefillOrderLineSizesOutput>("prefill_order_line_sizes", args),

  setOrderLinePacking: (args: { order_line_id: string; pieces_per_carton: number | null }) =>
    callTool<typeof args, {
      order_line_id: string;
      pieces_per_carton: number | null;
      reconciliation: SizeReconciliation;
    }>("set_order_line_packing", args),

  // ── Supplier PPO v2 (#1064, Epic #1063 Phase 1a; migration 6165) ────────
  // A PPO is per SUPPLIER and holds snapshotted blocks from many customer
  // SOs (design §2.3). The six document verbs are orders.manage-gated at the
  // Edge Function layer; the two TERMS verbs are admin.manage — the resolved
  // terms still travel with every getSupplierPo response, so a merch user can
  // print the document without being able to change what it says.
  createSupplierPo: (args: {
    supplier_id: string;
    order_line_ids: string[];
    notes?: string;
  }) =>
    callTool<typeof args, SupplierPoDocument & { snapshot: SupplierPoSnapshot }>(
      "create_supplier_po", args,
    ),

  addSupplierPoLines: (args: { purchase_order_id: string; order_line_ids: string[] }) =>
    callTool<typeof args, SupplierPoDocument & { snapshot: SupplierPoSnapshot }>(
      "add_supplier_po_lines", args,
    ),

  removeSupplierPoLine: (args: { purchase_order_id: string; order_line_id: string }) =>
    callTool<typeof args, SupplierPoDocument & { removed_line_id: string }>(
      "remove_supplier_po_line", args,
    ),

  listSupplierPos: (
    args: {
      supplier_id?: string;
      status?: SupplierPoStatus;
      /** Matched through the blocks' snapshotted order_id as well as the legacy header link. */
      order_id?: string;
      customer_id?: string;
      /**
       * #1065: matched the same way, through production.orders.season_id of
       * the orders the blocks came from. Server-side on purpose — filtering
       * the page client-side would drop everything past the 200-row cap.
       */
      season_id?: string;
      limit?: number;
    } = {},
  ) =>
    callTool<typeof args, {
      purchase_orders: SupplierPoDocument[];
      count: number;
      total_count: number;
    }>("list_supplier_pos", args),

  getSupplierPo: (args: { id: string }) =>
    callTool<typeof args, SupplierPoDocument>("get_supplier_po", args),

  /**
   * #1065: a 5-minute signed URL for one PPO's STORED document in the
   * private supplier-pos bucket. `signed_url` is null with a reason when
   * there is nothing to sign — `not_rendered` means the PO has never been
   * rendered-and-stored, because a dry-run preview deliberately stores
   * nothing.
   */
  getSupplierPoFileUrl: (args: { id: string; kind?: "pdf" | "xlsx" }) =>
    callTool<typeof args, {
      signed_url: string | null;
      storage_key: string | null;
      filename: string | null;
      kind: string;
      reason: string | null;
      expires_in: number | null;
    }>("get_supplier_po_file_url", args),

  cancelSupplierPo: (args: { id: string; reason: string }) =>
    callTool<typeof args, SupplierPoDocument & { was_noop: boolean }>(
      "cancel_supplier_po", args,
    ),

  /**
   * #1131 (Epic #1063 phase 2c): D30's deposit TICK. `paid: true` stamps
   * `purchase_order.deposit_paid_at`, `false` clears it. A tick only — no
   * amount or payment data is modelled anywhere, by design.
   *
   * It is the last S6 -> S7 checklist item a person sets by hand, so ticking
   * it is what clears the order's `ppo_deposit_paid` missing-item badge on
   * /orders and the S6 owner's /todo row (migration 6182's rules + triggers
   * do that server-side — nothing here needs to know).
   */
  setSupplierPoDepositPaid: (args: { id: string; paid?: boolean }) =>
    callTool<typeof args, SupplierPoDocument & { was_noop: boolean }>(
      "set_supplier_po_deposit_paid", args,
    ),

  getSupplierPoTerms: (args: { supplier_id: string }) =>
    callTool<typeof args, {
      supplier_id: string;
      terms: SupplierPoTermsRow | null;
      defaults: SupplierPoTermsPatch;
      resolved: ResolvedSupplierPoTerms;
    }>("get_supplier_po_terms", args),

  setSupplierPoTerms: (args: { supplier_id: string; patch: SupplierPoTermsPatch }) =>
    callTool<typeof args, {
      supplier_id: string;
      terms: SupplierPoTermsRow;
      resolved: ResolvedSupplierPoTerms;
    }>("set_supplier_po_terms", args),

  // ── PPO amendments — revision, supersede chain and line diff (#1130,
  // Epic #1063 phase 2b; migration 6202) ──────────────────────────────────
  // A revision is a NEW purchase-order row (same po_number, status reset to
  // draft, revision_no + 1), re-snapshotted from the LIVE order lines, with
  // the previous revision's superseded_by pointing forward at it. The two
  // amendment-flag sources (2a's tech-pack-version-changed notification and
  // #1102's customer-PO-amendment flag) are both consumed by this call.
  reviseSupplierPo: (args: { purchase_order_id: string; change_notes?: string }) =>
    callTool<typeof args, SupplierPoDocument & {
      previous_purchase_order_id: string;
      revision_no: number;
      snapshot: SupplierPoSnapshot;
    }>("revise_supplier_po", args),

  listSupplierPoRevisions: (args: { purchase_order_id: string }) =>
    callTool<typeof args, SupplierPoRevisionList>("list_supplier_po_revisions", args),

  diffSupplierPoRevisions: (
    args: { purchase_order_id_a: string; purchase_order_id_b: string },
  ) =>
    callTool<typeof args, SupplierPoRevisionDiff>("diff_supplier_po_revisions", args),

  // ── Supplier PI — the record, its diff against the PPO, and the
  // per-difference accept/reject (#1208, Epic #1063 phase 3a; migration
  // 6205) ──────────────────────────────────────────────────────────────────
  // Manual entry: every figure is transcribed from the supplier's own
  // document. `differences` is recomputed server-side on every read from the
  // live PI and PPO rows — only the DECISIONS are stored, keyed on each
  // difference's stable `diff_key`. The portal never derives a diff itself.
  createSupplierPi: (args: {
    purchase_order_id: string;
    pi_number: string;
    pi_date: string;
    lines: SupplierPiLineInput[];
    tolerance_pct?: number;
    deposit_pct?: number;
    etd?: string;
    packing_notes?: string;
    notes?: string;
  }) => callTool<typeof args, SupplierPiDocument>("create_supplier_pi", args),

  editSupplierPi: (args: {
    supplier_pi_id: string;
    patch?: SupplierPiPatch;
    /** When given, REPLACES the whole line set. Omit to leave it untouched. */
    lines?: SupplierPiLineInput[];
  }) => callTool<typeof args, SupplierPiDocument>("edit_supplier_pi", args),

  getSupplierPi: (args: { supplier_pi_id: string }) =>
    callTool<typeof args, SupplierPiDocument>("get_supplier_pi", args),

  listSupplierPis: (args: {
    purchase_order_id?: string;
    status?: SupplierPiStatus;
    limit?: number;
  }) =>
    callTool<typeof args, {
      supplier_pis: SupplierPiDocument[];
      count: number;
      total_count: number;
    }>("list_supplier_pis", args),

  decideSupplierPiDifference: (args: {
    supplier_pi_id: string;
    diff_key: string;
    decision: "accepted" | "rejected";
    /** Mandatory and non-blank — a difference is never settled silently. */
    reason: string;
  }) => callTool<typeof args, SupplierPiDocument>("decide_supplier_pi_difference", args),

  receiveSupplierPi: (args: { supplier_pi_id: string }) =>
    callTool<typeof args, SupplierPiDocument & { was_noop: boolean }>(
      "receive_supplier_pi",
      args,
    ),

  // ── The AI PI reader's bridge (#1210, Epic #1063 phase 3c; migration
  // 6215) ──────────────────────────────────────────────────────────────────
  // A supplier-PI document is READ by document-extraction-worker into
  // ops.document_extractions.ai_draft, CONFIRMED field by field by a person
  // in <ConfirmExtractionPanel>, and only then RECORDED as the 3a PI record.
  // The preview exists because a supplier states THEIR style number and
  // production.supplier_pi_lines needs a PPO block: the resolver reports what
  // it cannot place rather than guessing, so a reviewer links those rows
  // first and records with the same links.
  previewSupplierPiFromExtraction: (args: {
    extraction_id: string;
    line_links?: SupplierPiLineLink[];
  }) =>
    callTool<typeof args, SupplierPiFromExtractionPreview>(
      "preview_supplier_pi_from_extraction",
      args,
    ),

  createSupplierPiFromExtraction: (args: {
    extraction_id: string;
    line_links?: SupplierPiLineLink[];
    notes?: string;
  }) =>
    callTool<typeof args, SupplierPiDocument & { extraction_id: string; was_new: boolean }>(
      "create_supplier_pi_from_extraction",
      args,
    ),
};

// ── Supplier PPO types (migration 6165 / #1064) ────────────────────────────

export type SupplierPoStatus =
  | "draft" | "sent" | "acknowledged" | "confirmed" | "cancelled";

/** One snapshotted block — a production.purchase_order_lines row. */
export interface SupplierPoLine {
  id: string;
  purchase_order_id: string;
  order_line_id: string;
  line_no: number;
  /** The customer SO this block serves, snapshotted when the block was cut. */
  order_id: string | null;
  order_po_number: string | null;
  customer_id: string | null;
  customer_name: string | null;
  customer_po_no: string | null;
  style_code: string;
  style_name: string | null;
  colour: string | null;
  qty_pcs: number | null;
  /** The realised per-size split, in pieces. Never recomputed on read. */
  sizes: Array<{ slot_position: number; label: string | null; qty: number }>;
  unit_price_usd: number | string | null;
  /** Order-level (6154); the three carton fields below are per-line. */
  packing_type: "solid" | "ratio" | null;
  supply_unit: string | null;
  /** The season's P-code pack catalogue reference (6057, not #1062). */
  packing_code: string | null;
  pieces_per_carton: number | null;
  carton_count: number | null;
  carton_count_exact: boolean | null;
  /** Phase 2a pins this at send; always null until then. */
  pinned_tech_pack_upload_id: string | null;
  deleted_at: string | null;
}

/** production.resolve_supplier_po_terms(): supplier row over global default. */
export interface ResolvedSupplierPoTerms {
  supplier_id: string | null;
  incoterm: string | null;
  inspection: string | null;
  deposit_pct: number | string | null;
  balance_terms: string | null;
  qty_tolerance_pct: number | string | null;
  bank_details: string | null;
  notes: string | null;
  /** Per field: "supplier" (an override) or "default" (inherited). */
  sources: Record<string, "supplier" | "default">;
  has_supplier_row: boolean;
  /** The single "30% deposit, 70% balance against B/L copy" line. */
  payment_terms_line: string | null;
}

/** The seven patchable fields of crm.supplier_po_terms. */
export interface SupplierPoTermsPatch {
  incoterm?: string | null;
  inspection?: string | null;
  deposit_pct?: number | null;
  balance_terms?: string | null;
  qty_tolerance_pct?: number | null;
  bank_details?: string | null;
  notes?: string | null;
}

export type SupplierPoTermsRow = SupplierPoTermsPatch & {
  id?: string;
  supplier_id?: string;
};

/** #1130: an open tech-pack / customer-PO-amendment flag on one PPO. */
export interface SupplierPoPendingAmendmentReason {
  kind: "tech_pack_amendment_needed" | "po_amendment_flag" | string;
  dedupe_key: string;
  title: string;
  body: string | null;
  created_at: string;
}

/** production.supplier_po_json(): one PPO, header + rollups + terms. */
export interface SupplierPoDocument {
  purchase_order: Record<string, unknown>;
  supplier: Record<string, unknown> | null;
  /** Empty on a list row — listSupplierPos omits the blocks by design. */
  lines: SupplierPoLine[];
  line_count: number;
  total_qty_pcs: number;
  total_value_usd: number | string;
  /** The distinct customer SO numbers this PPO's blocks span (design §2.3). */
  order_po_numbers: string[];
  customer_names: string[];
  terms: ResolvedSupplierPoTerms;
  /** #1130: why, if at all, this PPO is currently flagged for an amendment. */
  pending_amendment_reasons: SupplierPoPendingAmendmentReason[];
}

/** #1130: one row of mcp_list_supplier_po_revisions' revision history. */
export interface SupplierPoRevisionSummary {
  id: string;
  revision_no: number;
  /** True for the one revision nothing live supersedes (superseded_by is null). */
  is_current: boolean;
  status: SupplierPoStatus;
  superseded_by: string | null;
  revision_reason: string | null;
  needs_amendment_review: boolean;
  amendment_flagged_at: string | null;
  generated_at: string | null;
  sent_at: string | null;
  document_rendered_at: string | null;
  cancelled_at: string | null;
  line_count: number;
  total_qty_pcs: number;
  created_at: string | null;
  created_by: string | null;
}

export interface SupplierPoRevisionList {
  purchase_order_id: string;
  po_number: string | null;
  /** Newest revision first. */
  revisions: SupplierPoRevisionSummary[];
}

/** #1130: mcp_diff_supplier_po_revisions' field-change shape. */
export interface SupplierPoFieldChange {
  field: string;
  old: unknown;
  new: unknown;
}

export interface SupplierPoRevisionDiff {
  purchase_order_id_a: string;
  purchase_order_id_b: string;
  header_changes: SupplierPoFieldChange[];
  line_changes: {
    added: Array<Record<string, unknown>>;
    removed: Array<Record<string, unknown>>;
    changed: Array<{
      order_line_id: string;
      fields: SupplierPoFieldChange[] | null;
    }>;
  };
}

export interface SupplierPoSnapshot {
  added_line_ids: string[];
  added_count: number;
  skipped_order_line_ids: string[];
  skipped_count: number;
}

// ── Supplier PI types (migration 6205 / #1208, Epic #1063 phase 3a) ────────

/** draft = entered, not yet received. */
export type SupplierPiStatus = "draft" | "received";

/** One size entry, as stated on the PI (the PPO snapshot also carries a slot). */
export interface SupplierPiSize {
  label?: string | null;
  qty: number | string;
  slot_position?: number;
}

/** One PI line — a production.supplier_pi_lines row. */
export interface SupplierPiLine {
  id: string;
  supplier_pi_id: string;
  /** The PPO block this line reconciles against. */
  purchase_order_line_id: string;
  line_no: number;
  fob_usd?: number | string | null;
  qty_pcs?: number | null;
  /** Free text ("1:2:2:1", "S-XL") when no per-size split was given. */
  size_ratio?: string | null;
  sizes?: SupplierPiSize[];
  packing_type?: string | null;
  pieces_per_carton?: number | null;
  carton_count?: number | null;
  notes?: string | null;
}

/** What create/edit accept per line. `sizes` XOR `size_ratio` in practice. */
export interface SupplierPiLineInput {
  purchase_order_line_id: string;
  fob_usd?: number;
  qty_pcs?: number;
  size_ratio?: string;
  sizes?: Array<{ label: string; qty: number }>;
  packing_type?: "solid" | "ratio";
  pieces_per_carton?: number;
  carton_count?: number;
  notes?: string;
}

/** The seven header fields editSupplierPi will patch. */
export interface SupplierPiPatch {
  pi_number?: string;
  pi_date?: string;
  tolerance_pct?: number | null;
  deposit_pct?: number | null;
  etd?: string | null;
  packing_notes?: string | null;
  notes?: string | null;
}

export type SupplierPiDifferenceKind =
  | "price"
  | "qty_per_size"
  | "qty_total"
  | "packing"
  | "etd";

/**
 * One computed difference between the PI and its PPO.
 *
 * `diff_key` is stable across a re-computation of the same underlying delta,
 * which is both what lets a decision outlive an edit of the PI's lines and
 * the handle decideSupplierPiDifference takes.
 */
export interface SupplierPiDifference {
  diff_key: string;
  kind: SupplierPiDifferenceKind;
  purchase_order_line_id: string | null;
  order_id: string | null;
  /** The size label, for a qty_per_size difference; null otherwise. */
  label: string | null;
  /** The PPO's value. */
  old_value: unknown;
  /** The PI's value. */
  new_value: unknown;
  decision: "pending" | "accepted" | "rejected";
  reason: string | null;
  decided_at: string | null;
  decided_by: string | null;
}

/** production.supplier_pi_json(): one PI, header + lines + diff. */
export interface SupplierPiDocument {
  supplier_pi: Record<string, unknown>;
  lines: SupplierPiLine[];
  differences: SupplierPiDifference[];
  /** True when no difference is left at decision = "pending". */
  fully_settled: boolean;
}

// ── The AI PI reader's bridge (#1210, Epic #1063 phase 3c; migration 6215) ─

/**
 * "This draft row is THAT PPO block" — the explicit override for a row the
 * style match could not place. `line_no` is 1-based into the confirmed
 * `lines` array and is preferred over `style_code`: a PI can legitimately
 * carry two rows with the same style code.
 */
export interface SupplierPiLineLink {
  purchase_order_line_id: string;
  line_no?: number;
  style_code?: string;
}

/** Why one draft row could not be placed against a block of the PPO. */
export type SupplierPiUnresolvedReason =
  | "no_style_on_row"
  | "no_match"
  | "ambiguous_match"
  | "duplicate_block"
  | "link_not_a_live_block";

export interface SupplierPiUnresolvedRow {
  line_no: number;
  style_code: string | null;
  style_name: string | null;
  reason: SupplierPiUnresolvedReason;
}

/** One live block of the PPO, for the per-row picker. */
export interface SupplierPiPpoBlock {
  purchase_order_line_id: string;
  line_no: number;
  style_code: string | null;
  style_name: string | null;
  colour: string | null;
  qty_pcs: number | null;
}

/**
 * Which gate stops a confirmed draft being recorded, or null when none does.
 * Reported rather than raised, so the card can say which one it is.
 */
export type SupplierPiExtractionBlocker =
  | "not_found"
  | "not_done"
  | "not_a_purchase_order"
  | "fields_pending"
  | "ppo_not_found"
  | "ppo_cancelled";

/** public.mcp_preview_supplier_pi_from_extraction(): what the write would do. */
export interface SupplierPiFromExtractionPreview {
  blocker: SupplierPiExtractionBlocker | null;
  may_create: boolean;
  extraction_id: string;
  purchase_order_id?: string | null;
  po_number?: string | null;
  source_upload_id?: string | null;
  extraction_type?: string | null;
  status?: string | null;
  pending_fields?: string[] | null;
  /** The PI this extraction already produced — recording again returns it. */
  already_recorded?: string | null;
  /** A live PI on this PPO from another source: recording is refused. */
  ppo_has_other_pi?: string | null;
  header?: {
    pi_number: string | null;
    pi_date: string | null;
    tolerance_pct: number | null;
    deposit_pct: number | null;
    etd: string | null;
    packing_notes: string | null;
    fob_usd: number | null;
  } | null;
  lines?: SupplierPiLineInput[] | null;
  unresolved?: SupplierPiUnresolvedRow[] | null;
  ppo_blocks?: SupplierPiPpoBlock[] | null;
}

// ── Multi-order shipments (#1073, Epic #1072 phase 1a; migration 6172) ─────
// A shipment carries MANY customer orders (design §5.12), each optionally
// with part quantities of its lines. production.shipment_json() builds the
// one read projection every write RPC and both reads share.

export type ShipmentStatus =
  | "planned" | "booked" | "in_transit" | "arrived" | "cleared"
  | "delivered" | "accepted" | "disputed" | "rejected" | "cancelled";

/**
 * The subset edit_shipment's field patch may move the status to — and the
 * subset the shipment must ALREADY be in for the patch to carry a status at
 * all. A delivered / accepted / disputed / rejected shipment cannot be patched
 * back into the shipping half (it would reset delivery_status to `pending` on
 * every linked order and leave the delivery dates stale); omit `status` to
 * patch its other fields.
 */
export type ShipmentShippingStatus =
  | "planned" | "booked" | "in_transit" | "arrived" | "cleared";

/** The four statuses record_delivery records. */
export type ShipmentDeliveryStatus =
  "delivered" | "accepted" | "disputed" | "rejected";

/**
 * The six record_delivery fields a caller may RETRACT by naming them in
 * `clear`. Mirrors CLEARABLE_DELIVERY_FIELDS in
 * cc-os-mcp/lib/shipment-shapes.ts and p_clear's whitelist in
 * public.mcp_record_delivery. The three GRN / DC-booking fields joined this
 * list in migration 6194 (#1178).
 */
export type ClearableDeliveryField =
  | "accepted_date"
  | "discrepancies"
  | "delivery_proof_url"
  | "grn_number"
  | "dc_booking_number"
  | "dc_booking_date";

/**
 * One received-qty / discrepancy row sent to record_delivery's
 * `received_lines` (#1178). Mirrors deliveryLineInputSchema in
 * cc-os-mcp/lib/shipment-shapes.ts.
 */
export interface DeliveryLineInput {
  order_line_id: string;
  received_qty?: number | null;
  discrepancy_reason?: string | null;
}

/**
 * One stored received-qty / discrepancy row, joined to its order line
 * (production.shipment_delivery_lines, #1178).
 */
export interface ShipmentDeliveryLine {
  id: string;
  order_line_id: string;
  order_id: string | null;
  line_no: number | null;
  style_code: string | null;
  colour: string | null;
  ordered_qty_pcs: number | null;
  received_qty: number | null;
  discrepancy_reason: string | null;
  discrepancy_resolution_state: "open" | "resolved" | null;
  discrepancy_resolution_reason: string | null;
  discrepancy_resolved_at: string | null;
}

/** One order the shipment carries, as production.shipment_json() renders it. */
export interface ShipmentOrder {
  shipment_order_id: string;
  order_id: string;
  po_number: string | null;
  display_id: string | null;
  status: string | null;
  delivery_status: string | null;
  customer_id: string | null;
  customer_name: string | null;
  supplier_id: string | null;
  supplier_name: string | null;
  season_id: string | null;
  notes: string | null;
}

/** One stored part-shipment row, joined to its order line. */
export interface ShipmentOrderLine {
  id: string;
  order_line_id: string;
  order_id: string | null;
  line_no: number | null;
  style_code: string | null;
  style_name: string | null;
  colour: string | null;
  ordered_qty_pcs: number | null;
  qty_pcs: number | null;
  cartons: number | null;
  notes: string | null;
}

/**
 * production.shipment_json() — one shipment, header + orders + lines.
 *
 * `order` and `tracking_events` are OPTIONAL because they are not part of that
 * projection: public.mcp_get_shipment adds them on top, and the six write
 * verbs (which return shipment_json verbatim) do not. Declaring them required
 * made every write verb's return type claim fields that are never there.
 */
export interface ShipmentDocument {
  shipment: Record<string, unknown> | null;
  /**
   * LEGACY single link (production.shipments.order_id), from get_shipment
   * only. Prefer `orders`.
   */
  order?: Record<string, unknown> | null;
  orders: ShipmentOrder[];
  order_lines: ShipmentOrderLine[];
  /** Received-qty / discrepancy rows (#1178, migration 6194). */
  delivery_lines?: ShipmentDeliveryLine[];
  order_count: number;
  /** The distinct customer SO numbers this shipment carries (design §5.12). */
  order_po_numbers: string[];
  /** The distinct customer names this shipment carries. */
  customer_names: string[];
  shipping_mark_approved: boolean;
  /** The last 50 audit events on the shipment row, from get_shipment only. */
  tracking_events?: Array<Record<string, unknown>>;
}

/**
 * What `applyShipmentFromExtraction` returns (#1212, Epic #1072 phase 3b,
 * migration 6216): the shipment projection plus what the call actually did.
 *
 * `was_new` is false when this document had already been applied — one
 * confirmed document is one application, however many times the button is
 * pressed. `skipped_fields` names every confirmed field a shipment has no
 * column for (the invoice number, the consignee, the two line tables, the
 * reader's own notes); they are reported, not written.
 */
export interface ShipmentFromExtraction extends ShipmentDocument {
  extraction_id: string;
  was_new: boolean;
  created_shipment: boolean;
  applied_fields: Record<string, unknown>;
  skipped_fields: string[];
  /** job_target_shipment | upload_scope_order | … — how the shipment was found. */
  resolved_from: string;
  /** >1 means a part shipment: the newest of several live shipments was used. */
  shipment_candidate_count: number;
}

/** One part-shipment input row: how much of an order line travels on this shipment. */
export interface ShipmentLineInput {
  order_line_id: string;
  qty_pcs?: number | null;
  cartons?: number | null;
  notes?: string | null;
}

/**
 * The whitelisted shipping-field patch create_shipment / edit_shipment
 * accept. Every field is nullable: an explicit null CLEARS the column, while
 * omitting the key leaves it alone. Mirrors shipmentFieldsSchema in
 * cc-os-mcp/lib/shipment-shapes.ts.
 */
export interface ShipmentFieldsPatch {
  vessel_name?: string | null;
  bl_number?: string | null;
  awb_number?: string | null;
  container_number?: string | null;
  container_size?: string | null;
  etd?: string | null;
  eta?: string | null;
  actual_departure?: string | null;
  actual_arrival?: string | null;
  port_of_loading?: string | null;
  port_of_discharge?: string | null;
  cartons?: number | null;
  cbm?: number | null;
  gross_weight_kg?: number | null;
  net_weight_kg?: number | null;
  freight_usd?: number | null;
  duties_zar?: number | null;
  clearing_zar?: number | null;
  last_mile_zar?: number | null;
  landed_total_zar?: number | null;
  tracking_url?: string | null;
  shipping_mark_upload_id?: string | null;
  forwarder_party_id?: string | null;
  status?: ShipmentShippingStatus;
  notes?: string | null;
}

/** The add/skip summary production.link_shipment_orders() returns. */
export interface ShipmentOrdersSummary {
  added_order_ids: string[];
  added_count: number;
  skipped_order_ids: string[];
  skipped_count: number;
}

/** The summary production.set_shipment_lines() returns. */
export interface ShipmentLinesSummary {
  line_count: number;
  changed: boolean;
}

// Alias scope/source vocabulary — mirrors ALIAS_SCOPES/ALIAS_SOURCES in
// supabase/functions/cc-os-mcp/lib/brand-model.ts (migration 6076).
export type AliasScope = "customer" | "brand" | "buyer" | "label" | "channel";
export type AliasSource = "planning" | "tminus" | "briefed" | "any";

// ── Size model types (6057 / #595, #648, #673) ─────────────────────────────
// See production.size_sets / size_set_members / size_curves /
// size_curve_points / size_prepacks / size_prepack_slots in migration
// 6057_size_model.sql. `ratio` is NEVER a stored field anywhere here — it is
// always weight / Σ weight, derived server-side by
// production.size_curve_ratios() (D2) — do not add a client-computed ratio
// that could drift from it; use `ratio` from SizeCurveRatioPoint verbatim.

export interface SizeCurveRatioPoint {
  slot_position: number;
  label: string | null;
  weight: number;
  /** weight / Σ weight, 6dp. NULL when every weight in the curve is zero. */
  ratio: number | null;
}

export interface SizeCurveRow {
  id: string;
  code: string;
  name: string | null;
  is_active: boolean;
  /** Points with their DERIVED ratio — never a stored value (D2). */
  points: SizeCurveRatioPoint[];
}

export interface SizePrepackTotals {
  prepack_id: string;
  code: string;
  /** Pack total AS WRITTEN on the sheet (Matrix col W). Null if never declared. */
  declared_total: number | null;
  /** Σ slot qty, computed from size_prepack_slots. */
  computed_total: number;
  /** declared_total === computed_total, or null when nothing was declared. */
  matches: boolean | null;
}

export interface SizePrepackSlot {
  slot_position: number;
  qty: number;
}

export interface SizePrepackRow {
  id: string;
  code: string;
  name: string | null;
  is_active: boolean;
  totals: SizePrepackTotals;
  slots: SizePrepackSlot[];
}

export interface SizeSetRecord {
  id: string;
  season_code: string;
  code: string;
  name: string | null;
  description: string | null;
  source: string;
  is_active: boolean;
  notes: string | null;
}

export interface SizeSetDetail {
  size_set: SizeSetRecord | null;
  members: SizeSetMember[];
  curves: SizeCurveRow[];
  /** Writable via mcp.upsertSizePrepack (migration 6084 / #681). */
  prepacks: SizePrepackRow[];
}

/**
 * One entry in the PACKS/SINGLES packing-instruction catalogue
 * (production.packing_instructions, migration 6057). Free-standing —
 * keyed by (season_code, code), NOT nested under a size set. Read/write via
 * mcp.listPackingInstructions / mcp.upsertPackingInstruction (migration
 * 6084 / #681).
 */
export interface PackingInstructionRow {
  id: string;
  season_code: string;
  code: string;
  label: string | null;
  description: string | null;
  /** Free text; documented vocabulary is packs | singles | cancelled | unknown (6057 D6). */
  unit_kind: string;
  is_active: boolean;
}

// ── Per-customer/category size-curve templates (#1062, design D22; migration
// 6154) ──────────────────────────────────────────────────────────────────
// production.size_curve_templates: "for this size set, customer X, category
// C, pre-fill from curve Y". NULL customer_id/category means "any",
// mirroring production.size_set_assignments' own convention.

export interface SizeCurveTemplateRow {
  id: string;
  size_set_id: string;
  size_set_code: string;
  season_code: string;
  size_curve_id: string;
  size_curve_code: string;
  customer_id: string | null;
  customer_name: string | null;
  category: string | null;
  priority: number;
  notes: string | null;
}

// ── Solid size-to-carton packing + brief/template pre-fill (#1062, design
// D22/D23; migration 6154) ─────────────────────────────────────────────────

export interface PrefillOrderLineSizesOutput {
  order_line_id: string;
  /** Which input decided the split: the line's brief, a resolved template, or no usable source. */
  source: "brief" | "template" | "none";
  applied: boolean;
  /** Set only when source = "none" (nothing applied) or useful context otherwise. */
  reason?: string;
  size_curve_id?: string | null;
  reconciliation?: SizeReconciliation;
}

// ── Critical Path types (W7.2 / W7.3) ─────────────────────────────────────
// Universal critical path: catalog of milestone definitions, per-order-type
// templates, per-order milestone instances, and append-only approval ledger.
// See bot-deploy/cc-os/docs/migration-plans/2026-05-04-universal-critical-
// path-and-admin-crud.md for the full data model.

// A staff.roles.name value. Free-form since #619 (mirrors RolePicker's
// `Role` type) — roles are rows in staff.roles (#612), not a fixed union;
// validate against the registry (useRoles()) at the UI layer, or rely on the
// FK the 6050_ migration added onto default_owner_role / owner_role_override
// for the ultimate enforcement.
export type CatalogOwnerRole = string;

export interface MilestoneCatalogRow {
  id: string;
  name: string;
  label: string;
  description: string | null;
  default_owner_role: CatalogOwnerRole;
  side_effects: Record<string, unknown>;
  default_color: string | null;
  default_sequence: number;
  archived_at: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface OrderTypeRow {
  id: string;
  slug: string;
  label: string;
  description: string | null;
  is_default: boolean;
  display_order: number;
  archived_at: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface OrderTypeTemplateRow {
  /** Order type fields (joined). */
  order_type_id: string;
  order_type_slug: string;
  order_type_label: string;
  order_type_display_order: number;
  /** Template-row fields. */
  template_id: string;
  sequence: number;
  required: boolean;
  owner_role_override: CatalogOwnerRole | null;
  /** Catalog fields (joined — flat property names per the SQL view). */
  milestone_catalog_id: string;
  milestone_name: string;
  milestone_label: string;
  milestone_description: string | null;
  default_owner_role: CatalogOwnerRole;
  effective_owner_role: CatalogOwnerRole;
  side_effects: Record<string, unknown>;
  default_color: string | null;
  default_sequence: number;
}

export type CriticalPathStatus =
  | "pending" | "at_risk" | "late" | "done" | "skipped"
  | "approved" | "unapproved";

export interface CriticalPathMilestone {
  submitted_date?: string | null;
  approved_date?: string | null;
  /**
   * The milestone's resolved owner label (#1067): production.milestones.owner
   * for a real row, or what a ghost row would resolve to. A staff display_name
   * when a stage owner is configured in Settings, else the effective owner
   * role name.
   */
  owner?: string | null;
  /**
   * Whether the signed-in caller may act on (and date) this milestone —
   * production.actor_may_approve_milestone, the SAME predicate the write gate
   * uses: admin.manage, the effective owner role, or the stage owner from
   * Settings. Never re-derive this client-side (#1067).
   */
  can_set_dates?: boolean;
  note?: string | null;
  variance_days?: number | null;
  timeline_active?: boolean;
  shutdown_days?: number;
  blocked?: boolean;
  /** Null when this is a template ghost row (no milestone instance yet). */
  milestone_id: string | null;
  catalog_id: string;
  name: string;
  label: string;
  sequence: number;
  owner_role: CatalogOwnerRole;
  status: CriticalPathStatus | null;
  target_date: string | null;
  actual_date: string | null;
  notes: string | null;
  required: boolean;
  color: string | null;
  side_effects?: Record<string, unknown>;
  /** Latest approval entry from the production.milestone_approvals ledger. */
  latest_approval: {
    id: string;
    action: "approve" | "unapprove" | "reset";
    actor_id: string | null;
    note: string | null;
    occurred_at: string;
  } | null;
}

export interface OrderCriticalPath {
  order: {
    criticality?: string | null;
    criticality_override?: string | null;
    criticality_computed?: string | null;
    float_days?: number | null;
    timeline_state?: string;
    first_unreachable_milestone?: string | null;
    timeline_region?: string;
    timeline_region_assumed?: boolean;
    production_start_override?: string | null;
    resub_needed?: boolean | null;
    resub_buffer_days?: number | null;
    confirmation_date?: string | null;
    customer_required_date?: string | null;
    production_days?: number | null;
    shipping_lead_time?: number | null;
    customs_clearance?: number | null;
    processing_time?: number | null;
    processing_to_customer_time?: number | null;
    /**
     * The floor an owner-entered approval date is validated against (#1067):
     * the order's earliest known date (created_at, or confirmation_date when
     * that is earlier). Used as the date picker's `min`.
     */
    earliest_dateable?: string | null;
    id: string;
    po_number: string | null;
    status: OrderStatus | null;
    customer_id: string | null;
    supplier_id: string | null;
    target_ex_factory: string | null;
    order_type_id: string | null;
    order_type_slug: string | null;
    order_type_label: string | null;
  } | null;
  milestones: CriticalPathMilestone[];
}

// ── Tags types (5700/5701) ────────────────────────────────────────────────
// Linear-style 12-colour palette. Mirrors samples.tag_color enum.
export type TagColor =
  | "red" | "orange" | "yellow" | "lime" | "green" | "mint"
  | "cyan" | "blue" | "indigo" | "violet" | "pink" | "slate";

// Tag category (mirrors samples.tag_category enum). The DB derives `color`
// from `category` server-side, so the UI now picks the category and the
// colour follows. Keep TagColor around because chips still render via
// `tag.color`.
export type TagCategory =
  | "fabric" | "construction" | "silhouette" | "detail" | "decoration" | "other";

/** Category → derived TagColor. Mirrors the SQL mapping. */
export const TAG_CATEGORY_COLOR: Record<TagCategory, TagColor> = {
  fabric: "blue",
  construction: "slate",
  silhouette: "indigo",
  detail: "green",
  decoration: "pink",
  other: "orange",
};

/** Pretty label for each category, used in dropdowns and section headers. */
export const TAG_CATEGORY_LABEL: Record<TagCategory, string> = {
  fabric: "Fabric",
  construction: "Construction",
  silhouette: "Silhouette",
  detail: "Detail",
  decoration: "Decoration",
  other: "Other",
};

/** Stable iteration order for categories (used everywhere we render a list). */
export const TAG_CATEGORIES: TagCategory[] = [
  "fabric", "construction", "silhouette", "detail", "decoration", "other",
];

export interface Tag {
  id: string;
  name: string;
  color: TagColor;
  category: TagCategory;
  description: string | null;
  /** Combined count across library_item_tags + stock_offer_tags. */
  usage_count?: number;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
  updated_by?: string | null;
  agent_id?: string | null;
}

// ── Briefs types (5961-5964) ─────────────────────────────────────────────
// ONE BRIEF = ONE SAMPLE (5964): a brief now carries its single sample's
// source, requested changes, pricing and origin directly — the former
// brief_items detail table is gone, and the brief's `status` doubles as the
// per-sample outcome. See the create_brief / update_brief / get_brief verbs for
// the authoritative field shapes.

/**
 * The brief lifecycle. `costed` (migration 6166, #1092) is design §4's S4 and
 * sits between `briefed` and `won`: `draft → briefed → costed → won`, with
 * `lost` / `cancelled` reachable as before. Two of those steps are gated
 * server-side by the completeness checklist (S2→S3 and S3→S4) and `won` only
 * by a backing order — see <AdvanceButton> for the client half.
 */
export type BriefStatus =
  | "draft" | "briefed" | "costed" | "won" | "lost" | "cancelled";

/**
 * Every value samples.briefs.origin can hold (migration 6117).
 * `imported` (6073) and `pending` (6117) are SYSTEM-ONLY: written by
 * ops.promote_briefed_run and create_briefs_from_meeting respectively, and not
 * accepted by mcp_create_brief / mcp_update_brief — see BriefSettableOrigin.
 * `pending` = the meeting row carried a note or a target price but no decision.
 */
export type BriefOrigin = "kept" | "cutting" | "manual" | "imported" | "pending";

/** The subset a person may set through createBrief / updateBrief. */
export type BriefSettableOrigin = Exclude<BriefOrigin, "imported" | "pending">;

/**
 * One thing create_briefs_from_meeting could not do cleanly, per (sample,
 * buyer) group. `code` is stable and machine-readable
 * (`buyer_customer_mismatch`, `origin_pending_unavailable`); `message` is the
 * sentence to show. Scoped-out buyers are NOT reported — that skip is silent
 * by design (#537).
 */
export interface BriefFromMeetingWarning {
  sample_id: string;
  buyer: string;
  code: string;
  message: string;
}

/** The 6 change categories a buyer can request against a source sample. */
export type BriefChangeType =
  | "colour" | "fabric" | "styling" | "price" | "measurement" | "other";

export const BRIEF_CHANGE_TYPES: BriefChangeType[] = [
  "colour", "fabric", "styling", "price", "measurement", "other",
];

/** One requested change on a brief (jsonb {type, detail}). */
export interface BriefChange {
  type: BriefChangeType;
  detail: string;
}

/**
 * Point-in-time snapshot of the source colourway, captured at brief time so the
 * brief still reads sensibly after the source is edited / deleted. Shape mirrors
 * supabase/functions/cc-os-mcp/lib/brief-source.ts → captureSource().
 */
export interface BriefSourceSnapshot {
  id?: string | null;
  sample_id?: string | null;
  library_code?: string | null;
  style_code?: string | null;
  style_name?: string | null;
  colour?: string | null;
  size?: string | null;
  category?: string | null;
  pantone_code?: string | null;
  /** Colourway's supplier, falling back to the parent style's. */
  supplier_id?: string | null;
  /** Colourway minimum order quantity (no style-level equivalent). */
  moq?: number | null;
  /** Colourway tech pack, falling back to the parent style's. */
  tech_pack_url?: string | null;
  supplier_dollar_price?: number | null;
  factor?: number | null;
  rand_price?: number | null;
  hero_image_url?: string | null;
  captured_at?: string | null;
  /**
   * Only on briefs created by create_briefs_from_meeting: which
   * samples.meeting_buyer_samples rows the brief came from, and what they
   * said. Written once and never rewritten.
   */
  meeting?: {
    meeting_buyer_sample_ids?: string[];
    status?: BriefOrigin | null;
    note?: string | null;
    target_price?: number | null;
    captured_at?: string | null;
  } | null;
  /**
   * Only on briefs imported by the Briefed sheet ingest (ops.promote_briefed_run,
   * migration 6086+). Sheet-side context the ingest captured alongside the
   * snapshot's top-level style/price fields — read SERVER-side by
   * preview_order_lines_from_briefs (#1101) to pre-fill the order composer's
   * season/qty/ship-date/supplier fields; the portal's own copy of that
   * derivation was deleted in #1128. See
   * supabase/functions/cc-os-mcp/lib/brief-source.ts and migration
   * 6123_briefed_ingest_selling_price.sql for the authoritative key list —
   * only the keys #916 actually reads are typed here.
   */
  ingest?: {
    /** Sheet's SEASON column, matched case-insensitively against SeasonOption.code. */
    season?: string | null;
    /** Sheet's SUPPLIER column, raw text (not resolved to a crm.suppliers.id). */
    supplier_raw?: string | null;
    /** Sheet's REQ SHIPPING DATE column, YYYY-MM-DD when present. */
    req_shipping_date?: string | null;
    /** Sheet's TOTAL QTY column. */
    total_qty?: number | null;
    /** Sheet's UNITS ORDERED column — qty fallback when total_qty is blank. */
    units_ordered?: number | null;
  } | null;
  [key: string]: unknown;
}

/**
 * One point of a brief's size curve — relative weights per size, mirroring
 * production.size_curve_points (no ratio is stored, so the weights are
 * relative). Validated server-side by samples.brief_size_curve_is_valid
 * (migration 6116): a non-empty array of objects with EXACTLY {size, weight},
 * size non-empty and unique across the array, weight a number >= 0.
 */
export interface BriefSizeCurvePoint {
  size: string;
  weight: number;
}

/**
 * The 18 typed brief-spec columns (migration 6116_brief_v2_spec_columns) —
 * "a brief is one style in one colour". Mirrors BRIEF_SPEC_KEYS in
 * supabase/functions/cc-os-mcp/lib/brief-spec.ts, which is the authority for
 * the per-column validation; every one is nullable and every one is backed by
 * a named DB CHECK.
 */
export interface BriefSpecFields {
  colour_name: string | null;
  /** Non-blank when set (briefs_colour_code_not_blank). */
  colour_code: string | null;
  /** FK to production.seasons(id). */
  season_id: string | null;
  /** The requested drop/delivery MONTH — always a YYYY-MM-01 date. */
  delivery_month: string | null;
  target_units_min: number | null;
  target_units_max: number | null;
  target_price: number | null;
  /** 3-letter uppercase ISO code; required by the DB whenever target_price is set. */
  target_price_currency: string | null;
  fabric: string | null;
  composition: string | null;
  block_fit: string | null;
  size_range: string | null;
  trims_notes: string | null;
  fabric_weight_gsm: number | null;
  size_curve: BriefSizeCurvePoint[] | null;
  costing_due_date: string | null;
  labdip_due_date: string | null;
  sample_due_date: string | null;
}

/**
 * The style-identity columns (migration 6118_style_identity_split). The first
 * three are settable; `style_key` is entirely trigger-derived
 * (samples.tg_briefs_style_identity) and MATCH-ONLY — never editable, and
 * never accepted by create_brief / update_brief.
 */
export interface BriefStyleIdentityFields {
  customer_style_no: string | null;
  /** Matches ^S-[0-9]{4}-[0-9]{3,}$ (briefs_cc_sample_no_format). */
  cc_sample_no: string | null;
  supplier_style_code: string | null;
  /** Read-only, trigger-derived normalized parent-style match key. */
  style_key: string | null;
}

/** The style-identity columns a caller may actually set — style_key never is. */
type SettableBriefStyleIdentityFields = Omit<BriefStyleIdentityFields, "style_key">;

/**
 * createBrief's spec args — plain optional, mirroring briefSpecCreateShape: a
 * spec-less create must never NAME any of these keys (the verb only includes a
 * key when `args[key] !== undefined`), so there is no `null` here.
 */
export type BriefSpecCreateArgs = {
  [K in keyof BriefSpecFields]?: NonNullable<BriefSpecFields[K]>;
};

/**
 * updateBrief's spec args — nullable + optional, mirroring
 * briefSpecUpdateShape: omit a key to leave the column untouched, pass an
 * explicit `null` to clear it.
 */
export type BriefSpecUpdateArgs = {
  [K in keyof BriefSpecFields]?: BriefSpecFields[K];
};

/** createBrief's style-identity args (plain optional; style_key excluded). */
export type BriefStyleIdentityCreateArgs = {
  [K in keyof SettableBriefStyleIdentityFields]?: NonNullable<
    SettableBriefStyleIdentityFields[K]
  >;
};

/** updateBrief's style-identity args (null clears; style_key excluded). */
export type BriefStyleIdentityUpdateArgs = {
  [K in keyof SettableBriefStyleIdentityFields]?: SettableBriefStyleIdentityFields[K];
};

export interface Brief
  extends AuditFields, Partial<BriefSpecFields>, Partial<BriefStyleIdentityFields> {
  id: string;
  /** Human-readable BRF-YYYY-NNNN id (DB trigger). */
  display_id: string;
  title: string;
  /** Doubles as the per-sample outcome (draft → briefed → costed → won / lost / cancelled). */
  status: BriefStatus;
  customer_id: string | null;
  buyer_name: string | null;
  meeting_id: string | null;
  notes: string | null;
  // ── The brief's single sample (folded from the former brief_item) ──
  /** FK to the source colourway; null once the source is deleted (SET NULL). */
  source_library_item_id: string | null;
  source_snapshot: BriefSourceSnapshot | null;
  changes: BriefChange[];
  new_supplier_dollar_price: number | null;
  /** The SELLING MULTIPLE (customer rand per supplier USD, e.g. ×40) — not the landed-cost factor (production.style_costings.factor / .landed_factor). The DB also exposes this value under the unambiguous name samples.briefs.selling_multiple (issue #921), not yet threaded through this verb's column list. */
  new_factor: number | null;
  /** GENERATED = new_supplier_dollar_price × new_factor. Read-only. */
  new_rand_price: number | null;
  /** Manual ZAR override; when set it wins over new_rand_price. */
  final_price_zar: number | null;
  origin: BriefOrigin;
  // ── Brand / buyer / label (migration 6077, Epic #740 Phase 3b) ──
  /** crm.brands.id — the customer's brand this brief is for. */
  brand_id?: string | null;
  /** crm.customer_buyers.id — the CRM buyer record (buyer_name stays the free-text fallback). */
  buyer_id?: string | null;
  /** production.labels.id — what the goods carry when it is not the customer's own brand. */
  label_id?: string | null;
  /** The S1 opportunity (samples.meeting_buyer_samples row) this brief originated from. Optional FK, ON DELETE SET NULL (#923). */
  origin_opportunity_id?: string | null;
  /** crm.suppliers.id — written by accept_quote from the brief's accepted quote, alongside new_supplier_dollar_price (the FOB). Null until a quote is accepted (#1055, ruling D12). */
  supplier_id?: string | null;
  /** Set by mcp_create_order (#465) when this brief is converted to an order. */
  converted_order_id?: string | null;
  /** Flow badge (#984) — missing-item count + days in status. Read with rowBadge(). */
  badge?: RowBadge | null;
  created_at: string;
  created_by?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
  deleted_at?: string | null;
  [key: string]: unknown;
}

// ── Supplier quotes on a brief (#1055, migration 6157) ──────────────────────

/** crm.supplier_quotes.status. */
export type QuoteStatus = "open" | "accepted" | "superseded" | "expired" | "rejected";

/**
 * One list_quotes row. Shaped by public.mcp_list_quotes's column list, which
 * migration 6157 widened with brief_id, moq, lead_time_days and superseded_by
 * so the brief page's Quotes panel can render a brief's quotes, mark the
 * accepted one, and say which quote superseded each of the others.
 */
export interface BriefQuote {
  id: string;
  supplier_id: string | null;
  customer_id: string | null;
  season_id: string | null;
  style_code: string | null;
  description: string | null;
  qty_pcs: number | null;
  /** The FOB. USD is the only currency an accepted quote may carry (ruling D4). */
  unit_price_usd: number | null;
  unit_price_zar: number | null;
  status: QuoteStatus;
  quote_date: string | null;
  expiry_date: string | null;
  /** samples.briefs.id this quote was given against, when any. */
  brief_id: string | null;
  moq: number | null;
  lead_time_days: number | null;
  /** The quote that replaced this one, set by accept_quote's supersede sweep. */
  superseded_by: string | null;
  created_at: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
  agent_id?: string | null;
  [key: string]: unknown;
}

/**
 * list_briefs row — same shape as a Brief (ONE BRIEF = ONE SAMPLE, so there is
 * no items roll-up). Kept as a named alias so the list views read clearly.
 */
export type BriefListRow = Brief;

// ── Brief <-> S1 opportunity matcher (#963, migration 6137) ─────────────────

/** samples.meeting_buyer_samples.status. */
export type OpportunityStatus = "pending" | "kept" | "returned" | "declined" | "cutting";

/**
 * The S1 opportunity a brief originated from (or a suggested candidate),
 * returned by get_brief (origin_opportunity, when origin_opportunity_id is
 * set) and suggest_brief_opportunities (one per candidate).
 */
export interface BriefOpportunity {
  /** samples.meeting_buyer_samples.id — this IS origin_opportunity_id once linked. */
  id?: string;
  /** Alias used by suggest_brief_opportunities's rows. Same value as `id`. */
  opportunity_id?: string;
  meeting_id: string | null;
  /** Human-readable MTG-YYYY-NNNN, joined in server-side (get_brief / suggest). */
  meeting_display_id: string | null;
  /** Only present on a suggest_brief_opportunities row. */
  meeting_date?: string | null;
  buyer_name: string | null;
  buyer_id: string | null;
  status: OpportunityStatus;
  notes: string | null;
  target_price: number | null;
  /** Only present on a suggest_brief_opportunities row. */
  score?: number;
  /** Only present on a suggest_brief_opportunities row. */
  reasons?: string[];
  /** Only present on a suggest_brief_opportunities row. */
  already_linked_briefs?: number;
}

// ── Brief measurement spec + fit/PP sessions (#1274, Epic #1272 phase 1b,
// migration 6220) — the "Spec & fits" tab. ────────────────────────────────

/**
 * production.brief_spec_points.source — aliased from the generated manifest
 * rather than re-typed here, so the CHECK constraint stays the single source
 * of truth (the same thing `OpsCompletenessRulesCheckKind` does above).
 */
export type BriefSpecPointSource = ProductionBriefSpecPointsSource;

/** One POM on a brief's live measurement spec. */
export interface BriefSpecPoint {
  id: string;
  brief_id: string;
  pom_code: string;
  description: string | null;
  tol_minus: number | null;
  tol_plus: number | null;
  unit: string;
  /** Size label -> target value, e.g. {"S": 50.5, "M": 52, "L": 54}. */
  values: Record<string, number | null>;
  sort_order: number;
  source: BriefSpecPointSource;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** setBriefSpecPoints' input shape for one POM — the complete list is resent every call. */
export interface BriefSpecPointInput {
  pom_code: string;
  description?: string;
  tol_minus?: number;
  tol_plus?: number;
  unit?: string;
  values?: Record<string, number | null>;
  sort_order?: number;
  notes?: string;
}

export interface BriefSpecPointsList {
  brief_id: string;
  spec_points: BriefSpecPoint[];
  count: number;
}

/** production.fit_sessions.stage — aliased from the generated manifest. */
export type FitSessionStage = ProductionFitSessionsStage;

/** production.fit_sessions.decision — aliased from the generated manifest. */
export type FitSessionDecision = ProductionFitSessionsDecision;

/** One fit / PP session header. */
export interface FitSessionHeader {
  id: string;
  brief_id: string;
  sample_id: string | null;
  stage: FitSessionStage;
  session_date: string;
  size_measured: string;
  decision: FitSessionDecision;
  general_comments: string | null;
  next_sample_due: string | null;
  notes: string | null;
  is_current?: boolean;
  superseded_by?: string | null;
  created_at: string | null;
  created_by: string | null;
}

export interface FitSessionsList {
  brief_id: string;
  fit_sessions: FitSessionHeader[];
}

/** createFitSession's input shape for one measurement. */
export interface FitMeasurementInput {
  spec_point_id: string;
  measured?: number;
  comment?: string;
}

/**
 * One recorded measurement, joined with its POM for display. `target` /
 * `tol_minus` / `tol_plus` are the values DENORMALISED at write time — a
 * later spec revision does not change what this row reports.
 * `out_of_tolerance` is null (not false) when `measured` or `target` is null.
 */
export interface FitMeasurement {
  id: string;
  spec_point_id: string;
  pom_code: string;
  description: string | null;
  unit: string;
  target: number | null;
  tol_minus: number | null;
  tol_plus: number | null;
  measured: number | null;
  deviation: number | null;
  out_of_tolerance: boolean | null;
  comment: string | null;
}

export interface FitSessionDetail {
  fit_session: FitSessionHeader;
  measurements: FitMeasurement[];
}

// ── Brief completeness checklist (#964, Epic #957 Phase 1b) ─────────────────
// get_brief_missing_items's read of #944's ops.missing_items — the items
// still missing for the brief's NEXT transition(s) out of its current stage,
// plus the most recent sign-off override (if any) that landed it there.

export type BriefMissingItemLevel = "required" | "recommended";

export interface BriefMissingItem {
  /** Stable key (ops.completeness_rules.rule_key) — drives the field link. */
  rule_key: string;
  label: string;
  level: BriefMissingItemLevel;
  /** The stage this item blocks reaching (ops.completeness_rules.stage_to). */
  stage: string;
  stage_from: string;
  sort_order: number;
}

/** The audited sign-off override (ops.completeness_overrides) that waived the
 *  items above to land the brief in its CURRENT status — null when it got
 *  there without one. */
export interface BriefMissingItemsOverride {
  stage_from: string;
  stage_to: string;
  reason: string;
  created_at: string;
  missing_keys: string[];
  missing_labels: string[];
}

export interface BriefMissingItemsResult {
  items: BriefMissingItem[];
  overridden: BriefMissingItemsOverride | null;
}

// ── Order completeness checklist (#1103, Epic #1060 Phase 2c) ───────────────
// get_order_missing_items' read of the SAME ops.missing_items evaluator the
// brief panel uses. One extra field: `owner_stage`. An order at `confirmed`
// carries TWO checklists on one status move — S5 → S6 (migration 6191, owner
// s5_customer_order) and S6 → S7 (migration 6182, owner s6_supplier_po) —
// because S6 has no order status of its own, and owner_stage is what tells
// them apart.

export interface OrderMissingItem {
  /** Stable key (ops.completeness_rules.rule_key). */
  rule_key: string;
  label: string;
  level: BriefMissingItemLevel;
  /** The stage this item blocks reaching (ops.completeness_rules.stage_to). */
  stage: string;
  stage_from: string;
  /** ops.completeness_rules.owner_stage — the sN_ key this item belongs to. */
  owner_stage: string | null;
  sort_order: number;
}

export interface OrderMissingItemsResult {
  order_id: string;
  /** null when the order does not exist, is soft-deleted, or is out of a
   *  customer-scoped caller's mapping — all three answer identically. */
  status: string | null;
  items: OrderMissingItem[];
  overridden: BriefMissingItemsOverride | null;
}

// ── Shipment completeness checklist (#1179, Epic #1072 Phase 2b) ────────────
// get_shipment_missing_items' read of the SAME ops.missing_items evaluator the
// brief and order panels use. Design §6.2 gives a shipment TWO checklists with
// two different owners — S9 → S10 (packing list, shipping mark, ETD, BL/AWB,
// ETA; owner s9_shipped) and S10 → Closed (DC booking, delivered date, GRN,
// quantities received, discrepancies resolved, POD; owner s10_delivered) — so
// owner_stage carries the same weight it does on the order side.
//
// There is deliberately NO `overridden` field: nothing gates a shipment status
// move (migration 6203 reports, it does not enforce), so no shipment override
// RPC exists and ops.completeness_overrides never holds an entity='shipment'
// row.

export interface ShipmentMissingItem {
  /** Stable key (ops.completeness_rules.rule_key). */
  rule_key: string;
  label: string;
  level: BriefMissingItemLevel;
  /** The status this item blocks reaching (ops.completeness_rules.stage_to). */
  stage: string;
  stage_from: string;
  /** ops.completeness_rules.owner_stage — the sN_ key this item belongs to. */
  owner_stage: string | null;
  sort_order: number;
}

export interface ShipmentMissingItemsResult {
  shipment_id: string;
  /** null when the shipment does not exist, is soft-deleted, or carries no
   *  order a customer-scoped caller may read — all three answer identically. */
  status: string | null;
  items: ShipmentMissingItem[];
}

// ── Sample uploads types ─────────────────────────────────────────────────

export interface SampleUpload {
  id: string;
  library_item_id: string | null;
  style_id: string | null;
  meeting_id: string | null;
  brief_id: string | null;
  /** Customer-order scope (#1204, migration 6204). NULL for every other scope. */
  order_id: string | null;
  /**
   * Supplier-PO (PPO) scope (#1208, migration 6205) — where the supplier's PI
   * document lives. NULL for every other scope.
   */
  purchase_order_id: string | null;
  /** Shipment scope (#1211, migration 6208) — the shipping and receiving documents. */
  shipment_id: string | null;
  /**
   * QA-inspection scope (#1225, migration 6211) — the inspector's own report.
   * This column IS the "report linked to its inspection" link; there is
   * deliberately no second pointer on production.qa_inspections.
   */
  qa_inspection_id: string | null;
  /**
   * Fit / PP session scope (#1292, migration 6226) — CC's own fit sample
   * report in both renditions, and the supplier's marked-up reply to the
   * workbook.
   */
  fit_session_id: string | null;
  doc_type_id: string | null;
  doc_type_name?: string | null;
  doc_type_slug?: string | null;
  storage_path: string;
  original_filename: string;
  mime_type: string | null;
  size_bytes: number | null;
  content_hash: string | null;
  label: string | null;
  supersedes_id: string | null;
  // Tech-pack versioning (#962, Epic #957 Phase 1c, migration 6146). Only
  // ever set for brief-scoped Tech Pack uploads; NULL for every other row.
  version_no: number | null;
  change_note: string | null;
  approved_at: string | null;
  approved_by: string | null;
  /** Whose document this is. 'supplier' is set by email triage on a stored shipping document (#1211), 'inspector' on a stored QIMA report (#1225). */
  origin: "cc" | "customer" | "supplier" | "inspector" | null;
  created_at: string;
  created_by: string | null;
  created_by_email?: string | null;
  updated_at: string;
  updated_by: string | null;
  deleted_at: string | null;
}

/** One row from samples.brief_tech_pack_versions — see list_brief_tech_pack_versions. */
export interface TechPackVersion {
  upload_id: string;
  version_no: number | null;
  change_note: string | null;
  origin: "cc" | "customer" | null;
  approved_at: string | null;
  approved_by: string | null;
  storage_path: string;
  original_filename: string;
  created_at: string;
  created_by: string | null;
  deleted_at: string | null;
}

export type SampleUploadEventType =
  | "upload"
  | "download"
  | "bulk_download"
  | "edit"
  | "soft_delete"
  | "restore"
  | "supersede";

export interface SampleUploadEvent {
  id: string;
  upload_id: string;
  event_type: SampleUploadEventType;
  actor_id: string | null;
  actor_email?: string | null;
  occurred_at: string;
  metadata: Record<string, unknown>;
}

export interface SampleUploadDocType {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  /** #1273: cc-os GENERATES this document and CC sends it out. */
  is_outgoing?: boolean;
  /** #1273: it prints a price — costings.view / costings.manage to reach it. */
  carries_prices?: boolean;
  /** #1273: ANY-of capability names needed on top of being staff. */
  required_capabilities?: string[];
}

// ── Outgoing documents (#1273, migration 6225, Epic #1272 phase 1a) ───────

/**
 * The records an outgoing document can hang off — five from #1273, plus
 * `fit_session` (#1292, migration 6226). The fit / PP sample report hangs off
 * the SESSION rather than its brief because a document supersedes the scope's
 * current one OF THE SAME TYPE, and a brief has many fit sessions.
 */
export type OutgoingDocumentScopeKind =
  | "order"
  | "qa_inspection"
  | "brief"
  | "shipment"
  | "purchase_order"
  | "fit_session";

/** One recorded send. A document can have several — it may go out twice. */
export interface DocumentSend {
  send_id: string;
  sent_at: string;
  sent_to: string[];
  sent_by: string | null;
  sent_by_name: string | null;
  note: string | null;
}

export interface OutgoingDocument {
  upload_id: string;
  doc_type_id: string;
  doc_type_name: string;
  doc_type_slug: string;
  carries_prices: boolean;
  label: string | null;
  original_filename: string;
  mime_type: string | null;
  size_bytes: number | null;
  storage_path: string;
  supersedes_id: string | null;
  /** Nothing live supersedes this one. */
  is_current: boolean;
  /** 1 for the first generation, 2 for the one that replaced it, … */
  version_no: number;
  generated_at: string;
  generated_by: string | null;
  generated_by_name: string | null;
  sends: DocumentSend[];
}

export interface OutgoingDocumentsResult {
  scope_kind: OutgoingDocumentScopeKind;
  scope_id: string;
  documents: OutgoingDocument[];
  /**
   * How many documents on this scope the caller may not see — a price-bearing
   * one without costings.view / costings.manage. Reported rather than
   * silently dropped: a shorter list is otherwise indistinguishable from an
   * empty one.
   */
  hidden_count: number;
}

export interface DocumentSendResult {
  send_id: string;
  upload_id: string;
  scope_kind: OutgoingDocumentScopeKind;
  scope_id: string;
  doc_type_slug: string | null;
  sent_at: string;
  sent_to: string[];
  sent_by: string | null;
  note: string | null;
  /** How many live sends this document now has, including this one. */
  send_count: number;
}

// ── Sample-upload share links (#519) ─────────────────────────────────────

/** Lifecycle state of a share link, derived server-side in the list verb. */
export type SampleUploadShareLinkStatus = "active" | "expired" | "revoked";

/**
 * A share link as the staff-facing panel sees it. Note what is NOT here: the
 * token, and the token's hash. Neither is ever returned by a list/revoke verb
 * — the plaintext exists only in the create response.
 */
export interface SampleUploadShareLink {
  id: string;
  label: string | null;
  expires_at: string;
  revoked_at: string | null;
  revoked_by: string | null;
  access_count: number;
  last_accessed_at: string | null;
  created_at: string;
  created_by: string | null;
  status: SampleUploadShareLinkStatus;
  document_count: number;
}

/**
 * Create response. `token` and `share_path` are shown ONCE and never
 * retrievable again — the UI must make that clear before the dialog closes.
 */
export interface CreatedSampleUploadShareLink {
  share_link: Omit<SampleUploadShareLink, "status" | "document_count"> & {
    upload_ids: string[];
  };
  token: string;
  /** Origin-relative, e.g. `/share/<token>`; prefix with window.location.origin. */
  share_path: string;
}

// ── AI image-gen prompt preview type (v2 / Task 2) ───────────────────────
// Admin-only read-only result from preview_image_gen_prompt.

export interface PreviewImageGenPromptResult {
  ok: boolean;
  error?: string | null;
  prompt_version?: string;
  prompts?: Record<string, string>;
  fabric_description_note?: string;
}

// ── AI image-gen types (Phase 1b / 5972-5976) ────────────────────────────

export interface ImageGenKindCounts {
  eligible: number;
  missing: number;
  stale: number;
  unchanged_skip: number;
  to_generate: number;
}

export interface ImageGenEligibility {
  total_matching_items: number;
  kinds: Record<string, ImageGenKindCounts>;
  to_generate: number;
  estimate_immediate_usd: number;
  estimate_batch_usd: number;
  matching_item_ids: string[] | null;
}

export type ImageGenFilters = Record<string, unknown>;

export interface CreateImageGenRunResult {
  ok: boolean;
  error?: string | null;
  run_id?: string | null;
  job_count?: number | null;
  skipped_unchanged?: number | null;
  estimated_cost_usd?: number | null;
  budget_cap_usd?: number | null;
}

export interface ReviewImageGenJobResult {
  ok: boolean;
  error?: string | null;
  attached_image_id?: string | null;
  new_job_id?: string | null;
  purged?: number;
}

// ── Sample Library types (Phase 7+) ───────────────────────────────────────
// Mirrors the `library` schema. Audit columns travel on every list/get
// response. Optional fields stay loose where the server hasn't crystallised.

export type LoanStatus = "out" | "returned" | "overdue" | "lost";

export interface LibraryItem extends AuditFields {
  id: string;
  sample_id: string;
  category_id?: string | null;
  storage_location_id?: string | null;
  supplier_id?: string | null;
  customer_id?: string | null;
  season?: string | null;
  style_code?: string | null;
  description?: string | null;
  colourway?: string | null;
  size?: string | null;
  status?: "available" | "on_loan" | "reserved" | "archived" | "lost" | null;
  qr_code?: string | null;
  barcode?: string | null;
  cost_price?: number | null;
  retail_price?: number | null;
  notes?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at?: string | null;
  deleted_at?: string | null;
  // Age + last-used tracking (5730_library_items_last_used).
  // last_used_at is bumped server-side via triggers on samples.loans /
  // samples.meeting_samples. age_days and
  // days_since_last_used arrive pre-computed from mcp_list_library_items;
  // days_since_last_edit arrives from mcp_get_library_item.
  last_used_at?: string | null;
  age_days?: number;
  days_since_last_used?: number | null;
  days_since_last_edit?: number;
  // 5826: hex colour for the swatch dot on the library list. Auto-extracted
  // from the colourway's primary image at upload (lib/colorExtraction.ts) or
  // backfilled by python/backfill_swatch_colors.py. NULL means "not yet run"
  // — UI falls back to a neutral grey.
  swatch_color?: string | null;
  [key: string]: unknown;
}

// 5985/5986: one physical copy of a sample, under the shared S-number.
// A sample always has >= 1 unit; loans target a specific unit.
export interface LibraryItemUnit {
  id: string;
  library_item_id?: string;
  unit_number: number;
  condition: "new" | "good" | "worn" | "damaged" | "retired";
  current_status: "in_office" | "on_loan" | "retired" | "lost";
  storage_location_id?: string | null;
  storage_location_name?: string | null;
  notes?: string | null;
  // Present in the get_library_item envelope for on_loan units.
  open_loan?: {
    id: string;
    display_id: string | null;
    borrower_label: string | null;
    borrower_kind: string;
    loaned_at: string | null;
    expected_return: string | null;
  } | null;
  [key: string]: unknown;
}

export interface ListLibraryItemsInput {
  /** ILIKE substring match across style_code/style_name/sample_id/library_code/colour. */
  search?: string;
  /** Deprecated alias for `search` — kept while older callers migrate. */
  query?: string;
  category_id?: string;
  storage_location_id?: string;
  supplier_id?: string;
  customer_id?: string;
  season?: string;
  /** Filter by samples.library_items.sample_type. */
  sample_type?: string;
  // Status matches the samples.library_items.current_status DB enum and
  // the verb's input schema (samples-side values, not the abstract states
  // earlier drafts used). See db-enums.generated.SAMPLES_LIBRARY_ITEMS_CURRENT_STATUS.
  status?: "in_office" | "on_loan" | "reserved" | "retired" | "lost";
  include_soft_deleted?: boolean;
  limit?: number;
  cursor?: string;
  // 5912: AND-of-all over (library_item_tags ∪ parent style_tags). Replaces
  // the ANY-of semantics that 5822 shipped — see migration header for the
  // rationale (mirrors the post-2026-05-05 portal tag conventions).
  tag_ids?: string[];
  // 5731: server-side cold-sample filter for the Library Dashboard's
  // ColdSamplesCard. All three fields are optional; defaults preserve the
  // prior behaviour. Pass days_since_last_used_min=90, include_never_used,
  // and sort_by='days_since_last_used_desc' for the cold view.
  days_since_last_used_min?: number;
  include_never_used?: boolean;
  // 5912: sort enum expanded to match the library page's SortKey contract
  // for colourways mode. Only desc is meaningful — the SQL hardcodes
  // desc/nulls last for each non-default sort.
  sort_by?:
    | "created_at_desc"
    | "days_since_last_used_desc"
    | "age_days_desc"
    | "price_desc"
    | "last_used_desc"
    | "last_edited_desc";
  // 5818-5822: style/colorway split
  style_id?: string;
  include_archived?: boolean;
  // 5912: missing-data boolean flags backing the library page's
  // ?missing_*=1 URL flags. Each composes AND with every other filter —
  // pass true to keep only rows where the field is unset.
  // missing_season treats both NULL and '' as missing.
  missing_price?: boolean;
  missing_supplier?: boolean;
  missing_storage?: boolean;
  missing_season?: boolean;
  missing_ai?: boolean;
  // 5979: when true, retired library_items are excluded. NOTE: this key is
  // honoured ONLY by the image-gen eligibility/create resolver
  // (resolve_image_gen_targets) — NOT by mcp_list_library_items, which ignores
  // it. To hide retired rows from a browse backed by list_library_items, filter
  // client-side on current_status !== 'retired' (see ai-images-generate.tsx).
  exclude_retired?: boolean;
}

// ===== Style/colorway split (5818-5822) =====

export interface Style extends AuditFields {
  id: string;
  supplier_id?: string | null;
  supplier_name?: string | null;
  supplier_style_code?: string | null;
  style_name?: string | null;
  gender?: "men" | "women" | "boys" | "girls" | "unisex" | "accessories" | null;
  category_id?: string | null;
  category_name?: string | null;
  season?: string | null;
  factor?: number | null;
  supplier_dollar_price?: number | null;
  tech_pack_url?: string | null;
  notes?: string | null;
  archived_at?: string | null;
  metadata?: Record<string, unknown> | null;
  // List-only convenience fields
  colorway_count?: number;
  first_image_url?: string | null;
  // 5827/5828: denormalised colourway summary for the library-list swatch row.
  // Each entry has the minimum needed to render a swatch pill + tap-swap +
  // per-colourway age/status/tag pills. Ordered by library_no (creation order).
  colorways?: Array<{
    id: string;
    library_code?: string | null;
    colour?: string | null;
    swatch_color?: string | null;
    pantone_code?: string | null;
    sample_id?: string | null;
    current_status?: string | null;
    // 5954: digital-only flag (image_only) for the "Digital only" badge
    image_only?: boolean | null;
    first_image_url?: string | null;
    // 5828: age + last-used fields
    age_days?: number | null;
    days_since_last_used?: number | null;
    last_used_at?: string | null;
    // 5828: colourway-level tag ids
    tag_ids?: string[] | null;
  }>;
  // 5828: style-level tag ids (from samples.style_tags)
  style_tag_ids?: string[] | null;
  [key: string]: unknown;
}

export interface LibraryItemCustomerCode {
  id?: string;
  customer_id: string;
  customer_name?: string | null;
  code: string;
}

export interface HardDeletePreview {
  library_code: string | null;
  style_name: string | null;
  open_loan: boolean;
  open_loan_display_id: string | null;
  counts: {
    images: number;
    uploads: number;
    tags: number;
    customer_codes: number;
    meeting_links: number;
    buyer_samples: number;
    loans: number;
    decks: number;
  };
}

export interface HardDeleteResult {
  deleted: boolean;
  library_code: string | null;
  loans_deleted: number;
  decks_detached: number;
  storage_paths: { images: string[]; uploads: string[] };
}

export interface ListStylesInput {
  supplier_id?: string;
  category_id?: string;
  season?: string;
  search?: string;
  include_archived?: boolean;
  limit?: number;
  cursor_created_at?: string;
  cursor_id?: string;
  // 5831: colourway-level any-match filters (style passes if ≥1 colourway matches)
  status?: string;
  sample_type?: string;
  storage_location_id?: string;
  // 5832: server-side sort (Styles mode dropdown)
  sort_by?:
    | "created_at_desc"
    | "age_days_desc"
    | "price_desc"
    | "last_used_desc"
    | "last_edited_desc";
  // 5836: AND tag filter applied server-side. Style passes when every id is
  // carried by samples.style_tags or any of its non-archived colourways via
  // samples.library_item_tags. Empty/undefined → no filter.
  tag_ids?: string[];
  // 5954: "Digital only" filter (image_only) — any-colourway-matches.
  image_only?: boolean;
}

export interface ListStylesOutput {
  styles: Style[];
  next_cursor: { created_at: string; id: string } | null;
  total: number;
}

export interface GetStyleOutput {
  style: Style | null;
  colorways: Array<LibraryItem & {
    storage_location_name?: string | null;
    category_name?: string | null;
    images?: Array<{ id: string; url: string; label: string; position: number }>;
    customer_codes?: LibraryItemCustomerCode[];
    tags?: Array<{ tag_id: string; name: string; category: string; color: string }>;
  }>;
  style_tags: Array<{ tag_id: string; name: string; category: string; color: string }>;
}

export interface ListLibraryItemsOutput {
  // `rows` matches every other list_* verb. The verb previously returned a
  // duplicate `library_items` alias; that was removed in P1-9.
  rows: LibraryItem[];
  next_cursor: string | null;
  count: number;
  total_count?: number;
  has_more?: boolean;
}

export interface Loan extends AuditFields {
  id: string;
  library_item_id: string;
  sample_id?: string | null;
  borrower: string;
  borrower_user_id?: string | null;
  loaned_at: string;
  due_date?: string | null;
  returned_at?: string | null;
  status: LoanStatus;
  meeting_id?: string | null;
  customer_id?: string | null;
  condition_out?: string | null;
  condition_in?: string | null;
  notes?: string | null;
  created_at?: string | null;
  [key: string]: unknown;
}

export interface ListLoansInput {
  status?: LoanStatus;
  borrower?: string;
  library_item_id?: string;
  customer_id?: string;
  meeting_id?: string;
  overdue_only?: boolean;
  limit?: number;
  cursor?: string;
}

export interface ListLoansOutput {
  loans: Loan[];
  next_cursor: string | null;
  count: number;
  total_count?: number;
}

export interface Category extends AuditFields {
  id: string;
  name: string;
  parent_id?: string | null;
  slug?: string | null;
  description?: string | null;
  sort_order?: number | null;
  archived_at?: string | null;
  created_at?: string | null;
  [key: string]: unknown;
}

export interface StorageLocation extends AuditFields {
  id: string;
  code: string;
  label?: string | null;
  parent_id?: string | null;
  capacity?: number | null;
  notes?: string | null;
  archived_at?: string | null;
  created_at?: string | null;
  [key: string]: unknown;
}

// Shape returned by the `get_library_dashboard` verb (backed by
// public.mcp_get_library_dashboard()). Status counts are keyed by
// samples.library_items.current_status and surface dynamically — keep the
// record loose so new statuses don't crash the page.
export interface LibraryDashboard {
  total_samples: number;
  status_counts: Record<string, number>;
  open_loans: number;
  overdue_loans: number;
  recent_samples: Array<{
    id: string;
    sample_id: string | null;
    style_code: string | null;
    style_name: string | null;
    created_at: string | null;
    [key: string]: unknown;
  }>;
  overdue: Array<{
    loan_id?: string;
    id?: string;
    library_item_id: string;
    sample_code: string | null;
    expected_return: string | null;
    borrower_kind: string | null;
    borrower_label: string | null;
    days_overdue?: number;
    [key: string]: unknown;
  }>;
  upcoming_returns: Array<{
    loan_id?: string;
    id?: string;
    library_item_id: string;
    sample_code: string | null;
    expected_return: string | null;
    borrower_kind: string | null;
    borrower_label: string | null;
    [key: string]: unknown;
  }>;
}

function shallowArrayEqual(a: unknown[], b: unknown[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!Object.is(a[i], b[i])) return false;
  }
  return true;
}

// Generic data-fetching hook. Re-runs `fn` whenever `deps` change.
// Keep it thin — no caching, no cancellation. Refine handles that for resources;
// MCP calls are direct.
export function useMcp<T>(fn: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);
  const fnRef = useRef(fn);
  // Keep the ref pointing at the latest `fn` without reading/writing it during
  // render. This effect is declared before the fetch effect below, so within a
  // single commit React runs it first — the fetch effect always sees the `fn`
  // from the same render it was scheduled by.
  useEffect(() => {
    fnRef.current = fn;
  });

  // `loading` is derived from a generation counter rather than flipped on
  // synchronously at the top of the fetch effect (`react-hooks/set-state-in-effect`).
  // The counter is bumped during render (not in the effect) exactly when the
  // effect's own dependency array would consider itself changed -- mirroring
  // React's shallow per-element dependency comparison -- so it stays in sync
  // whether the fetch was triggered by `refresh()` (tick) or by a `deps` change.
  const runDeps = [...deps, tick];
  const [prevRunDeps, setPrevRunDeps] = useState(runDeps);
  const [generation, setGeneration] = useState(0);
  const [resolvedGeneration, setResolvedGeneration] = useState(-1);
  if (!shallowArrayEqual(prevRunDeps, runDeps)) {
    setPrevRunDeps(runDeps);
    setGeneration((g) => g + 1);
  }
  const loading = resolvedGeneration !== generation;

  useEffect(() => {
    let cancelled = false;
    const myGeneration = generation;
    fnRef.current()
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setError(null);
        setResolvedGeneration(myGeneration);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e : new Error(String(e)));
        setResolvedGeneration(myGeneration);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const refresh = () => setTick((t) => t + 1);

  return { data, error: loading ? null : error, loading, refresh };
}

export interface TimelineRow {
 id: string; set_id: string; milestone_catalog_id: string; name: string; label: string;
 sequence: number; offset_days: number; anchor: string; direction: string;
 approval_lag_days: number; resub_buffer_days: number; required: boolean;
}
export interface TimelineOverride {
 customer_id: string; order_type_id: string; milestone_catalog_id: string;
 offset_days: number | null; approval_lag_days: number | null; resub_buffer_days: number | null;
 required: boolean | null; enabled: boolean | null; note: string | null;
}
export interface TimelineSettings {
 sets: Array<{ id: string; name: string; slug: string; is_default: boolean; float_green_min: number; float_amber_min: number }>;
 rows: TimelineRow[];
 customers: Array<{ id: string; name: string; resub_default: boolean }>;
 order_types: Array<{ id: string; label: string }>;
 assignments: Array<{ customer_id: string | null; order_type_id: string; set_id: string }>;
 overrides: TimelineOverride[];
 shutdowns: Array<{ id: string; name: string; starts_on: string; ends_on: string; notes: string | null }>;
}
